// src/components/tickets/components/TicketEditorDrawer.tsx
import React from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css, keyframes } from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import PrimaryCta from '../../PrimaryCta';
import PortalAvatar from '../../profile/PortalAvatar';
import { useApi } from '../../../context/ApiProvider';
import { useAuth } from '../../../context/AuthProvider';
import {
    deleteTicketCopilotThread,
    listTicketCopilotThreads,
    streamTicketCopilotMessage,
    type SendTicketCopilotMessageResponse,
    type AICopilotThread,
    type AICopilotThreadMessage,
} from '../../../api/ai';
import { apiFetch } from '../../../lib/api';
import { resolveClientDisplayName } from '../../../lib/clientDisplayName';
import { buildTicketEditorTicket } from '../../../lib/tickets/ticketEditorTicket';
import { convertFromUTC, convertToUTC, getUserTimezone } from '../../../utils/timezone';
import type { AppDispatch, RootState } from '../../../store';
import {
    buildTicketAssigneeCacheKey,
    buildTicketContextCacheKey,
    fetchTicketComments,
    fetchTicketCreateContext,
    fetchTicketDetail,
    fetchTicketInternalTeamAssignees,
    fetchTicketOrgAssignees,
    fetchTicketUserIdentity,
    selectAllTickets,
    selectCachedTicketById,
    selectCommentBucketMeta,
    selectCommentListForTicket,
    selectTicketDetailMeta,
    selectTicketInternalTeamAssigneesByTeamId,
    selectTicketOrgAssigneesByKey,
    selectTicketContextByKey,
    selectTicketContextMetaByKey,
    selectTicketUserIdentityMap,
    TICKET_COMMENTS_TTL_MS,
    TICKET_CONTEXT_TTL_MS,
    TICKET_DETAIL_TTL_MS,
    ticketPatched,
} from '../../../store/features/tickets/ticketsSlice';
import type {
    Ticket as StoreTicket,
    TicketRelationshipSummary,
    TicketStatus,
} from '../../../store/features/tickets/types';
/* Pane and sections */

const Backdrop = styled(motion.div)`
  position: fixed; inset: 0; background: rgba(0,0,0,.55); z-index: 1200;
`;

const TICKET_KBASE_PANEL_WIDTH = 'min(760px, 56vw)';

const FlatInteractiveFieldStyles = css`
  background: transparent;
  border: 1px solid transparent;
  color: #E8EAED;
  border-radius: 8px;
  font-weight: 700;
  outline: none;
  box-shadow: none;

  &:hover:not(:focus-visible) {
    border-style: dashed;
    border-color: rgba(255,255,255,.28);
  }

  &:focus-visible {
    background: rgba(255,255,255,.03);
    border-style: solid;
    border-color: transparent;
    box-shadow: inset 0 0 0 1px rgba(138,180,248,.75);
    outline: 2px solid rgba(138,180,248,.35);
    outline-offset: 1px;
  }
`;

// Sheet: add color-scheme so native controls render dark
const Sheet = styled(motion.aside)<{ $kbaseOpen?: boolean; $rightOffset?: string }>`
  position: fixed; top: 0; right: ${({ $rightOffset }) => $rightOffset ?? '0px'}; height: 100dvh; @supports not (height: 100dvh){ height: 100vh; }
  width: ${({ $kbaseOpen }) => ($kbaseOpen ? 'clamp(320px, 44vw, 560px)' : 'clamp(340px, 92vw, 620px)')}; z-index: 1201;
  background: linear-gradient(180deg, rgba(20,22,28,.96), rgba(18,20,25,.96));
  color: #E8EAED; border-left: 1px solid rgba(255,255,255,.08);
  box-shadow: -18px 0 48px rgba(0,0,0,.45);
  display: grid; grid-template-rows: auto 1fr auto;
  border-top-left-radius: 18px; border-bottom-left-radius: 18px; overflow: hidden;
  color-scheme: dark; /* <-- makes native dropdown popups dark in modern browsers */
  transition: right .22s ease, width .22s ease;
`;

// Flat, readable select matching the drawer
const Select = styled.select`
  width: 100%;
  padding: 7px 38px 7px 10px;
  ${FlatInteractiveFieldStyles}
  appearance: none; -webkit-appearance: none; -moz-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23cbd2e1' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
  option { background: #12161b; color: #E8EAED; } /* improves popup legibility in Chrome */
`;


const Header = styled.div`
  padding: 12px; display: grid; gap: 10px; border-bottom: 1px solid rgba(255,255,255,.08);
`;

const HeaderMetricsStack = styled.div`
  display: grid;
  gap: 2px;
  min-width: 0;
`;

const HeaderMetricLine = styled.div`
  color: #cbd2e1;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
`;

const HeaderBar = styled.div`
  position: relative;
  display: grid; gap: 10px;
`;

const HeaderGrid = styled.div`
  display: grid;
  gap: 10px;
`;

const ControlsBar = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
`;

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;
// (replace) make RightControls an anchor for menus
const RightControls = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
`;

// Actions menu (desktop dropdown + mobile bottom sheet)
const MenuPopover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 74;
  display: grid;
  gap: 2px;
  padding: 6px 0;
  min-width: 220px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(18,20,25,.98);
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(0,0,0,.45);

  @media (max-width: 540px) {
    position: fixed;
    left: 12px;
    right: 12px;
    bottom: calc(env(safe-area-inset-bottom) + 12px);
    top: auto;
  }
`;

const MenuItem = styled.button<{ $danger?: boolean }>`
  all: unset;
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 10px;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  touch-action: manipulation;
  color: ${({ $danger }) => ($danger ? '#FCA5A5' : '#E8EAED')};
  &:hover { background: rgba(255,255,255,.06); }
  .text { font-weight: 800; }
`;

const MenuIcon = styled.div`
  width: 28px; height: 28px; border-radius: 8px;
  display: grid; place-items: center;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.04);
  font-size: 14px; color: #cbd2e1;
`;

const MenuDivider = styled.div`
  height: 1px; background: rgba(255,255,255,.08); margin: 4px 0;
`;

// Delete confirmation (always mobile-friendly sheet)
const ConfirmPopover = styled.div`
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1260;
  width: min(92vw, 360px);
  display: grid; gap: 10px; padding: 14px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(18,20,25,.98);
  border-radius: 14px;
  box-shadow: 0 12px 28px rgba(0,0,0,.45);
`;

const ConfirmActions = styled.div`
  display: grid; grid-auto-flow: column;
  gap: 8px; justify-content: end;
`;

const SecondaryBtn = styled.button`
  all: unset; cursor: pointer;
  padding: 10px 12px; border-radius: 10px; font-weight: 900; font-size: 13px;
  color: #E8EAED; border: 1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.04);
`;

const DangerBtn = styled.button`
  all: unset; cursor: pointer;
  padding: 10px 12px; border-radius: 10px; font-weight: 900; font-size: 13px;
  color: #111827; background: #EF4444;
  border: 1px solid rgba(0,0,0,.25);
`;


const GhostBtn = styled.button`
  all: unset;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.03);
  color: #E8EAED; font-weight: 800; cursor: pointer;
  &:hover { background: rgba(255,255,255,.06); }
`;

const IconBtn = styled.button`
  all: unset; width: 36px; height: 36px;
  display: grid; place-items: center;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.03);
  color: #cbd2e1; cursor: pointer;
  &:hover { background: rgba(255,255,255,.06); color: #E8EAED; }
`;

const MetaGrid = styled.div`
  display: grid;
  gap: 10px;

  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-areas:
    "agent assignee"
    "status priority"
    "milestone milestone"
    "project team"
    "start due";

  @media (min-width: 560px) {
    grid-template-columns: repeat(12, minmax(0, 1fr));
    grid-template-areas:
      "agent agent agent agent agent agent assignee assignee assignee assignee assignee assignee"
      "status status status priority priority priority start start start due due due"
      "team team team team project project project project milestone milestone milestone milestone";
  }

  & > *:nth-child(1) { grid-area: agent; }
  & > *:nth-child(2) { grid-area: assignee; }
  & > *:nth-child(3) { grid-area: status; }
  & > *:nth-child(4) { grid-area: priority; }
  & > *:nth-child(5) { grid-area: milestone; }
  & > *:nth-child(6) { grid-area: project; }
  & > *:nth-child(7) { grid-area: team; }
  & > *:nth-child(8) { grid-area: start; }
  & > *:nth-child(9) { grid-area: due; }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 7px 10px;
  ${FlatInteractiveFieldStyles}
`;

const StaticValue = styled.div`
  display: grid;
  align-content: start;
  justify-items: start;
  min-height: 0;
  padding: 0;                    /* no field chrome */
  border: 0;
  background: transparent;
  color: #cbd2e1;                /* muted */
  font-weight: 800;
  cursor: default;
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const DrawerSkeletonSurface = styled.div<{
  $width?: string;
  $height?: string;
  $radius?: string;
}>`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '14px'};
  border-radius: ${({ $radius }) => $radius || '8px'};
  background: linear-gradient(
    90deg,
    rgba(255,255,255,.06) 0%,
    rgba(255,255,255,.12) 50%,
    rgba(255,255,255,.06) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.35s linear infinite;
`;

const DrawerSkeletonLine = styled(DrawerSkeletonSurface)``;

const DrawerSkeletonCircle = styled(DrawerSkeletonSurface)`
  width: 38px;
  height: 38px;
  border-radius: 999px;
`;

const DrawerSkeletonStack = styled.div`
  display: grid;
  gap: 8px;
  min-width: 0;
`;

const ClientIdentityValue = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ClientIdentityName = styled.div`
  color: #cbd2e1;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AssignmentIdentityValue = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const AssignmentIdentityName = styled.div`
  color: #cbd2e1;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;


const TitleInput = styled.input`
  width: 100%;
  background: rgba(255,255,255,.03);
  color: #E8EAED;
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 800;
  &:focus-visible { outline: 2px solid #8ab4f8; outline-offset: 2px; }
  &::placeholder { color: #9AA4B2; font-weight: 700; }
`;

const MetaCell = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 6px;
  label { color:#9AA4B2; font-size:11px; font-weight:900; letter-spacing:.2px; text-transform: uppercase; }
`;

const FieldHint = styled.div<{ $error?: boolean }>`
  color: ${({ $error }) => ($error ? '#FCA5A5' : '#8FA2BF')};
  font-size: 11px;
  font-weight: 700;
  line-height: 1.35;
`;

const RoutingFieldsGrid = styled.div`
  display: grid;
  gap: 10px;

  @media (min-width: 720px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: start;
  }
`;

const RelationshipFieldsGrid = styled.div`
  display: grid;
  gap: 10px;

  @media (min-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
  }
`;

const RelationshipPillList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const RelationshipPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,.1);
  background: rgba(255,255,255,.04);
  color: #cbd2e1;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.3;
`;

const KBaseProcessRow = styled.div`
  display: grid;
  gap: 10px;

  @media (min-width: 720px) {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
  }
`;

const AssistantActionButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 42px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid rgba(224,161,94,.45);
  background: rgba(224,161,94,.08);
  color: #F3F4F6;
  cursor: pointer;
  font-size: 13px;
  font-weight: 900;
  transition: background .16s ease, border-color .16s ease, transform .16s ease;

  &:hover {
    background: rgba(224,161,94,.14);
    border-color: rgba(224,161,94,.72);
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: .45;
    cursor: not-allowed;
  }
`;

const OpenProcessActionButton = styled(AssistantActionButton)`
  justify-self: start;
  min-height: 36px;
  padding: 8px 12px;
  gap: 8px;
  font-size: 12px;
`;

const AssistantActionsRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;

  @media (min-width: 720px) {
    justify-content: flex-end;
  }
`;

const AssistantButtonIcon = styled.span`
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #E0A15E;
  flex: 0 0 auto;
`;

const KBaseFinder = styled.div`
  position: relative;
  min-width: 0;
`;

const KBaseTrigger = styled.button<{ $open?: boolean }>`
  width: 100%;
  min-height: 38px;
  padding: 7px 36px 7px 10px;
  ${FlatInteractiveFieldStyles}
  text-align: left;
  cursor: pointer;
  position: relative;
  color: ${({ $open }) => ($open ? '#E8EAED' : '#CBD2E1')};

  &::after {
    content: '▾';
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #CBD2E1;
    font-size: 12px;
    pointer-events: none;
  }
`;

const KBaseTriggerValue = styled.span<{ $placeholder?: boolean }>`
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $placeholder }) => ($placeholder ? '#9AA4B2' : '#E8EAED')};
`;

const KBasePopover = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 72;
  display: grid;
  gap: 8px;
  padding: 10px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(18,20,25,.98);
  box-shadow: 0 16px 34px rgba(0,0,0,.36);
`;

const KBaseSearchInput = styled.input`
  width: 100%;
  min-height: 38px;
  ${FlatInteractiveFieldStyles}
  padding: 7px 10px;
  font-size: 13px;
  font-weight: 700;

  &::placeholder {
    color: #9AA4B2;
  }
`;

const KBaseResults = styled.div`
  max-height: 320px;
  overflow-y: auto;
  display: grid;
  gap: 8px;
  padding-right: 2px;
`;

const KBaseResultSection = styled.div`
  display: grid;
  gap: 6px;
`;

const KBaseResultHeading = styled.div`
  color: #9AA4B2;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .24px;
  text-transform: uppercase;
`;

const KBaseResultButton = styled.button<{ $active?: boolean }>`
  all: unset;
  cursor: pointer;
  display: grid;
  gap: 4px;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224,161,94,.42)' : 'rgba(255,255,255,.08)')};
  background: ${({ $active }) => ($active ? 'rgba(224,161,94,.12)' : 'rgba(255,255,255,.03)')};

  &:hover {
    background: rgba(255,255,255,.07);
    border-color: rgba(224,161,94,.3);
  }
`;

const KBaseResultTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const KBaseResultTitle = styled.div`
  color: #E8EAED;
  font-size: 13px;
  font-weight: 900;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const KBaseResultBadge = styled.span`
  flex: 0 0 auto;
  padding: 3px 7px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.04);
  color: #CBD2E1;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .18px;
  text-transform: uppercase;
`;

const KBaseResultMeta = styled.div`
  color: #9AA4B2;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.35;
`;

const KBaseEmptyState = styled.div`
  color: #9AA4B2;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 2px;
`;

const KBaseProcessSheet = styled(motion.aside)`
  position: fixed;
  top: 0;
  right: 0;
  height: 100dvh;
  width: ${TICKET_KBASE_PANEL_WIDTH};
  z-index: 1202;
  background: linear-gradient(180deg, rgba(20,22,28,.985), rgba(18,20,25,.985));
  border-left: 1px solid rgba(255,255,255,.08);
  box-shadow: -18px 0 48px rgba(0,0,0,.45);
  overflow: hidden;
  color: #E8EAED;
  display: flex;
  flex-direction: column;

  @media (max-width: 980px) {
    width: min(360px, 46vw);
  }
`;

const KBaseProcessMainPane = styled.div<{ $aiOpen?: boolean }>`
  flex: 0 0 auto;
  height: ${({ $aiOpen }) => ($aiOpen ? '58%' : '100%')};
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  transition: height .24s ease;
`;

const KBaseAiPane = styled.div<{ $open?: boolean }>`
  width: 100%;
  height: ${({ $open }) => ($open ? '42%' : '0px')};
  min-height: ${({ $open }) => ($open ? '280px' : '0px')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: translateY(${({ $open }) => ($open ? '0' : '18px')});
  transition: height .24s ease, min-height .24s ease, opacity .2s ease, transform .24s ease;
  overflow: hidden;
  border-top: ${({ $open }) => ($open ? '1px solid rgba(255,255,255,.08)' : '0')};
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  background: rgba(255,255,255,.02);
`;

const KBaseProcessHeader = styled.div`
  display: grid;
  gap: 10px;
  padding: 14px 14px 12px;
  border-bottom: 1px solid rgba(255,255,255,.08);
`;

const KBaseProcessHeaderTop = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 10px;
`;

const KBaseProcessKicker = styled.div`
  color: #9AA4B2;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .24px;
  text-transform: uppercase;
`;

const KBaseProcessTitle = styled.h3`
  margin: 4px 0 0;
  color: #F3F4F6;
  font-size: 20px;
  font-weight: 900;
  line-height: 1.15;
`;

const KBaseProcessCloseButton = styled.button`
  all: unset;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.03);
  color: #CBD2E1;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover {
    background: rgba(255,255,255,.07);
    color: #F3F4F6;
  }
`;

const KBaseProcessMetaText = styled.div`
  color: #CBD2E1;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
`;

const KBaseProcessBody = styled.div`
  min-height: 0;
  overflow-y: auto;
  display: grid;
  gap: 14px;
  align-content: start;
  padding: 14px;
`;

const KBaseProcessFooter = styled.div`
  padding: 12px 14px 14px;
  border-top: 1px solid rgba(255,255,255,.08);
`;

const KBaseFooterActionButton = styled(AssistantActionButton)`
  width: 100%;
`;

const KBaseProcessSection = styled.section`
  display: grid;
  gap: 8px;
`;

const KBaseProcessSectionTitle = styled.div`
  color: #9AA4B2;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .24px;
  text-transform: uppercase;
`;

const KBaseProcessParagraph = styled.div`
  color: #E8EAED;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.6;
`;

const KBaseProcessList = styled.ol`
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 6px;
  color: #E8EAED;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.55;
`;

const KBaseProcessRichText = styled.div`
  color: #E8EAED;
  font-size: 13px;
  line-height: 1.65;

  h1, h2, h3, h4 {
    color: #F3F4F6;
    margin: 0 0 10px;
    line-height: 1.25;
  }

  p {
    margin: 0 0 10px;
  }

  ul, ol {
    margin: 0 0 10px;
    padding-left: 18px;
  }

  blockquote {
    margin: 0;
    padding: 0 0 0 12px;
    border-left: 2px solid rgba(224,161,94,.55);
    color: #CBD2E1;
  }

  a {
    color: #8AB4F8;
  }
`;

const KBaseAiHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px 10px;
  border-bottom: 1px solid rgba(255,255,255,.08);
`;

const KBaseAiTitleGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const KBaseAiHeaderActions = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const KBaseAiTitle = styled.div`
  color: #F3F4F6;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.2;
`;

const KBaseAiBotIcon = styled.span`
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #E0A15E;
`;

const KBaseAiHeaderButton = styled.button`
  all: unset;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.03);
  color: #CBD2E1;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover {
    background: rgba(255,255,255,.07);
    color: #F3F4F6;
  }
`;

const KBaseAiCollapseButton = styled(KBaseAiHeaderButton)``;

const KBaseAiActionsButton = styled(KBaseAiHeaderButton)``;

const KBaseAiActionsMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 40px;
  z-index: 5;
  min-width: 170px;
  display: grid;
  gap: 2px;
  padding: 6px 0;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(18,20,25,.98);
  box-shadow: 0 12px 28px rgba(0,0,0,.42);
`;

const KBaseAiActionItem = styled.button<{ $danger?: boolean }>`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  color: ${({ $danger }) => ($danger ? '#FCA5A5' : '#E8EAED')};
  font-size: 12px;
  font-weight: 800;

  &:hover {
    background: rgba(255,255,255,.06);
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
`;

const KBaseAiBody = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding: 14px 16px;
  display: grid;
  gap: 10px;
  align-content: start;
`;

const KBaseAiMessage = styled.div<{ $assistant?: boolean; $pending?: boolean }>`
  justify-self: ${({ $assistant }) => ($assistant ? 'start' : 'end')};
  width: fit-content;
  max-width: min(88%, 640px);
  padding: ${({ $assistant }) => ($assistant ? '2px 0' : '10px 12px')};
  border-radius: ${({ $assistant }) => ($assistant ? '0' : '14px')};
  border: 1px solid ${({ $assistant }) => ($assistant ? 'transparent' : 'rgba(255,255,255,.1)')};
  background: ${({ $assistant }) => ($assistant ? 'transparent' : 'rgba(255,255,255,.03)')};
  color: #E8EAED;
  font-size: ${({ $assistant }) => ($assistant ? '13px' : '12px')};
  font-weight: ${({ $assistant }) => ($assistant ? '600' : '700')};
  line-height: ${({ $assistant }) => ($assistant ? '1.65' : '1.55')};
  opacity: ${({ $pending }) => ($pending ? 0.72 : 1)};
  white-space: pre-wrap;
  word-break: break-word;
`;

const KBaseAiEmptyState = styled.div`
  color: #9AA4B2;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.55;
  padding: 2px 2px 0;
`;

const KBaseAiErrorText = styled.div`
  color: #FCA5A5;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.45;
  grid-column: 1 / -1;
`;

const KBaseAiComposer = styled.div`
  padding: 12px 14px 14px;
  border-top: 1px solid rgba(255,255,255,.08);
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 8px;
  align-items: end;
`;

const KBaseAiAttachmentList = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const KBaseAiAttachmentChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.04);
  color: #DCE3EA;
  font-size: 11px;
  font-weight: 800;

  .name {
    max-width: 170px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const KBaseAiAttachmentRemove = styled.button`
  all: unset;
  cursor: pointer;
  color: #9AA4B2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;

  &:hover {
    color: #F3F4F6;
  }
`;

const KBaseAiAttachButton = styled.button`
  all: unset;
  width: 42px;
  min-height: 42px;
  border-radius: 10px;
  border: 1px dashed rgba(255,255,255,.2);
  background: rgba(255,255,255,.03);
  color: #CBD2E1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(255,255,255,.07);
    color: #F3F4F6;
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
`;

const KBaseAiInput = styled.textarea`
  width: 100%;
  min-height: 60px;
  max-height: 140px;
  resize: none;
  ${FlatInteractiveFieldStyles}
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;

  &::placeholder {
    color: #9AA4B2;
  }
`;

const KBaseAiSendButton = styled.button`
  all: unset;
  cursor: pointer;
  padding: 10px 14px;
  border-radius: 10px;
  font-weight: 900;
  font-size: 13px;
  color: #111827;
  background: linear-gradient(90deg, #8DEBFF 0%, #F8C9A5 100%);
  border: 1px solid rgba(0,0,0,.25);
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    cursor: default;
    opacity: .55;
  }
`;



const Body = styled.div<{ $withComments?: boolean }>`
  padding: 12px;
  display: grid;
  gap: 12px;
  min-height: 0;
  align-content: start;
  overflow-y: ${({ $withComments }) => ($withComments ? 'hidden' : 'auto')};
  ${({ $withComments }) =>
    $withComments
      ? 'grid-template-rows: minmax(220px, 0.95fr) minmax(200px, 1.05fr);'
      : ''}
`;

const SubtaskLikeInputStyles = css`
  ${FlatInteractiveFieldStyles}
  padding: 7px 10px;
`;

const Field = styled.div`
  display: grid; gap: 4px;
  min-height: 0;
  align-content: start;
  label { color:#9AA4B2; font-size:11px; font-weight:900; letter-spacing:.2px; text-transform: uppercase; }
  input, textarea {
    ${SubtaskLikeInputStyles}
  }
  textarea {
    min-height: 140px;
    resize: vertical;
  }
`;

const DescriptionSubtasksSection = styled.section<{ $withComments?: boolean }>`
  display: grid;
  gap: 12px;
  align-content: start;
  grid-auto-rows: max-content;
  min-height: ${({ $withComments }) => ($withComments ? '0' : 'clamp(280px, 46vh, 460px)')};
  height: ${({ $withComments }) => ($withComments ? '100%' : 'auto')};
  max-height: ${({ $withComments }) => ($withComments ? 'none' : 'clamp(320px, 58vh, 560px)')};
  overflow-y: auto;
  padding-right: 4px;
  scrollbar-width: thin;
  scrollbar-gutter: stable;
  overscroll-behavior: contain;
`;

const CommentsField = styled(Field)`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  align-content: stretch;
  min-height: 0;
`;

const Subtasks = styled.div`
  display: grid; gap: 8px;
`;

const AddSubtaskBtn = styled.button`
  all: unset;
  cursor: pointer;
  color: #cbd2e1;
  padding: 5px 8px;
  border-radius: 10px;
  border: 1px dashed rgba(255,255,255,.2);
  background: rgba(255,255,255,.03);
  width: fit-content;
  height: fit-content;
  font-weight: 800;
`;
const SubtaskItem = styled.div<{ $dragging?: boolean; $dropTarget?: boolean; $done?: boolean }>`
  display: grid;
  grid-template-columns: 18px 22px minmax(120px, 1fr) max-content 38px 32px;
  gap: 8px;
  align-items: center;
  background: ${({ $dragging, $dropTarget }) =>
    $dragging
      ? 'rgba(138,180,248,.16)'
      : $dropTarget
        ? 'rgba(138,180,248,.10)'
        : 'rgba(255,255,255,.04)'};
  border: 1px solid ${({ $dropTarget }) => ($dropTarget ? 'rgba(138,180,248,.45)' : 'rgba(255,255,255,.08)')};
  border-radius: 10px;
  padding: 8px 10px;
  min-height: 44px;
  opacity: ${({ $done, $dragging }) => ($done && !$dragging ? 0.58 : 1)};
  filter: ${({ $done, $dragging }) => ($done && !$dragging ? 'saturate(.7)' : 'none')};
  transition: opacity .18s ease, filter .18s ease, background-color .18s ease, border-color .18s ease;

  @media (max-width: 720px) {
    grid-template-columns: 18px 22px minmax(88px, 1fr) max-content 34px 30px;
    gap: 6px;
    padding: 8px;
  }
`;

const SubtaskDragHandle = styled.button`
  all: unset;
  width: 18px;
  height: 18px;
  display: grid;
  place-items: center;
  color: #9AA4B2;
  border-radius: 6px;
  cursor: grab;
  user-select: none;
  font-size: 12px;
  font-weight: 900;
  line-height: 1;
  &:hover { color: #E8EAED; background: rgba(255,255,255,.08); }
  &:active { cursor: grabbing; }
  &[disabled] { opacity: .5; cursor: default; }
`;

const SubtaskToggle = styled.button<{ $done: boolean }>`
  all: unset;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  cursor: pointer;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 900;
  color: ${({ $done }) => ($done ? '#111827' : 'transparent')};
  border: 1px solid ${({ $done }) => ($done ? 'rgba(141,235,255,.85)' : 'rgba(255,255,255,.32)')};
  background: ${({ $done }) => ($done ? 'linear-gradient(90deg, #8DEBFF 0%, #F8C9A5 100%)' : 'transparent')};
  &:hover {
    border-color: rgba(141,235,255,.75);
  }
  &[disabled] { opacity: .6; cursor: default; }
`;

const SubtaskTextInput = styled.input`
  && {
    width: 100%;
    min-width: 0;
    ${SubtaskLikeInputStyles}
  }
`;

const SubtaskDateWrap = styled.div`
  position: relative;
  width: max-content;
  min-width: 34px;
  height: 34px;
`;

const SubtaskDateTrigger = styled.button<{ $selected?: boolean }>`
  all: unset;
  box-sizing: border-box;
  width: ${({ $selected }) => ($selected ? 'auto' : '34px')};
  min-width: 34px;
  height: 34px;
  padding: ${({ $selected }) => ($selected ? '0 10px' : '0')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ $selected }) => ($selected ? '8px' : '999px')};
  border: 1px ${({ $selected }) => ($selected ? 'solid transparent' : 'dashed rgba(255,255,255,.26)')};
  background: rgba(255,255,255,.03);
  color: ${({ $selected }) => ($selected ? '#7FE6EA' : '#cbd2e1')};
  font-size: 12px;
  font-weight: 800;
  text-transform: ${({ $selected }) => ($selected ? 'none' : 'uppercase')};
  letter-spacing: .1px;
  cursor: pointer;
  &:hover { border-color: ${({ $selected }) => ($selected ? 'transparent' : 'rgba(141,235,255,.7)')}; }
  &:focus-visible { outline: 2px solid #8ab4f8; outline-offset: 1px; }
  &[disabled] { opacity: .6; cursor: default; }
`;

const SubtaskDateIconCircle = styled.span`
  width: 14px;
  height: 14px;
  display: grid;
  place-items: center;
  color: inherit;

  svg {
    width: 11px;
    height: 11px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const SubtaskDateText = styled.span`
  white-space: nowrap;
  line-height: 1;
`;

const HiddenSubtaskDateInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;

const SubtaskAssigneeWrap = styled.div`
  position: relative;
`;

const SubtaskAssigneeTrigger = styled.button`
  all: unset;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 1px dashed rgba(255,255,255,.26);
  background: rgba(255,255,255,.03);
  display: grid;
  place-items: center;
  cursor: pointer;
  color: #cbd2e1;
  &:hover { border-color: rgba(141,235,255,.7); }
`;

const SubtaskAssigneeMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 78;
  display: grid;
  min-width: 220px;
  max-height: 220px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(18,20,25,.98);
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(0,0,0,.45);
  gap: 4px;
`;

const SubtaskAssigneeItem = styled.button<{ $active?: boolean }>`
  all: unset;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
  color: #E8EAED;
  background: ${({ $active }) => ($active ? 'rgba(138,180,248,.18)' : 'transparent')};
  &:hover { background: rgba(255,255,255,.08); }
`;

const SubtaskAssigneeName = styled.div`
  min-width: 0;
  color: #E8EAED;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SubtaskActionBtn = styled.button<{ $danger?: boolean }>`
  all: unset;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,.14);
  background: rgba(255,255,255,.03);
  display: grid;
  place-items: center;
  cursor: pointer;
  color: ${({ $danger }) => ($danger ? '#FCA5A5' : '#cbd2e1')};
  font-size: 12px;
  font-weight: 900;
  &:hover {
    background: rgba(255,255,255,.08);
    color: ${({ $danger }) => ($danger ? '#F87171' : '#E8EAED')};
  }
  &[disabled] {
    opacity: .5;
    cursor: default;
  }
`;

const CommentsArea = styled.div`
  margin-top: 8px;
  display: grid;
  gap: 10px;
  min-height: 0;
  height: 100%;
  align-content: start;

  /* Fill the remaining comments panel height and scroll internally */
  max-height: none;
  overflow-y: auto;

  /* Small UX niceties for scrollbars */
  padding-right: 4px;             /* prevents text hugging the scrollbar */
  scrollbar-width: thin;          /* Firefox */
  scrollbar-gutter: stable;       /* keeps layout stable when scrollbar appears */
  overscroll-behavior: contain;   /* avoids scroll chaining with the drawer body */

  .empty {
    color: #9AA4B2;
  }

  .item {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: start;
    gap: 10px;
    padding: 0;
    border: 0;
    background: transparent;
    border-radius: 0;
  }

  .itemContent {
    display: grid;
    gap: 4px;
    min-width: 0;
  }

  .meta { color:#9AA4B2; font-size:12px; font-weight:800; }
  .body {
    color: #E8EAED;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const Footer = styled.div`
  padding: 10px; border-top: 1px solid rgba(255,255,255,.08);
  background: linear-gradient(180deg, rgba(20,22,28,.96), rgba(18,20,25,.96));
  display: grid; gap: 8px;
`;

const CommentComposer = styled.div`
  display: grid; grid-template-columns: 1fr auto; gap: 8px;
  textarea {
    ${SubtaskLikeInputStyles}
    min-height: 60px;
  }
  button {
    all: unset; cursor: pointer; padding: 10px 14px; border-radius: 10px; font-weight: 900; font-size: 13px;
    color: #111827; background: linear-gradient(90deg, #8DEBFF 0%, #F8C9A5 100%);
    border: 1px solid rgba(0,0,0,.25);
  }
`;



const SaveStatus = styled.div`
  color: #E0A15E;
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
  align-self: center;
`;

const CreatedHeaderMeta = styled.div`
  display: grid;
  gap: 2px;
  min-width: 0;
`;

const CreatedHeaderValue = styled.div`
  color: #cbd2e1;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
`;

const TicketNumberBadge = styled.div`
  color: #8deBff;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1.2;
`;



// Add below MetaCell styled component (or nearby)

// Anchor the popovers to this cell
const AssignCell = styled(MetaCell)`
  position: relative;
`;

const AgentAssignCell = styled(MetaCell)`
  position: relative;
`;

const AgentAssignTrigger = styled.button`
  all: unset;
  width: fit-content;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  border: 0;
  background: transparent;

  &:hover {
    opacity: .92;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .18);
    border-radius: 10px;
  }
`;

const EmptyAssigneeAvatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(165deg, #ecd1ab 0%, #dfba88 100%);
  color: #1a1a1a;
  display: grid;
  place-items: center;
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
`;

const AgentAssignMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 73;
  width: min(320px, 76vw);
  display: grid;
  gap: 6px;
  padding: 8px 0;
  border: 1px solid rgba(255, 255, 255, .12);
  background: rgba(18, 20, 25, .98);
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, .45);
`;

const AgentAssignList = styled.div`
  display: grid;
  gap: 2px;
  max-height: 280px;
  overflow-y: auto;
  padding-right: 4px;
`;

const AgentAssignItem = styled.button<{ $active?: boolean }>`
  all: unset;
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 10px;
  align-items: center;
  padding: 8px 10px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, .14)' : 'transparent')};

  &:hover {
    background: rgba(255, 255, 255, .06);
  }
`;

const AgentAssignName = styled.div`
  color: #E8EAED;
  font-size: 12px;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;


/* Types */

export type EditorMode = 'create' | 'edit';
export type UiPriority = 'low' | 'medium' | 'high' | 'urgent';

type Subtask = {
    id: string;
    text: string;
    done: boolean;
    dueDate?: string | null;
    assigneeId?: string | null;
};
type AgentOption = {
    id: string;
    name: string;
    email?: string;
    title?: string;
    role?: string;
    photoURL?: string | null;
};
const EMPTY_AGENT_OPTIONS: AgentOption[] = [];

type UserIdentityLookup = {
    username?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    displayName?: string | null;
    name?: string | null;
    email?: string | null;
    photoURL?: string | null;
    photoUrl?: string | null;
};

type TicketComment = {
    id: string;
    body: string;
    authorId: string | null;
    createdAt?: any;
    authorFirstName?: string | null;
    authorLastName?: string | null;
    authorDisplayName?: string | null;
    authorEmail?: string | null;
    authorPhotoURL?: string | null;
};

type TicketOfferingOption = {
    id: string;
    code: string;
    name: string;
    type?: string | null;
    source?: string | null;
    targetCompletionMinutes?: number | null;
    resolutionTargetMinutes?: number | null;
    subcategories: Array<{
        id: string;
        name: string;
        description?: string | null;
    }>;
};

type TicketSubscriptionOption = {
    id: string;
    serviceId: string;
    tierId?: string | null;
    serviceCode: string;
    serviceTitle: string;
    tierSkuId?: string | null;
    tierName?: string | null;
};

type TicketTeamOption = {
    id: string;
    label: string;
    designationType?: string | null;
};

type TicketKBaseProcessStatus = 'Draft' | 'Verified' | 'Needs Review';

type TicketKBaseProcess = {
    id: string;
    title: string;
    processType: 'service' | 'internal';
    serviceId: string | null;
    serviceCode: string | null;
    serviceLabel: string | null;
    offeringCode: string | null;
    category: string;
    subcategoryId: string | null;
    subcategories: string[];
    tags: string[];
    status: TicketKBaseProcessStatus;
    summary: string;
    objective: string;
    scope: string;
    prerequisites: string[];
    requiredInputs: string[];
    expectedOutputs: string[];
    toolsAndPlatforms: string[];
    orderedSteps: string[];
    qaChecklist: string[];
    definitionOfDone: string[];
    hardRules: string[];
    doNotDo: string[];
    approvalRequirements: string[];
    escalationTriggers: string[];
    handoffRules: string[];
    riskNotes: string[];
    bodyHtml: string;
    linkedIds: string[];
    isBookmarked: boolean;
    contributors: Array<{
        id: string;
        name: string;
        role: 'admin' | 'agent';
        edits: number;
    }>;
    revisions: Array<{
        id: string;
        author: string;
        changedAt: string;
        summary: string;
    }>;
};

type TicketKBaseProcessesResponse = {
    processes?: TicketKBaseProcess[];
};

type TicketCopilotUiMessage = {
    id: string;
    role: 'assistant' | 'user';
    body: string;
    pending?: boolean;
};

type TicketKBaseScopeLevel = 'none' | 'service' | 'offering' | 'subcategory';

type TicketKBaseScope = {
    level: TicketKBaseScopeLevel;
    serviceId: string;
    serviceCode: string;
    serviceLabel: string;
    offeringCode: string;
    offeringName: string;
    subcategoryId: string;
    subcategoryName: string;
};

const TICKET_KBASE_CACHE_TTL_MS = 60_000;
const TICKET_KBASE_RECENT_LIMIT = 6;
const TICKET_PROCESS_COPILOT_AGENT_CODE = 'ticket-process-copilot';
const MAX_KBASE_AI_ATTACHMENTS = 4;
const MAX_KBASE_AI_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const KBASE_AI_ALLOWED_ATTACHMENT_MIME_TYPES = new Set([
    'image/png',
    'image/jpeg',
    'image/webp',
    'application/pdf',
    'text/plain',
    'text/csv',
    'application/csv',
    'application/json',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

let cachedTicketKBaseProcesses: { fetchedAt: number; processes: TicketKBaseProcess[] } | null = null;
let cachedTicketKBaseProcessesPromise: Promise<TicketKBaseProcess[]> | null = null;

type TicketUpdatePayload = Partial<{
    title: string;
    description: string;
    projectId: string;
    milestoneId: string | null;
    priority: UiPriority;
    startDate: string;
    dueDate: string;
    assignedAgentId: string | null;
    assignedOrgMemberId: string | null;
    owningTeamNodeId: string | null;
    clientSubscriptionId: string | null;
    offeringId: string | null;
    offeringSubcategoryId: string | null;
    kbaseProcessId: string | null;
    estimatedMinutes: number | null;
    blockedByTicketIds: string[];
    handoffToTicketIds: string[];
    subtasks: Subtask[];
    participants: string[];
}>;

type TicketAutosaveSnapshot = {
    title: string;
    description: string;
    projectId: string;
    milestoneId: string | null;
    priority: UiPriority;
    startDate: string;
    dueDate: string;
    assignedAgentId: string | null;
    assignedOrgMemberId: string | null;
    owningTeamNodeId: string | null;
    clientSubscriptionId: string | null;
    offeringId: string | null;
    offeringSubcategoryId: string | null;
    kbaseProcessId: string | null;
    estimatedMinutes: number | null;
    blockedByTicketIds: string[];
    handoffToTicketIds: string[];
    subtasks: Subtask[];
    participants: string[];
};

type Props = {
    open: boolean;
    mode: EditorMode;
    readOnly?: boolean;
    onAssignSelf?: (id: string) => Promise<void>;
    project?: { id: string; name: string; color?: string; orgId?: string | null; milestones?: { id: string; title: string }[] };
    ticket?: {
        id: string;
        orgId?: string | null;
        ticketNumber?: string | null;
        title: string;
        createdAt?: any;
        description?: string;
        projectId: string;
        milestoneId?: string | null;
        milestoneTitle?: string | null;
        teamId?: string | null;
        owningTeamNodeId?: string | null;
        currentTeamNodeId?: string | null;
        currentTeamLabel?: string | null;
        defaultTeamNodeId?: string | null;
        defaultTeamLabel?: string | null;
        serviceId?: string | null;
        tierId?: string | null;
        offeringId?: string | null;
        offeringSubcategoryId?: string | null;
        kbaseProcessId?: string | null;
        clientSubscriptionId?: string | null;
        serviceCode?: string | null;
        serviceTitle?: string | null;
        tierSkuId?: string | null;
        tierName?: string | null;
        offeringCode?: string | null;
        offeringName?: string | null;
        offeringSubcategoryName?: string | null;
        offeringSubcategoryDescription?: string | null;
        estimatedMinutes?: number | null;
        remainingEstimateMinutes?: number | null;
        targetCompletionMinutes?: number | null;
        actualActiveMinutes?: number | null;
        pausedMinutes?: number | null;
        priority?: UiPriority;
        assignedAgentId?: string | null;
        assignedAgentFirstName?: string | null;
        assignedAgentLastName?: string | null;
        assignedAgentDisplayName?: string | null;
        assignedAgentEmail?: string | null;
        assignedAgentPhotoURL?: string | null;
        assignedOrgMemberId?: string | null;
        assignedOrgMemberFirstName?: string | null;
        assignedOrgMemberLastName?: string | null;
        assignedOrgMemberDisplayName?: string | null;
        assignedOrgMemberEmail?: string | null;
        assignedOrgMemberPhotoURL?: string | null;
        plannedStartAt?: any;
        plannedEndAt?: any;
        timezone?: string | null;
        slaResponseDueAt?: any;
        slaResolutionDueAt?: any;
        clientId?: string;
        clientBusinessName?: string | null;
        clientBusinessLogoURL?: string | null;
        clientFirstName?: string | null;
        clientLastName?: string | null;
        clientDisplayName?: string | null;
        clientEmail?: string | null;
        clientPhotoURL?: string | null;
        createdById?: string | null;
        subtasks?: Subtask[];
        status?: TicketStatus;                     // use shared type
        blockedBy?: TicketRelationshipSummary[];
        blocks?: TicketRelationshipSummary[];
        handoffTo?: TicketRelationshipSummary[];
        handoffFrom?: TicketRelationshipSummary[];
        participants?: string[];
        subscriptionOptions?: TicketSubscriptionOption[];
        subscriptionSelectionRequired?: boolean;
        subscriptionResolutionError?: string | null;
        offeringOptions?: TicketOfferingOption[];
        offeringSelectionRequired?: boolean;
        teamOptions?: TicketTeamOption[];
        projects?: { id: string; name: string }[];
        onDelete?: (id: string) => void;           // optional: nested handler
        onAttach?: (id: string, file: File) => void;
    } | null;
    projects?: { id: string; name: string; milestones?: { id: string; title: string }[] }[];
    agents?: AgentOption[]; // optional: used when assigning
    onClose: () => void;

    /* submit handlers supplied by the page (dispatches thunks) */
    onCreate: (payload: {
        title: string;
        description: string;
        projectId: string;
        milestoneId?: string | null;
        priority: UiPriority;
        assignedAgentId: string | null;
        assignedOrgMemberId?: string | null;
        owningTeamNodeId?: string | null;
        clientSubscriptionId?: string | null;
        offeringId?: string | null;
        offeringSubcategoryId?: string | null;
        kbaseProcessId?: string | null;
        estimatedMinutes?: number | null;
        blockedByTicketIds?: string[];
        handoffToTicketIds?: string[];
        subtasks: Subtask[];
    }) => Promise<string>; // returns new ticket id

    onUpdate: (id: string, payload: TicketUpdatePayload) => Promise<void>;
};

function normalizeSubscriptionOptions(raw: any): TicketSubscriptionOption[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((entry: any) => ({
            id: cleanText(entry?.id) || '',
            serviceId: cleanText(entry?.serviceId) || '',
            tierId: cleanText(entry?.tierId) || null,
            serviceCode: cleanText(entry?.serviceCode) || '',
            serviceTitle: cleanText(entry?.serviceTitle) || '',
            tierSkuId: cleanText(entry?.tierSkuId) || null,
            tierName: cleanText(entry?.tierName) || null,
        }))
        .filter((entry: TicketSubscriptionOption) => Boolean(entry.id && entry.serviceId && entry.serviceTitle));
}

function normalizeOfferingOptions(raw: any): TicketOfferingOption[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((entry: any) => ({
            id: cleanText(entry?.id) || '',
            code: cleanText(entry?.code) || '',
            name: cleanText(entry?.name) || '',
            type: cleanText(entry?.type) || null,
            source: cleanText(entry?.source) || null,
            targetCompletionMinutes: parseNumberish(entry?.targetCompletionMinutes),
            resolutionTargetMinutes: parseNumberish(entry?.resolutionTargetMinutes),
            subcategories: Array.isArray(entry?.subcategories)
                ? entry.subcategories
                    .map((subcategory: any) => ({
                        id: cleanText(subcategory?.id) || '',
                        name: cleanText(subcategory?.name) || '',
                        description: cleanText(subcategory?.description) || null,
                    }))
                    .filter((subcategory: { id: string; name: string }) => Boolean(subcategory.id && subcategory.name))
                : [],
        }))
        .filter((entry: TicketOfferingOption) => Boolean(entry.id && entry.name));
}

function resolveOfferingSubcategoryId(
    offeringOptions: TicketOfferingOption[],
    selectedOfferingId: string,
    candidateSubcategoryId: string,
): string {
    const normalizedSubcategoryId = cleanText(candidateSubcategoryId) || '';
    if (!normalizedSubcategoryId) return '';
    const selectedOffering = offeringOptions.find((option) => option.id === selectedOfferingId);
    if (!selectedOffering) return '';
    return selectedOffering.subcategories.some((subcategory) => subcategory.id === normalizedSubcategoryId)
        ? normalizedSubcategoryId
        : '';
}

function normalizeTeamOptions(raw: any): TicketTeamOption[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((entry: any) => ({
            id: cleanText(entry?.id) || '',
            label: cleanText(entry?.label) || '',
            designationType: cleanText(entry?.designationType) || null,
        }))
        .filter((entry: TicketTeamOption) => Boolean(entry.id && entry.label));
}

function formatSubscriptionLabel(option?: TicketSubscriptionOption | null): string {
    if (!option) return '--';
    const serviceLabel = cleanText(option.serviceTitle);
    const tierLabel = cleanText(option.tierName);
    if (!tierLabel) return serviceLabel;

    const normalizedService = serviceLabel.toLowerCase();
    const normalizedTier = tierLabel.toLowerCase();

    if (normalizedService && normalizedTier === normalizedService) {
        return serviceLabel;
    }

    if (normalizedService && normalizedTier.startsWith(normalizedService)) {
        const suffix = tierLabel
            .slice(serviceLabel.length)
            .replace(/^[-–—:/|]+/, '')
            .trim();
        return suffix ? `${serviceLabel} / ${suffix}` : serviceLabel;
    }

    return `${serviceLabel} / ${tierLabel}`;
}

function parseNumberish(raw: any): number | null {
    if (typeof raw === 'number' && Number.isFinite(raw)) return Math.round(raw);
    if (typeof raw === 'string') {
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) return Math.round(parsed);
    }
    return null;
}

function normalizeRelationshipIds(raw: unknown): string[] {
    if (!Array.isArray(raw)) return [];
    return Array.from(
        new Set(
            raw
                .map((entry) => cleanText(entry))
                .filter((entry): entry is string => Boolean(entry)),
        ),
    );
}

function mapTicketRelationshipSummary(raw: any): TicketRelationshipSummary | null {
    const id = cleanText(raw?.id);
    const title = cleanText(raw?.title);
    if (!id || !title) return null;
    return {
        id,
        ticketNumber: cleanText(raw?.ticketNumber) || null,
        title,
        status: cleanText(raw?.status) as TicketStatus | undefined,
        priority: cleanText(raw?.priority) as any,
        projectId: cleanText(raw?.projectId) || null,
        milestoneId: cleanText(raw?.milestoneId) || null,
        owningTeamNodeId: cleanText(raw?.owningTeamNodeId) || null,
        defaultTeamNodeId: cleanText(raw?.defaultTeamNodeId) || null,
        serviceId: cleanText(raw?.serviceId) || null,
        clientSubscriptionId: cleanText(raw?.clientSubscriptionId) || null,
        offeringId: cleanText(raw?.offeringId) || null,
        offeringSubcategoryId: cleanText(raw?.offeringSubcategoryId) || null,
    };
}

function formatMinutesAsHours(raw: number | null | undefined): string {
    if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) return '--';
    const hours = raw / 60;
    return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
}

/* Helpers */

// Robust timestamp-to-Date converter.
function tsToDate(ts?: any): Date | null {
    if (!ts) return null;

    // Firestore Timestamp (client/admin SDKs)
    if (typeof ts?.toDate === 'function') return ts.toDate();

    // {seconds, nanoseconds} or {_seconds, _nanoseconds}
    const secs =
        (typeof ts === 'object' && (ts.seconds ?? ts._seconds)) as number | undefined;
    if (typeof secs === 'number') return new Date(secs * 1000);

    // number (either ms or s)
    if (typeof ts === 'number') {
        const ms = ts > 1e12 ? ts : ts * 1000; // treat small numbers as seconds
        const d = new Date(ms);
        return isNaN(d.getTime()) ? null : d;
    }

    // ISO/string fallback
    if (typeof ts === 'string') {
        const d = new Date(ts);
        return isNaN(d.getTime()) ? null : d;
    }

    return null;
}

function mapUiToApiPriority(p: UiPriority) {
    return p === 'urgent' ? 'P0' : p === 'high' ? 'P1' : p === 'low' ? 'P3' : 'P2';
}

function toDateInputValue(ts?: any): string {
  const d = tsToDate(ts);
  if (!d) return '';
  const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toDateInputValueInTimezone(ts: any, timezone?: string | null): string {
    if (!ts) return '';
    try {
        return convertFromUTC(tsToDate(ts) as Date, timezone || 'UTC').localDate;
    } catch {
        return toDateInputValue(ts);
    }
}

function toPlannedBoundaryIsoFromLocalDate(
    value: string | undefined,
    boundary: 'start' | 'end',
    timezone: string,
): string | null {
    const normalized = String(value ?? '').trim();
    if (!normalized) return null;
    const utcDate = convertToUTC(normalized, boundary === 'start' ? '00:00' : '23:59', timezone);
    if (Number.isNaN(utcDate.getTime())) return null;
    return utcDate.toISOString();
}

function cleanText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function firstNonEmptyText(...values: unknown[]): string {
    for (const value of values) {
        const cleaned = cleanText(value);
        if (cleaned) return cleaned;
    }
    return '';
}

function normalizeSearchText(value: unknown): string {
    return cleanText(value).toLowerCase();
}

function normalizeTicketKBaseProcesses(raw: unknown): TicketKBaseProcess[] {
    if (!Array.isArray(raw)) return [];

    return raw
        .map((entry): TicketKBaseProcess => ({
            id: cleanText((entry as any)?.id),
            title: cleanText((entry as any)?.title),
            processType: (entry as any)?.processType === 'service' ? 'service' : 'internal',
            serviceId: cleanText((entry as any)?.serviceId) || null,
            serviceCode: cleanText((entry as any)?.serviceCode) || null,
            serviceLabel: cleanText((entry as any)?.serviceLabel) || null,
            offeringCode: cleanText((entry as any)?.offeringCode) || null,
            category: cleanText((entry as any)?.category),
            subcategoryId: cleanText((entry as any)?.subcategoryId) || null,
            subcategories: Array.isArray((entry as any)?.subcategories)
                ? (entry as any).subcategories.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            tags: Array.isArray((entry as any)?.tags)
                ? (entry as any).tags.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            status:
                (entry as any)?.status === 'Verified'
                    ? 'Verified'
                    : (entry as any)?.status === 'Needs Review'
                        ? 'Needs Review'
                        : 'Draft',
            summary: cleanText((entry as any)?.summary),
            objective: cleanText((entry as any)?.objective),
            scope: cleanText((entry as any)?.scope),
            prerequisites: Array.isArray((entry as any)?.prerequisites)
                ? (entry as any).prerequisites.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            requiredInputs: Array.isArray((entry as any)?.requiredInputs)
                ? (entry as any).requiredInputs.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            expectedOutputs: Array.isArray((entry as any)?.expectedOutputs)
                ? (entry as any).expectedOutputs.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            toolsAndPlatforms: Array.isArray((entry as any)?.toolsAndPlatforms)
                ? (entry as any).toolsAndPlatforms.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            orderedSteps: Array.isArray((entry as any)?.orderedSteps)
                ? (entry as any).orderedSteps.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            qaChecklist: Array.isArray((entry as any)?.qaChecklist)
                ? (entry as any).qaChecklist.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            definitionOfDone: Array.isArray((entry as any)?.definitionOfDone)
                ? (entry as any).definitionOfDone.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            hardRules: Array.isArray((entry as any)?.hardRules)
                ? (entry as any).hardRules.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            doNotDo: Array.isArray((entry as any)?.doNotDo)
                ? (entry as any).doNotDo.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            approvalRequirements: Array.isArray((entry as any)?.approvalRequirements)
                ? (entry as any).approvalRequirements.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            escalationTriggers: Array.isArray((entry as any)?.escalationTriggers)
                ? (entry as any).escalationTriggers.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            handoffRules: Array.isArray((entry as any)?.handoffRules)
                ? (entry as any).handoffRules.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            riskNotes: Array.isArray((entry as any)?.riskNotes)
                ? (entry as any).riskNotes.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            bodyHtml: cleanText((entry as any)?.bodyHtml),
            linkedIds: Array.isArray((entry as any)?.linkedIds)
                ? (entry as any).linkedIds.map((value: unknown) => cleanText(value)).filter(Boolean)
                : [],
            isBookmarked: Boolean((entry as any)?.isBookmarked),
            contributors: Array.isArray((entry as any)?.contributors)
                ? (entry as any).contributors
                    .map((value: any) => ({
                        id: cleanText(value?.id),
                        name: cleanText(value?.name),
                        role: value?.role === 'admin' ? 'admin' : 'agent',
                        edits: parseNumberish(value?.edits) ?? 0,
                    }))
                    .filter((value: { name: string }) => Boolean(value.name))
                : [],
            revisions: Array.isArray((entry as any)?.revisions)
                ? (entry as any).revisions
                    .map((value: any) => ({
                        id: cleanText(value?.id),
                        author: cleanText(value?.author),
                        changedAt: cleanText(value?.changedAt),
                        summary: cleanText(value?.summary),
                    }))
                    .filter((value: { id: string; author: string }) => Boolean(value.id || value.author))
                : [],
        }))
        .filter((entry) => Boolean(entry.id && entry.title));
}

async function fetchCachedTicketKBaseProcesses(force = false): Promise<TicketKBaseProcess[]> {
    const now = Date.now();
    if (!force && cachedTicketKBaseProcesses && now - cachedTicketKBaseProcesses.fetchedAt < TICKET_KBASE_CACHE_TTL_MS) {
        return cachedTicketKBaseProcesses.processes;
    }
    if (!force && cachedTicketKBaseProcessesPromise) {
        return cachedTicketKBaseProcessesPromise;
    }

    cachedTicketKBaseProcessesPromise = apiFetch<TicketKBaseProcessesResponse>('/api/admin/kbase/processes')
        .then((response) => {
            const processes = normalizeTicketKBaseProcesses(response?.processes);
            cachedTicketKBaseProcesses = {
                fetchedAt: Date.now(),
                processes,
            };
            return processes;
        })
        .finally(() => {
            cachedTicketKBaseProcessesPromise = null;
        });

    return cachedTicketKBaseProcessesPromise;
}

function getTicketKBaseRecentStorageKey(userId: string): string {
    return `staff-ticket-kbase-recent:${userId}`;
}

function readRecentTicketKBaseProcessIds(userId: string | null | undefined): string[] {
    if (!userId || typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(getTicketKBaseRecentStorageKey(userId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.map((value) => cleanText(value)).filter(Boolean) : [];
    } catch {
        return [];
    }
}

function writeRecentTicketKBaseProcessIds(userId: string | null | undefined, values: readonly string[]) {
    if (!userId || typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(
            getTicketKBaseRecentStorageKey(userId),
            JSON.stringify(values.slice(0, TICKET_KBASE_RECENT_LIMIT)),
        );
    } catch {
        // ignore local storage failures
    }
}

function formatKBaseAiAttachmentSize(sizeBytes: number) {
    if (sizeBytes >= 1024 * 1024) {
        return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    if (sizeBytes >= 1024) {
        return `${Math.max(1, Math.round(sizeBytes / 1024))} KB`;
    }
    return `${sizeBytes} B`;
}

function buildKBaseAiAttachmentUserMessage(
    message: string,
    attachments: readonly File[],
) {
    const attachmentLabel = attachments
        .map((attachment) => cleanText(attachment.name))
        .filter(Boolean)
        .join(', ');
    if (!attachmentLabel) {
        return message;
    }
    if (message) {
        return `${message}\n\n[Ephemeral attachments: ${attachmentLabel}]`;
    }
    return `Uploaded ephemeral attachments: ${attachmentLabel}`;
}

function mapTicketCopilotThreadMessagesToUi(
    messages: readonly AICopilotThreadMessage[] | null | undefined,
): TicketCopilotUiMessage[] {
    return (messages ?? [])
        .filter((message) => message.role === 'assistant' || message.role === 'user')
        .map((message) => ({
            id: cleanText(message.id) || `message-${Math.random().toString(36).slice(2, 8)}`,
            role: message.role as 'assistant' | 'user',
            body: cleanText(message.contentText) || '',
        }))
        .filter((message) => Boolean(message.body));
}

function threadMatchesSelectedProcess(
    thread: AICopilotThread,
    processId: string,
): boolean {
    const normalizedProcessId = cleanText(processId);
    if (!normalizedProcessId) return false;
    const ids = [...new Set((thread.contextProcessIds ?? []).map((value) => cleanText(value)).filter(Boolean))];
    return ids.length === 1 && ids[0] === normalizedProcessId;
}

function rankTicketKBaseStatus(status: TicketKBaseProcessStatus): number {
    if (status === 'Verified') return 0;
    if (status === 'Needs Review') return 1;
    return 2;
}

function resolveTicketKBaseScopeLevel(scope: TicketKBaseScope): TicketKBaseScopeLevel {
    if (scope.subcategoryId) return 'subcategory';
    if (scope.offeringCode) return 'offering';
    if (scope.serviceId || scope.serviceCode || scope.serviceLabel) return 'service';
    return 'none';
}

function processMatchesTicketService(process: TicketKBaseProcess, scope: TicketKBaseScope): boolean {
    if (process.processType !== 'service') return false;
    if (!scope.serviceId && !scope.serviceCode && !scope.serviceLabel) return false;

    return Boolean(
        (scope.serviceId && cleanText(process.serviceId) === scope.serviceId) ||
        (scope.serviceCode && normalizeSearchText(process.serviceCode) === normalizeSearchText(scope.serviceCode)) ||
        (scope.serviceLabel && normalizeSearchText(process.serviceLabel) === normalizeSearchText(scope.serviceLabel))
    );
}

function processMatchesTicketOffering(process: TicketKBaseProcess, scope: TicketKBaseScope): boolean {
    if (!processMatchesTicketService(process, scope)) return false;
    if (!scope.offeringCode) return false;
    if (normalizeSearchText(process.offeringCode) === normalizeSearchText(scope.offeringCode)) return true;
    return normalizeSearchText(process.category) === normalizeSearchText(scope.offeringName);
}

function processMatchesTicketSubcategory(process: TicketKBaseProcess, scope: TicketKBaseScope): boolean {
    if (!processMatchesTicketOffering(process, scope)) return false;
    if (!scope.subcategoryId && !scope.subcategoryName) return false;
    if (scope.subcategoryId && cleanText(process.subcategoryId) === scope.subcategoryId) return true;
    return process.subcategories.some(
        (subcategory) => normalizeSearchText(subcategory) === normalizeSearchText(scope.subcategoryName),
    );
}

function processMatchesTicketScope(process: TicketKBaseProcess, scope: TicketKBaseScope): boolean {
    const scopeLevel = resolveTicketKBaseScopeLevel(scope);
    if (scopeLevel === 'subcategory') return processMatchesTicketSubcategory(process, scope);
    if (scopeLevel === 'offering') return processMatchesTicketOffering(process, scope);
    if (scopeLevel === 'service') return processMatchesTicketService(process, scope);
    return false;
}

function matchesTicketKBaseQuery(process: TicketKBaseProcess, query: string): boolean {
    const normalizedQuery = normalizeSearchText(query);
    if (!normalizedQuery) return true;

    const haystack = [
        process.title,
        process.summary,
        process.serviceLabel,
        process.category,
        process.offeringCode,
        process.subcategories.join(' '),
        process.tags.join(' '),
        process.status,
    ]
        .map((value) => normalizeSearchText(value))
        .join(' ');

    return normalizedQuery
        .split(/\s+/)
        .filter(Boolean)
        .every((token) => haystack.includes(token));
}

function scoreTicketKBaseProcessSearch(
    process: TicketKBaseProcess,
    query: string,
    scope: TicketKBaseScope,
): number {
    const normalizedQuery = normalizeSearchText(query);
    if (!normalizedQuery) return 0;

    let score = 0;
    const title = normalizeSearchText(process.title);
    const category = normalizeSearchText(process.category);
    const summary = normalizeSearchText(process.summary);
    const tags = process.tags.map((tag) => normalizeSearchText(tag));
    const subcategories = process.subcategories.map((subcategory) => normalizeSearchText(subcategory));

    if (title === normalizedQuery) score += 120;
    else if (title.startsWith(normalizedQuery)) score += 90;
    else if (title.includes(normalizedQuery)) score += 70;

    if (category.includes(normalizedQuery)) score += 34;
    if (summary.includes(normalizedQuery)) score += 18;
    if (tags.some((tag) => tag.includes(normalizedQuery))) score += 24;
    if (subcategories.some((subcategory) => subcategory.includes(normalizedQuery))) score += 28;
    if (processMatchesTicketScope(process, scope)) score += 22;
    score -= rankTicketKBaseStatus(process.status) * 3;

    return score;
}

function normalizeSubtaskDate(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    const asString = String(value).trim();
    if (!asString) return null;

    const dateMatch = asString.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateMatch?.[1]) return dateMatch[1];

    const parsed = new Date(asString);
    if (Number.isNaN(parsed.getTime())) return null;

    return toDateInputValue(parsed.toISOString()) || null;
}

function parseDateOnly(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;

    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    const day = Number(match[3]);
    if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || !Number.isFinite(day)) return null;

    const parsed = new Date(year, monthIndex, day);
    if (Number.isNaN(parsed.getTime())) return null;
    if (
        parsed.getFullYear() !== year ||
        parsed.getMonth() !== monthIndex ||
        parsed.getDate() !== day
    ) {
        return null;
    }
    return parsed;
}

function startOfDay(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function startOfWeekMonday(value: Date): Date {
    const start = startOfDay(value);
    const offset = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - offset);
    return start;
}

function formatSubtaskDueDateLabel(value: string | null | undefined): string {
    if (!value) return '';
    const dueDate = parseDateOnly(value);
    if (!dueDate) return '';

    const today = startOfDay(new Date());
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    if (dueDate.getTime() === tomorrow.getTime()) return 'tomorrow';

    const weekStart = startOfWeekMonday(today);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    if (dueDate >= weekStart && dueDate <= weekEnd) {
        const weekday = dueDate.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
        if (weekday === 'tue') return 'tues';
        if (weekday === 'thu') return 'thurs';
        return weekday;
    }

    return dueDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    });
}

function normalizeSubtask(raw: unknown, index: number): Subtask {
    const record = (raw && typeof raw === 'object') ? (raw as Record<string, unknown>) : {};
    const id = cleanText(record.id) || `subtask-${index + 1}`;
    const text = typeof record.text === 'string' ? record.text : String(record.text ?? '');
    const doneValue = record.done;
    const done =
        typeof doneValue === 'boolean'
            ? doneValue
            : String(doneValue ?? '').toLowerCase() === 'true';

    return {
        id,
        text,
        done,
        dueDate: normalizeSubtaskDate(record.dueDate),
        assigneeId: cleanText(record.assigneeId) || null,
    };
}

function normalizeSubtasks(raw: unknown): Subtask[] {
    if (!Array.isArray(raw)) return [];
    return raw.map((entry, index) => normalizeSubtask(entry, index));
}

function toEstimatedHoursInput(value?: number | null): string {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return '';
    const hours = value / 60;
    return hours % 1 === 0 ? String(hours) : hours.toFixed(1);
}

function parseEstimatedMinutesInput(value: string): number | null {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return null;
    const parsed = Number.parseFloat(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    return Math.round(parsed * 60);
}

function areSubtasksEqual(a: Subtask[], b: Subtask[]): boolean {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let index = 0; index < a.length; index += 1) {
        const left = a[index];
        const right = b[index];
        if (
            left.id !== right.id ||
            left.text !== right.text ||
            left.done !== right.done ||
            left.dueDate !== right.dueDate ||
            left.assigneeId !== right.assigneeId
        ) {
            return false;
        }
    }
    return true;
}

/* Component */

export default function TicketEditorDrawer({
    open,
    mode,
    project,
    ticket: ticketProp,
    agents = [],
    projects = [],
    onClose,
    onCreate,
    onUpdate,
    readOnly = false,
    onAssignSelf,
}: Props) {
    const { user, role, activeOrgId } = useAuth();
    const api = useApi();
    const dispatch = useDispatch<AppDispatch>();
    const isStaff = role === 'agent' || role === 'admin';
    const isClientViewer = role === 'client';
    const cachedTicketSelector = React.useMemo(
        () => (ticketProp?.id ? selectCachedTicketById(ticketProp.id) : null),
        [ticketProp?.id],
    );
    const ticketDetailMetaSelector = React.useMemo(
        () => (ticketProp?.id ? selectTicketDetailMeta(ticketProp.id) : null),
        [ticketProp?.id],
    );
    const cachedCommentsSelector = React.useMemo(
        () => (ticketProp?.id ? selectCommentListForTicket(ticketProp.id) : null),
        [ticketProp?.id],
    );
    const cachedCommentsMetaSelector = React.useMemo(
        () => (ticketProp?.id ? selectCommentBucketMeta(ticketProp.id) : null),
        [ticketProp?.id],
    );
    const cachedTicket = useSelector((state: RootState) =>
        cachedTicketSelector ? cachedTicketSelector(state) : undefined,
    );
    const ticketDetailMeta = useSelector((state: RootState) =>
        ticketDetailMetaSelector ? ticketDetailMetaSelector(state) : null,
    );
    const ticket = React.useMemo(
        () =>
            buildTicketEditorTicket(
                cachedTicket ? { ...(ticketProp ?? {}), ...cachedTicket } : ticketProp,
                {
                    fallbackProjectId: project?.id ?? null,
                    onDelete: ticketProp?.onDelete,
                    onAttach: ticketProp?.onAttach,
                },
            ),
        [cachedTicket, project?.id, ticketProp],
    );
    const cachedComments = useSelector((state: RootState) =>
        cachedCommentsSelector ? cachedCommentsSelector(state) : [],
    );
    const cachedCommentsMeta = useSelector((state: RootState) =>
        cachedCommentsMetaSelector ? cachedCommentsMetaSelector(state) : null,
    );
    const canEditScheduleWindow = role === 'admin' || role === 'agent';
    const canEditInternalAssignee = role === 'admin' || role === 'agent';
    const canEditOrganizationAssignee = role === 'client' && !readOnly;
    const managedWorkflowFieldsReadOnly = readOnly || isClientViewer;

    // initial state (create uses project; edit uses ticket)
    const [title, setTitle] = React.useState('');
    const [projectId, setProjectId] = React.useState(project?.id || ticket?.projectId || '');
    const [selectedMilestoneId, setSelectedMilestoneId] = React.useState(ticket?.milestoneId ?? '');
    const [priority, setPriority] = React.useState<UiPriority>(ticket?.priority || 'medium');
    const [assigned, setAssigned] = React.useState<string | null>(ticket?.assignedAgentId ?? null);
    const [assignedOrgMember, setAssignedOrgMember] = React.useState<string | null>(
        ticket?.assignedOrgMemberId ?? null,
    );
    const [owningTeamNodeId, setOwningTeamNodeId] = React.useState<string>(
        ticket?.owningTeamNodeId ?? ticket?.currentTeamNodeId ?? ticket?.teamId ?? '',
    );
    const [clientSubscriptionId, setClientSubscriptionId] = React.useState<string>(
        ticket?.clientSubscriptionId ?? '',
    );
    const [offeringId, setOfferingId] = React.useState<string>(ticket?.offeringId ?? '');
    const [offeringSubcategoryId, setOfferingSubcategoryId] = React.useState<string>(
        ticket?.offeringSubcategoryId ?? '',
    );
    const [selectedKbaseProcessId, setSelectedKbaseProcessId] = React.useState(
        ticket?.kbaseProcessId ?? '',
    );
    const [kbaseProcesses, setKbaseProcesses] = React.useState<TicketKBaseProcess[]>([]);
    const [kbaseProcessesLoading, setKbaseProcessesLoading] = React.useState(false);
    const [kbaseProcessesError, setKbaseProcessesError] = React.useState<string | null>(null);
    const [kbaseMenuOpen, setKbaseMenuOpen] = React.useState(false);
    const [kbaseSearchQuery, setKbaseSearchQuery] = React.useState('');
    const [isKBaseProcessPanelOpen, setIsKBaseProcessPanelOpen] = React.useState(false);
    const [isKBaseAiPanelOpen, setIsKBaseAiPanelOpen] = React.useState(false);
    const [kBaseAiDraft, setKBaseAiDraft] = React.useState('');
    const [kBaseAiThreadId, setKBaseAiThreadId] = React.useState<string | null>(null);
    const [kBaseAiMessages, setKBaseAiMessages] = React.useState<TicketCopilotUiMessage[]>([]);
    const [kBaseAiLoading, setKBaseAiLoading] = React.useState(false);
    const [kBaseAiSending, setKBaseAiSending] = React.useState(false);
    const [kBaseAiError, setKBaseAiError] = React.useState<string | null>(null);
    const [kBaseAiAttachments, setKBaseAiAttachments] = React.useState<File[]>([]);
    const [isKBaseAiActionsOpen, setIsKBaseAiActionsOpen] = React.useState(false);
    const [kBaseAiClearing, setKBaseAiClearing] = React.useState(false);
    const [recentKbaseProcessIds, setRecentKbaseProcessIds] = React.useState<string[]>([]);
    const kbaseFinderRef = React.useRef<HTMLDivElement | null>(null);
    const kbaseSearchInputRef = React.useRef<HTMLInputElement | null>(null);
    const kBaseAiLoadVersionRef = React.useRef(0);
    const kBaseAiAbortRef = React.useRef<AbortController | null>(null);
    const kBaseAiMessagesRef = React.useRef<TicketCopilotUiMessage[]>([]);
    const kBaseAiActionsRef = React.useRef<HTMLDivElement | null>(null);
    const kBaseAiFileInputRef = React.useRef<HTMLInputElement | null>(null);
    React.useEffect(() => {
        kBaseAiMessagesRef.current = kBaseAiMessages;
    }, [kBaseAiMessages]);
    const ticketContextKey = React.useMemo(() => {
        const clientId = cleanText(ticket?.clientId);
        const projectIdForContext = cleanText(projectId) || cleanText(project?.id) || cleanText(ticket?.projectId);
        const orgIdForContext = cleanText(ticket?.orgId) || cleanText(project?.orgId);
        if (!clientId && !projectIdForContext) return null;
        return buildTicketContextCacheKey({
            clientId,
            projectId: projectIdForContext,
            orgId: orgIdForContext,
            clientSubscriptionId: cleanText(clientSubscriptionId) || null,
        });
    }, [clientSubscriptionId, project?.id, project?.orgId, projectId, ticket?.clientId, ticket?.orgId, ticket?.projectId]);
    const cachedTicketContext = useSelector((state: RootState) =>
        ticketContextKey ? selectTicketContextByKey(ticketContextKey)(state) : null,
    );
    const ticketContextMeta = useSelector((state: RootState) =>
        ticketContextKey ? selectTicketContextMetaByKey(ticketContextKey)(state) : null,
    );
    const [estimatedHours, setEstimatedHours] = React.useState<string>(
        typeof ticket?.estimatedMinutes === 'number' && Number.isFinite(ticket.estimatedMinutes)
            ? (ticket.estimatedMinutes / 60).toFixed(ticket.estimatedMinutes % 60 === 0 ? 0 : 1)
            : '',
    );
    const [subscriptionSelectionRequired, setSubscriptionSelectionRequired] = React.useState<boolean>(
        Boolean(ticket?.subscriptionSelectionRequired),
    );
    const [subscriptionResolutionError, setSubscriptionResolutionError] = React.useState<string | null>(
        cleanText(ticket?.subscriptionResolutionError) || null,
    );
    const [offeringSelectionRequired, setOfferingSelectionRequired] = React.useState<boolean>(
        Boolean(ticket?.offeringSelectionRequired),
    );
    const [desc, setDesc] = React.useState(ticket?.description || '');
    const [startDate, setStartDate] = React.useState<string>(toDateInputValueInTimezone(ticket?.plannedStartAt, ticket?.timezone));
    const [dueDate, setDueDate] = React.useState<string>(toDateInputValueInTimezone(ticket?.plannedEndAt, ticket?.timezone));
    const [subtasks, setSubtasks] = React.useState<Subtask[]>(normalizeSubtasks(ticket?.subtasks ?? []));
    const [draggingSubtaskId, setDraggingSubtaskId] = React.useState<string | null>(null);
    const [subtaskDropTargetId, setSubtaskDropTargetId] = React.useState<string | null>(null);
    const [openSubtaskAssigneeId, setOpenSubtaskAssigneeId] = React.useState<string | null>(null);
    const subtaskDateInputRefs = React.useRef<Record<string, HTMLInputElement | null>>({});
    const [saving, setSaving] = React.useState(false);
    const [saveState, setSaveState] = React.useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const viewerTimezone = React.useMemo(
        () => getUserTimezone() || ticket?.timezone || 'UTC',
        [ticket?.timezone],
    );
    const [formError, setFormError] = React.useState<string | null>(null);
    const isFirstLoadRef = React.useRef(true);
    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const [autosaveSuppressionVersion, setAutosaveSuppressionVersion] = React.useState(0);
    const autosaveSuppressionVersionRef = React.useRef(autosaveSuppressionVersion);
    const lastHandledAutosaveSuppressionRef = React.useRef(0);
    const autosaveBaselineRef = React.useRef<TicketAutosaveSnapshot | null>(null);
    autosaveSuppressionVersionRef.current = autosaveSuppressionVersion;


    // comments
    const [comments, setComments] = React.useState<TicketComment[]>([]);
    const [commentText, setCommentText] = React.useState('');
    const [status, setStatus] = React.useState<TicketStatus>(ticket?.status ?? 'open');
    const [participants, setParticipants] = React.useState<string[]>(ticket?.participants ?? []);
    const [blockedByTicketIds, setBlockedByTicketIds] = React.useState<string[]>(
        normalizeRelationshipIds(ticket?.blockedBy?.map((entry) => entry.id)),
    );
    const [handoffToTicketIds, setHandoffToTicketIds] = React.useState<string[]>(
        normalizeRelationshipIds(ticket?.handoffTo?.map((entry) => entry.id)),
    );
    // cache meta we might not get via props
    const [createdAtOverride, setCreatedAtOverride] = React.useState<any>(null);
    const [projectName, setProjectName] = React.useState<string | null>(null);
    const [clientIdentity, setClientIdentity] = React.useState<{
        uid?: string | null;
        businessName?: string | null;
        businessLogoURL?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        displayName?: string | null;
        email?: string | null;
        photoURL?: string | null;
    }>({});
    const [subscriptionOptions, setSubscriptionOptions] = React.useState<TicketSubscriptionOption[]>(
        Array.isArray(ticket?.subscriptionOptions) ? ticket.subscriptionOptions : [],
    );
    const [offeringOptions, setOfferingOptions] = React.useState<TicketOfferingOption[]>(
        Array.isArray(ticket?.offeringOptions) ? ticket.offeringOptions : [],
    );
    const [teamOptions, setTeamOptions] = React.useState<TicketTeamOption[]>(
        Array.isArray(ticket?.teamOptions) ? ticket.teamOptions : [],
    );
    const allStoreTickets = useSelector(selectAllTickets);
    const relationshipOptionById = React.useMemo(() => {
        const next = new Map<string, TicketRelationshipSummary>();
        const seedEntries = [
            ...(Array.isArray(ticket?.blockedBy) ? ticket.blockedBy : []),
            ...(Array.isArray(ticket?.blocks) ? ticket.blocks : []),
            ...(Array.isArray(ticket?.handoffTo) ? ticket.handoffTo : []),
            ...(Array.isArray(ticket?.handoffFrom) ? ticket.handoffFrom : []),
        ];
        for (const entry of seedEntries) {
            const summary = mapTicketRelationshipSummary(entry);
            if (!summary) continue;
            next.set(summary.id, summary);
        }

        const projectIdForRelationships =
            cleanText(projectId) ||
            cleanText(ticket?.projectId) ||
            cleanText(project?.id) ||
            '';

        if (projectIdForRelationships) {
            for (const candidate of allStoreTickets) {
                const summary = mapTicketRelationshipSummary(candidate);
                if (!summary || summary.id === ticket?.id) continue;
                if (cleanText(summary.projectId) !== projectIdForRelationships) continue;
                next.set(summary.id, summary);
            }
        }

        return next;
    }, [allStoreTickets, project?.id, projectId, ticket?.blockedBy, ticket?.blocks, ticket?.handoffFrom, ticket?.handoffTo, ticket?.id, ticket?.projectId]);
    const relationshipOptions = React.useMemo(
        () =>
            Array.from(relationshipOptionById.values()).sort((left, right) => {
                const leftTicketNumberRaw = cleanText(left.ticketNumber);
                const rightTicketNumberRaw = cleanText(right.ticketNumber);
                const leftTicketNumber = leftTicketNumberRaw ? Number(leftTicketNumberRaw) : null;
                const rightTicketNumber = rightTicketNumberRaw ? Number(rightTicketNumberRaw) : null;
                if (
                    leftTicketNumber !== null &&
                    rightTicketNumber !== null &&
                    Number.isFinite(leftTicketNumber) &&
                    Number.isFinite(rightTicketNumber) &&
                    leftTicketNumber !== rightTicketNumber
                ) {
                    return leftTicketNumber - rightTicketNumber;
                }
                return left.title.localeCompare(right.title);
            }),
        [relationshipOptionById],
    );
    const teamLabelByOptionId = React.useMemo(() => {
        const next = new Map<string, string>();
        for (const option of teamOptions) {
            const id = cleanText(option.id);
            const label = cleanText(option.label);
            if (!id || !label) continue;
            next.set(id, label);
        }
        return next;
    }, [teamOptions]);
    const orgAssigneeCacheKey = React.useMemo(
        () => buildTicketAssigneeCacheKey(ticket?.orgId ?? project?.orgId ?? activeOrgId ?? null),
        [activeOrgId, project?.orgId, ticket?.orgId],
    );
    const cachedOrgAssigneesSelector = React.useMemo(
        () => selectTicketOrgAssigneesByKey(orgAssigneeCacheKey),
        [orgAssigneeCacheKey],
    );
    const fetchedAssignees = useSelector((state: RootState) => cachedOrgAssigneesSelector(state));
    const normalizedOwningTeamNodeId = React.useMemo(
        () => String(owningTeamNodeId ?? '').trim(),
        [owningTeamNodeId],
    );
    const cachedInternalTeamAssigneesSelector = React.useMemo(
        () => selectTicketInternalTeamAssigneesByTeamId(normalizedOwningTeamNodeId),
        [normalizedOwningTeamNodeId],
    );
    const internalTeamAssignees = useSelector((state: RootState) =>
        normalizedOwningTeamNodeId ? cachedInternalTeamAssigneesSelector(state) : EMPTY_AGENT_OPTIONS,
    );
    const hasFreshTicketDetail =
        Boolean(ticketDetailMeta?.fetchedAt) &&
        Date.now() - Number(ticketDetailMeta?.fetchedAt) < TICKET_DETAIL_TTL_MS;
    const ticketHydrating =
        open &&
        mode === 'edit' &&
        !ticket &&
        !hasFreshTicketDetail &&
        Boolean(ticketDetailMeta?.loading);
    const contractContextLoading =
        open &&
        !readOnly &&
        Boolean(ticketContextKey) &&
        !cachedTicketContext &&
        Boolean(ticketContextMeta?.loading);

    const suppressNextAutosave = React.useCallback(() => {
        setAutosaveSuppressionVersion((current) => current + 1);
    }, []);

    const buildAutosaveSnapshot = React.useCallback(
        (
            overrides: Partial<TicketAutosaveSnapshot> = {},
        ): TicketAutosaveSnapshot => ({
            title,
            description: desc,
            projectId,
            milestoneId: selectedMilestoneId || null,
            priority,
            startDate,
            dueDate,
            assignedAgentId: assigned ?? null,
            assignedOrgMemberId: assignedOrgMember ?? null,
            owningTeamNodeId: owningTeamNodeId || null,
            clientSubscriptionId: clientSubscriptionId || null,
            offeringId: offeringId || null,
            offeringSubcategoryId: offeringSubcategoryId || null,
            kbaseProcessId: selectedKbaseProcessId || null,
            estimatedMinutes: parseEstimatedMinutesInput(estimatedHours),
            blockedByTicketIds,
            handoffToTicketIds,
            subtasks,
            participants,
            ...overrides,
        }),
        [
            blockedByTicketIds,
            assigned,
            assignedOrgMember,
            clientSubscriptionId,
            desc,
            dueDate,
            estimatedHours,
            handoffToTicketIds,
            offeringId,
            offeringSubcategoryId,
            selectedKbaseProcessId,
            owningTeamNodeId,
            participants,
            priority,
            projectId,
            selectedMilestoneId,
            startDate,
            subtasks,
            title,
        ],
    );
    const buildAutosaveSnapshotRef = React.useRef(buildAutosaveSnapshot);
    buildAutosaveSnapshotRef.current = buildAutosaveSnapshot;

    const updateAutosaveBaseline = React.useCallback((patch: Partial<TicketAutosaveSnapshot>) => {
        const snapshotBuilder = buildAutosaveSnapshotRef.current;
        const base =
            autosaveBaselineRef.current ??
            (snapshotBuilder ? snapshotBuilder() : null);
        if (!base) return;
        autosaveBaselineRef.current = {
            ...base,
            ...patch,
        };
    }, []);

    React.useEffect(() => {
        const validIds = new Set(relationshipOptions.map((entry) => entry.id));
        setBlockedByTicketIds((previous) => {
            const next = previous.filter((id) => validIds.has(id));
            return next.length === previous.length ? previous : next;
        });
        setHandoffToTicketIds((previous) => {
            const next = previous.filter((id) => validIds.has(id));
            return next.length === previous.length ? previous : next;
        });
    }, [relationshipOptions]);

    // Assignee menus
    const [showOrgAssigneeMenu, setShowOrgAssigneeMenu] = React.useState(false);
    const [showAssignedAgentMenu, setShowAssignedAgentMenu] = React.useState(false);

    const assignCellRef = React.useRef<HTMLDivElement>(null);
    const agentAssignCellRef = React.useRef<HTMLDivElement>(null);




    // who created the ticket (controls delete permission)
    const [createdById, setCreatedById] = React.useState<string | null>(null);

    // actions menu state
    const [showMenu, setShowMenu] = React.useState(false);
    const [confirmDelete, setConfirmDelete] = React.useState(false);

    // Anchors for click-outside handling.
    const rightRef = React.useRef<HTMLDivElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const confirmRef = React.useRef<HTMLDivElement>(null);


    const userMap = useSelector(selectTicketUserIdentityMap);

    async function fetchUsersById(uids: string[]) {
        const want = Array.from(new Set(uids.filter(Boolean))).filter(id => !userMap[id]);
        if (!want.length) return;
        if (role !== 'admin' && role !== 'agent') return;
        await Promise.allSettled(
            want.map((uid) => dispatch(fetchTicketUserIdentity({ id: uid }))),
        );
    }

    // Convert a uid to a display label (prefers @username, then displayName, otherwise uid)
    function nameFromIdentity(
        identity: Partial<UserIdentityLookup> | null | undefined,
        fallbackId?: string | null,
    ): string {
        const firstName = firstNonEmptyText(identity?.firstName);
        const lastName = firstNonEmptyText(identity?.lastName);
        const combinedName = [firstName, lastName].filter(Boolean).join(' ').trim();
        if (combinedName) return combinedName;

        const displayName = firstNonEmptyText(identity?.displayName, identity?.name);
        if (displayName) return displayName;

        const username = firstNonEmptyText(identity?.username);
        if (username) return username.startsWith('@') ? username : `@${username}`;

        const email = firstNonEmptyText(identity?.email);
        if (email) return email;

        const uid = firstNonEmptyText(fallbackId);
        return uid || '--';
    }

    React.useEffect(() => {
        let cancel = false;

        (async () => {
            if (!open || mode !== 'edit' || !ticket?.id) {
                return;
            }

            try {
                let data = ticket;
                const fetchedAt = ticketDetailMeta?.fetchedAt ?? null;
                const hasFreshDetail =
                    Boolean(fetchedAt) &&
                    Date.now() - Number(fetchedAt) < TICKET_DETAIL_TTL_MS;

                if (!hasFreshDetail) {
                    const action = await dispatch(fetchTicketDetail({ id: ticket.id }));
                    if (cancel) return;
                    if (fetchTicketDetail.fulfilled.match(action)) {
                        data =
                            buildTicketEditorTicket(
                                { ...(ticketProp ?? {}), ...action.payload },
                                {
                                    fallbackProjectId: project?.id ?? null,
                                    onDelete: ticketProp?.onDelete,
                                    onAttach: ticketProp?.onAttach,
                                },
                            ) ?? data;
                    }
                }

                if (cancel || !data) return;
                suppressNextAutosave();
                updateAutosaveBaseline({
                    projectId: cleanText(data?.projectId) || projectId || '',
                    clientSubscriptionId:
                        data?.clientSubscriptionId !== undefined
                            ? cleanText(data.clientSubscriptionId) || null
                            : clientSubscriptionId || null,
                    offeringId:
                        data?.offeringId !== undefined
                            ? cleanText(data.offeringId) || null
                            : offeringId || null,
                    offeringSubcategoryId:
                        data?.offeringSubcategoryId !== undefined
                            ? resolveOfferingSubcategoryId(
                                normalizeOfferingOptions(data?.offeringOptions),
                                cleanText(data?.offeringId) || '',
                                cleanText(data?.offeringSubcategoryId) || '',
                            ) || null
                            : resolveOfferingSubcategoryId(
                                normalizeOfferingOptions(data?.offeringOptions),
                                cleanText(data?.offeringId) || offeringId || '',
                                offeringSubcategoryId,
                            ) || null,
                    estimatedMinutes:
                        data?.estimatedMinutes !== undefined
                            ? toEstimatedHoursInput(
                                typeof data.estimatedMinutes === 'number'
                                    ? data.estimatedMinutes
                                    : Number(data.estimatedMinutes ?? NaN),
                            )
                                ? parseEstimatedMinutesInput(
                                    toEstimatedHoursInput(
                                        typeof data.estimatedMinutes === 'number'
                                            ? data.estimatedMinutes
                                            : Number(data.estimatedMinutes ?? NaN),
                                    ),
                                )
                                : null
                            : parseEstimatedMinutesInput(estimatedHours),
                    kbaseProcessId:
                        data?.kbaseProcessId !== undefined
                            ? cleanText(data.kbaseProcessId) || null
                            : selectedKbaseProcessId || null,
                    owningTeamNodeId:
                        cleanText(data?.currentTeamNodeId) ||
                        cleanText(data?.owningTeamNodeId) ||
                        cleanText(data?.teamId) ||
                        null,
                });

                if (data?.createdAt) setCreatedAtOverride(data.createdAt);
                if (data?.clientSubscriptionId !== undefined) {
                    setClientSubscriptionId(cleanText(data.clientSubscriptionId) || '');
                }
                if (data?.offeringId !== undefined) {
                    setOfferingId(cleanText(data.offeringId) || '');
                }
                if (data?.offeringSubcategoryId !== undefined || data?.offeringOptions !== undefined) {
                    setOfferingSubcategoryId(
                        resolveOfferingSubcategoryId(
                            normalizeOfferingOptions(data?.offeringOptions),
                            cleanText(data?.offeringId) || '',
                            cleanText(data?.offeringSubcategoryId) || '',
                        ),
                    );
                }
                if (data?.kbaseProcessId !== undefined) {
                    setSelectedKbaseProcessId(cleanText(data.kbaseProcessId) || '');
                }
                if (data?.estimatedMinutes !== undefined) {
                    setEstimatedHours(toEstimatedHoursInput(
                        typeof data.estimatedMinutes === 'number'
                            ? data.estimatedMinutes
                            : Number(data.estimatedMinutes ?? NaN),
                    ));
                }
                setSubscriptionOptions(normalizeSubscriptionOptions(data?.subscriptionOptions));
                if (typeof data?.subscriptionSelectionRequired === 'boolean') {
                    setSubscriptionSelectionRequired(data.subscriptionSelectionRequired);
                }
                setSubscriptionResolutionError(cleanText(data?.subscriptionResolutionError) || null);
                setOfferingOptions(normalizeOfferingOptions(data?.offeringOptions));
                if (typeof data?.offeringSelectionRequired === 'boolean') {
                    setOfferingSelectionRequired(data.offeringSelectionRequired);
                }
                setTeamOptions(normalizeTeamOptions(data?.teamOptions));
                setOwningTeamNodeId(
                    cleanText(data?.currentTeamNodeId) ||
                    cleanText(data?.owningTeamNodeId) ||
                    cleanText(data?.teamId) ||
                    '',
                );

                // ensure projectId is in local state if parent didn't supply it
                if (!projectId && data?.projectId) setProjectId(data.projectId);

                // prefer API-provided project name
                const pName =
                    (Array.isArray(data?.projects) && data.projects[0]?.name) ||
                    null;
                if (pName) setProjectName(pName);

                const responseClientId = firstNonEmptyText(data?.clientId, ticket?.clientId);
                setClientIdentity((prev) => ({
                    ...prev,
                    uid: firstNonEmptyText(responseClientId, prev.uid) || null,
                    businessName: firstNonEmptyText(
                        data?.clientBusinessName,
                        (data as any)?.client?.businessName,
                        (ticket as any)?.clientBusinessName,
                        prev.businessName,
                    ) || null,
                    businessLogoURL: firstNonEmptyText(
                        (data as any)?.clientBusinessLogoURL,
                        (data as any)?.client?.businessLogoURL,
                        (ticket as any)?.clientBusinessLogoURL,
                        prev.businessLogoURL,
                    ) || null,
                    firstName: firstNonEmptyText(
                        data?.clientFirstName,
                        (data as any)?.client?.firstName,
                        (ticket as any)?.clientFirstName,
                        prev.firstName,
                    ) || null,
                    lastName: firstNonEmptyText(
                        data?.clientLastName,
                        (data as any)?.client?.lastName,
                        (ticket as any)?.clientLastName,
                        prev.lastName,
                    ) || null,
                    displayName: firstNonEmptyText(
                        data?.clientDisplayName,
                        (data as any)?.client?.displayName,
                        (data as any)?.clientName,
                        (ticket as any)?.clientDisplayName,
                        prev.displayName,
                    ) || null,
                    email: firstNonEmptyText(
                        data?.clientEmail,
                        (data as any)?.client?.email,
                        (ticket as any)?.clientEmail,
                        prev.email,
                    ) || null,
                    photoURL: firstNonEmptyText(
                        (data as any)?.clientBusinessLogoURL,
                        data?.clientPhotoURL,
                        (data as any)?.client?.businessLogoURL,
                        (data as any)?.client?.photoURL,
                        (data as any)?.client?.photoUrl,
                        (ticket as any)?.clientBusinessLogoURL,
                        (ticket as any)?.clientPhotoURL,
                        prev.businessLogoURL,
                        prev.photoURL,
                    ) || null,
                }));

                if ((role === 'admin' || role === 'agent') && responseClientId) {
                    void dispatch(fetchTicketUserIdentity({ id: responseClientId }));
                }


                // The ticket creator controls delete permission.
                const creator =
                    data?.createdById ??
                    (data as any)?.createdBy ??
                    data?.clientId ??
                    (data as any)?.authorId ??
                    (data as any)?.userId ??
                    null;
                if (creator) setCreatedById(String(creator));



            } catch {
                // ignore; UI has graceful fallbacks
            }
        })();
        return () => {
            cancel = true;
        };
        // only when the specific ticket drawer opens/changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mode, ticket?.id]);

    React.useEffect(() => {
        let cancel = false;

        async function loadContractContext() {
            if (!open || readOnly) return;
            const clientId = cleanText(ticket?.clientId);
            const projectIdForContext = cleanText(projectId) || cleanText(project?.id) || cleanText(ticket?.projectId);
            const orgIdForContext = cleanText(ticket?.orgId) || cleanText(project?.orgId);
            if (!clientId && !projectIdForContext) return;

            try {
                let data = cachedTicketContext;
                const fetchedAt = ticketContextMeta?.fetchedAt ?? null;
                const hasFreshContext =
                    Boolean(fetchedAt) &&
                    Date.now() - Number(fetchedAt) < TICKET_CONTEXT_TTL_MS;

                if (!hasFreshContext) {
                    const action = await dispatch(fetchTicketCreateContext({
                        clientId,
                        projectId: projectIdForContext,
                        orgId: orgIdForContext,
                        clientSubscriptionId: clientSubscriptionId || null,
                    }));
                    if (cancel) return;
                    if (fetchTicketCreateContext.fulfilled.match(action)) {
                        data = action.payload.data;
                    } else {
                        const message = firstNonEmptyText(action.error?.message);
                        if (clientSubscriptionId && message === 'ticket_subscription_not_entitled') {
                            suppressNextAutosave();
                            updateAutosaveBaseline({
                                clientSubscriptionId: null,
                                offeringId: null,
                                offeringSubcategoryId: null,
                            });
                            setClientSubscriptionId('');
                            setOfferingId('');
                            setOfferingSubcategoryId('');
                            return;
                        }
                        throw new Error(message || 'ticket_context_unavailable');
                    }
                }

                if (cancel || !data) return;
                suppressNextAutosave();

                const nextSubscriptionOptions = normalizeSubscriptionOptions(data?.subscriptionOptions);
                const nextOfferingOptions = normalizeOfferingOptions(data?.offeringOptions);
                const nextTeamOptions = normalizeTeamOptions(data?.teamOptions);
                const nextDefaultSubscriptionId = cleanText(data?.defaultSubscriptionId) || '';
                const nextDefaultOfferingId = cleanText(data?.defaultOfferingId) || '';
                const nextDefaultTeamNodeId =
                    cleanText(data?.currentTeamNodeId) ||
                    cleanText(data?.defaultTeamNodeId) ||
                    '';
                const nextResolutionError = cleanText(data?.subscriptionResolutionError) || null;
                const nextSubscriptionIds = new Set(nextSubscriptionOptions.map((option) => option.id));
                const nextOfferingIds = new Set(nextOfferingOptions.map((option) => option.id));
                const nextTeamIds = new Set(nextTeamOptions.map((option) => option.id));

                setSubscriptionOptions(nextSubscriptionOptions);
                setSubscriptionSelectionRequired(
                    typeof data?.subscriptionSelectionRequired === 'boolean'
                        ? data.subscriptionSelectionRequired
                        : nextSubscriptionOptions.length > 1,
                );
                setSubscriptionResolutionError(nextResolutionError);
                setOfferingOptions(nextOfferingOptions);
                setOfferingSelectionRequired(
                    typeof data?.offeringSelectionRequired === 'boolean'
                        ? data.offeringSelectionRequired
                        : nextOfferingOptions.length > 1,
                );
                setTeamOptions(nextTeamOptions);

                const resolvedSubscriptionId =
                    cleanText(data?.subscription?.id) ||
                    (clientSubscriptionId && nextSubscriptionIds.has(clientSubscriptionId)
                        ? clientSubscriptionId
                        : '') ||
                    nextDefaultSubscriptionId;
                if (resolvedSubscriptionId !== clientSubscriptionId) {
                    setClientSubscriptionId(resolvedSubscriptionId);
                }

                const nextOfferingId =
                    offeringId && nextOfferingIds.has(offeringId)
                        ? offeringId
                        : nextDefaultOfferingId;
                if (nextOfferingId !== offeringId) {
                    setOfferingId(nextOfferingId);
                }
                const nextOfferingSubcategoryId = resolveOfferingSubcategoryId(
                    nextOfferingOptions,
                    nextOfferingId,
                    offeringSubcategoryId,
                );
                if (nextOfferingSubcategoryId !== offeringSubcategoryId) {
                    setOfferingSubcategoryId(nextOfferingSubcategoryId);
                }
                const resolvedTeamNodeId =
                    owningTeamNodeId && nextTeamIds.has(owningTeamNodeId)
                        ? owningTeamNodeId
                        : nextDefaultTeamNodeId;
                updateAutosaveBaseline({
                    clientSubscriptionId: resolvedSubscriptionId || null,
                    offeringId: nextOfferingId || null,
                    offeringSubcategoryId: nextOfferingSubcategoryId || null,
                    owningTeamNodeId: resolvedTeamNodeId || null,
                });
                if (resolvedTeamNodeId !== owningTeamNodeId) {
                    setOwningTeamNodeId(resolvedTeamNodeId);
                }
            } catch (error: any) {
                const message = firstNonEmptyText(error?.response?.data?.message, error?.message);
                if (!cancel && clientSubscriptionId && message === 'ticket_subscription_not_entitled') {
                    suppressNextAutosave();
                    updateAutosaveBaseline({
                        clientSubscriptionId: null,
                        offeringId: null,
                        offeringSubcategoryId: null,
                    });
                    setClientSubscriptionId('');
                    setOfferingId('');
                    setOfferingSubcategoryId('');
                    return;
                }
                if (!cancel) {
                    suppressNextAutosave();
                    setSubscriptionOptions([]);
                    setSubscriptionSelectionRequired(false);
                    setSubscriptionResolutionError(
                        mode === 'create' || mode === 'edit'
                            ? 'Unable to resolve the contracted service for this ticket.'
                            : null,
                    );
                    setOfferingOptions([]);
                    setOfferingSelectionRequired(false);
                    setTeamOptions([]);
                }
            } finally {
                // cache-first rendering; no local loading toggle on background refresh
            }
        }

        void loadContractContext();
        return () => {
            cancel = true;
        };
    }, [
        cachedTicketContext,
        clientSubscriptionId,
        dispatch,
        mode,
        offeringId,
        offeringSubcategoryId,
        open,
        project?.id,
        project?.orgId,
        projectId,
        readOnly,
        owningTeamNodeId,
        suppressNextAutosave,
        ticket?.clientId,
        ticketContextMeta?.fetchedAt,
        ticket?.orgId,
        ticket?.projectId,
        updateAutosaveBaseline,
    ]);


    // Fallback: seed creator id from the ticket prop (if present)
    React.useEffect(() => {
        if (!open) return;
        const fromProp =
            (ticket as any)?.createdById ??
            (ticket as any)?.createdBy ??
            (ticket as any)?.clientId ??
            (ticket as any)?.authorId ??
            null;
        if (fromProp) setCreatedById(String(fromProp));
    }, [open, ticket]);

    const ticketClientBusinessName = (ticket as any)?.clientBusinessName ?? null;
    const ticketClientBusinessLogoURL = (ticket as any)?.clientBusinessLogoURL ?? null;
    const ticketClientFirstName = (ticket as any)?.clientFirstName ?? null;
    const ticketClientLastName = (ticket as any)?.clientLastName ?? null;
    const ticketClientDisplayName = (ticket as any)?.clientDisplayName ?? null;
    const ticketClientEmail = (ticket as any)?.clientEmail ?? null;
    const ticketClientPhotoURL = (ticket as any)?.clientPhotoURL ?? null;

    React.useEffect(() => {
        if (!open) return;
        setClientIdentity((prev) => ({
            ...prev,
            uid: firstNonEmptyText(ticket?.clientId, prev.uid) || null,
            businessName: firstNonEmptyText(ticketClientBusinessName, prev.businessName) || null,
            businessLogoURL: firstNonEmptyText(ticketClientBusinessLogoURL, prev.businessLogoURL) || null,
            firstName: firstNonEmptyText(ticketClientFirstName, prev.firstName) || null,
            lastName: firstNonEmptyText(ticketClientLastName, prev.lastName) || null,
            displayName: firstNonEmptyText(ticketClientDisplayName, prev.displayName) || null,
            email: firstNonEmptyText(ticketClientEmail, prev.email) || null,
            photoURL: firstNonEmptyText(
                ticketClientBusinessLogoURL,
                ticketClientPhotoURL,
                prev.businessLogoURL,
                prev.photoURL,
            ) || null,
        }));
    }, [
        open,
        ticket?.id,
        ticket?.clientId,
        ticketClientBusinessName,
        ticketClientBusinessLogoURL,
        ticketClientFirstName,
        ticketClientLastName,
        ticketClientDisplayName,
        ticketClientEmail,
        ticketClientPhotoURL,
    ]);



    // Close popovers when clicking outside or pressing Escape
    React.useEffect(() => {
        const onDown = (e: MouseEvent | PointerEvent) => {
            const target = e.target as HTMLElement | null;

            // close assignee popovers if click is outside that cell
            if (assignCellRef.current && !assignCellRef.current.contains(e.target as Node)) {
                setShowOrgAssigneeMenu(false);
            }
            if (agentAssignCellRef.current && !agentAssignCellRef.current.contains(e.target as Node)) {
                setShowAssignedAgentMenu(false);
            }

            // close actions menu & confirm if click is outside them
            const insideRight = rightRef.current?.contains(e.target as Node);
            const insideMenu = menuRef.current?.contains(e.target as Node);
            const insideConfirm = confirmRef.current?.contains(e.target as Node);
            if (!insideRight && !insideMenu && !insideConfirm) {
                setShowMenu(false);
                setConfirmDelete(false);
            }

            if (!target?.closest?.('[data-subtask-assignee-root]')) {
                setOpenSubtaskAssigneeId(null);
            }
        };

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setShowOrgAssigneeMenu(false);
                setShowAssignedAgentMenu(false);
                setShowMenu(false);
                setConfirmDelete(false);
                setOpenSubtaskAssigneeId(null);
            }
        };
        document.addEventListener('pointerdown', onDown);
        document.addEventListener('mousedown', onDown); // fallback for older browsers
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('pointerdown', onDown);
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, []);


    const assigneeAgents = React.useMemo<AgentOption[]>(() => {
        const byId = new Map<string, AgentOption>();
        for (const candidate of [...agents, ...fetchedAssignees]) {
            const id = String(candidate?.id ?? '').trim();
            if (!id) continue;
            const name = String(candidate?.name ?? '').trim() || id;
            byId.set(id, {
                id,
                name,
                email: candidate?.email,
                title: candidate?.title ?? undefined,
                role: candidate?.role ?? undefined,
                photoURL: candidate?.photoURL ?? null,
            });
        }
        return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [agents, fetchedAssignees]);

    React.useEffect(() => {
        if (!open) return;
        if (agents.length > 0) return;
        const assigneeOrgId =
            String(ticket?.orgId ?? (project as any)?.orgId ?? activeOrgId ?? '').trim();
        void dispatch(fetchTicketOrgAssignees({ orgId: assigneeOrgId || null }));
    }, [activeOrgId, agents.length, dispatch, open, project, ticket?.orgId]);

    React.useEffect(() => {
        if (!open || !canEditInternalAssignee) return;

        const teamNodeId = String(owningTeamNodeId ?? '').trim();
        if (!teamNodeId) return;
        void dispatch(fetchTicketInternalTeamAssignees({ teamNodeId }));
    }, [canEditInternalAssignee, dispatch, open, owningTeamNodeId]);

    React.useEffect(() => {
        if (open) return;
        setShowOrgAssigneeMenu(false);
        setShowAssignedAgentMenu(false);
        setOpenSubtaskAssigneeId(null);
        setDraggingSubtaskId(null);
        setSubtaskDropTargetId(null);
    }, [open]);

    const assigneeQuery = '';

    const filteredAgents = React.useMemo(() => {
        const q = assigneeQuery.trim().toLowerCase();
        const list = assigneeAgents;
        if (!q) return list;
        return list.filter(a => a.name.toLowerCase().includes(q));
    }, [assigneeAgents, assigneeQuery]);

    const filteredInternalTeamAssignees = React.useMemo(() => {
        const q = assigneeQuery.trim().toLowerCase();
        const list = internalTeamAssignees;
        if (!q) return list;
        return list.filter((agent) => agent.name.toLowerCase().includes(q));
    }, [assigneeQuery, internalTeamAssignees]);

    const selectedAssigneeOption = React.useMemo(
        () => internalTeamAssignees.find((agent) => agent.id === assigned) ?? null,
        [assigned, internalTeamAssignees],
    );
    const selectedOrgMemberOption = React.useMemo(
        () => assigneeAgents.find((agent) => agent.id === assignedOrgMember) ?? null,
        [assigneeAgents, assignedOrgMember],
    );
    const assignedAgentIdentity = React.useMemo(() => {
        if (!assigned) return null;
        const lookup = userMap[assigned];
        return {
            id: assigned,
            name:
                selectedAssigneeOption?.name
                || nameFromIdentity(
                    {
                        firstName: ticket?.assignedAgentFirstName,
                        lastName: ticket?.assignedAgentLastName,
                        displayName: ticket?.assignedAgentDisplayName,
                        email: ticket?.assignedAgentEmail,
                        name: firstNonEmptyText(lookup?.name),
                    },
                    assigned,
                ),
            email:
                firstNonEmptyText(
                    selectedAssigneeOption?.email,
                    ticket?.assignedAgentEmail,
                    lookup?.email,
                ) || null,
                photoURL:
                firstNonEmptyText(
                    selectedAssigneeOption?.photoURL,
                    ticket?.assignedAgentPhotoURL,
                    lookup?.photoURL,
                ) || null,
        };
    }, [
        assigned,
        selectedAssigneeOption,
        ticket?.assignedAgentDisplayName,
        ticket?.assignedAgentEmail,
        ticket?.assignedAgentFirstName,
        ticket?.assignedAgentLastName,
        ticket?.assignedAgentPhotoURL,
        userMap,
    ]);
    const assignedOrgMemberIdentity = React.useMemo(() => {
        const sourceId = assignedOrgMember || ticket?.assignedOrgMemberId || null;
        if (!sourceId) return null;
        const option = selectedOrgMemberOption;
        const name =
            option?.name
            || nameFromIdentity(
                {
                    firstName: ticket?.assignedOrgMemberFirstName,
                    lastName: ticket?.assignedOrgMemberLastName,
                    displayName: ticket?.assignedOrgMemberDisplayName,
                    email: ticket?.assignedOrgMemberEmail,
                },
                sourceId,
            );
        return {
            id: sourceId,
            name,
            email: firstNonEmptyText(option?.email, ticket?.assignedOrgMemberEmail) || null,
            photoURL:
                firstNonEmptyText(option?.photoURL, ticket?.assignedOrgMemberPhotoURL) || null,
        };
    }, [
        assignedOrgMember,
        selectedOrgMemberOption,
        ticket?.assignedOrgMemberDisplayName,
        ticket?.assignedOrgMemberEmail,
        ticket?.assignedOrgMemberFirstName,
        ticket?.assignedOrgMemberId,
        ticket?.assignedOrgMemberLastName,
        ticket?.assignedOrgMemberPhotoURL,
    ]);
    const assigneeById = React.useMemo(() => {
        const map = new Map<string, AgentOption>();
        assigneeAgents.forEach((agent) => map.set(agent.id, agent));
        return map;
    }, [assigneeAgents]);


    // Resolve a project label from any of the inputs we may have.
    // Order: explicit prop -> projects[] passed in -> ticket.projects[] -> fallback
    const projectLabel = React.useMemo(() => {
        if (projectName) return projectName;

        const id =
            projectId ||
            ticket?.projectId ||
            project?.id ||
            '';

        if (!id) return '--';

        const fromTop = (projects || []).find(p => p.id === id)?.name;
        if (fromTop) return fromTop;

        const fromTicket = (ticket?.projects || []).find(p => p.id === id)?.name;
        if (fromTicket) return fromTicket;

        // last-resort: show the id so the cell is not blank
        return id;
    }, [projectName, projectId, ticket?.projectId, project?.id, projects, ticket?.projects]);

    const milestoneOptions = React.useMemo(() => {
        const activeProjectId = projectId || ticket?.projectId || project?.id || '';
        if (!activeProjectId) return [];
        const fromTop = (projects || []).find((entry) => entry.id === activeProjectId)?.milestones;
        if (Array.isArray(fromTop)) return fromTop;
        if (project?.id === activeProjectId && Array.isArray(project?.milestones)) {
            return project.milestones;
        }
        return [];
    }, [project?.id, project?.milestones, projectId, projects, ticket?.projectId]);

    const milestoneLabel = React.useMemo(() => {
        const selected = milestoneOptions.find((entry) => entry.id === selectedMilestoneId);
        if (selected?.title) return selected.title;
        const fallback = cleanText(ticket?.milestoneTitle);
        return fallback || 'Unassigned';
    }, [milestoneOptions, selectedMilestoneId, ticket?.milestoneTitle]);



    // Handlers
    function setAssignedAgent(nextAssigned: string | null) {
        suppressNextAutosave();
        setAssigned(nextAssigned);
        setShowAssignedAgentMenu(false);
        void commitImmediatePatch({ assignedAgentId: nextAssigned });
    }
    function setAssignedOrganizationMember(nextAssigned: string | null) {
        suppressNextAutosave();
        setAssignedOrgMember(nextAssigned);
        setShowOrgAssigneeMenu(false);
        void commitImmediatePatch({ assignedOrgMemberId: nextAssigned });
    }


    const creatorForCheck =
        createdById ??
        (ticket as any)?.createdById ??
        (ticket as any)?.createdBy ??
        (ticket as any)?.clientId ??     // fallback for older ticket shapes
        (ticket as any)?.authorId ??
        null;

    const canDelete = Boolean(
        mode === 'edit' && ticket?.id && user?.uid && user.uid === creatorForCheck
    );


    async function performDelete() {
        if (!ticket?.id || !canDelete) return;
        try {
            if (ticket.onDelete) {
                await ticket.onDelete(ticket.id);
            } else {
                await api.delete(`/tickets/${ticket.id}`);
            }
        } finally {
            setConfirmDelete(false);
            setShowMenu(false);
            onClose(); // close the drawer; parent can refetch
        }
    }


    // --- replace your draftKey with this (props-based; not state-based) ---
    const draftKey = React.useMemo(() => {
        if (mode === 'edit' && ticket?.id) return `ticket:edit:${ticket.id}`;
        const pid = project?.id || 'none';
        return `ticket:draft:project:${pid}`;
    }, [mode, ticket?.id, project?.id]);

    // --- replace your "restore draft / seed from props" effect with this ---
    React.useEffect(() => {
        if (!open) return;

        // Treat each open/hydration as a fresh first-load for autosave gating.
        isFirstLoadRef.current = true;
        lastHandledAutosaveSuppressionRef.current = autosaveSuppressionVersionRef.current;
        setSaveState('idle');
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }

        // 1) Seed from props
        const base = (mode === 'edit' && ticket)
            ? {
                title: ticket.title ?? '',
                projectId: ticket.projectId || project?.id || '',
                milestoneId: ticket.milestoneId ?? '',
                priority: (ticket.priority ?? 'medium') as UiPriority,
                startDate: toDateInputValueInTimezone(ticket.plannedStartAt, ticket.timezone),
                dueDate: toDateInputValueInTimezone(ticket.plannedEndAt, ticket.timezone),
                assigned: ticket.assignedAgentId ?? null,
                assignedOrgMember: ticket.assignedOrgMemberId ?? null,
                owningTeamNodeId:
                    ticket.owningTeamNodeId ??
                    ticket.currentTeamNodeId ??
                    ticket.teamId ??
                    '',
                clientSubscriptionId: ticket.clientSubscriptionId ?? '',
                offeringId: ticket.offeringId ?? '',
                offeringSubcategoryId:
                    resolveOfferingSubcategoryId(
                        Array.isArray(ticket.offeringOptions) ? ticket.offeringOptions : [],
                        ticket.offeringId ?? '',
                        ticket.offeringSubcategoryId ?? '',
                    ) || '',
                kbaseProcessId: ticket.kbaseProcessId ?? '',
                estimatedHours: toEstimatedHoursInput(ticket.estimatedMinutes),
                status: (ticket.status ?? 'open') as TicketStatus,
                desc: ticket.description ?? '',
                blockedByTicketIds: normalizeRelationshipIds(ticket.blockedBy?.map((entry) => entry.id)),
                handoffToTicketIds: normalizeRelationshipIds(ticket.handoffTo?.map((entry) => entry.id)),
                subtasks: normalizeSubtasks(ticket.subtasks ?? []),
                participants: Array.isArray(ticket.participants) ? ticket.participants : [],
            }
            : {
                title: '',
                projectId: project?.id || '',
                milestoneId: '',
                priority: 'medium' as UiPriority,
                startDate: '',
                dueDate: '',
                assigned: null as string | null,
                assignedOrgMember: null as string | null,
                owningTeamNodeId: '',
                clientSubscriptionId: '',
                offeringId: '',
                offeringSubcategoryId: '',
                kbaseProcessId: '',
                estimatedHours: '',
                status: 'open' as TicketStatus,
                desc: '',
                blockedByTicketIds: [] as string[],
                handoffToTicketIds: [] as string[],
                subtasks: [] as Subtask[],
                participants: [] as string[],
            };

        setTitle(base.title);
        setProjectId(base.projectId);
        setSelectedMilestoneId(base.milestoneId);
        setPriority(base.priority);
        setStartDate(base.startDate);
        setDueDate(base.dueDate);
        setAssigned(base.assigned);
        setAssignedOrgMember(base.assignedOrgMember);
        setOwningTeamNodeId(base.owningTeamNodeId);
        setClientSubscriptionId(base.clientSubscriptionId);
        setOfferingId(base.offeringId);
        setOfferingSubcategoryId(base.offeringSubcategoryId);
        setSelectedKbaseProcessId(base.kbaseProcessId);
        setEstimatedHours(base.estimatedHours);
        setStatus(base.status);
        setDesc(base.desc);
        setBlockedByTicketIds(base.blockedByTicketIds);
        setHandoffToTicketIds(base.handoffToTicketIds);
        setSubtasks(base.subtasks);
        setParticipants(base.participants);
        autosaveBaselineRef.current = {
            title: base.title,
            description: base.desc,
            projectId: base.projectId,
            milestoneId: base.milestoneId || null,
            priority: base.priority,
            startDate: base.startDate,
            dueDate: base.dueDate,
            assignedAgentId: base.assigned,
            assignedOrgMemberId: base.assignedOrgMember,
            owningTeamNodeId: base.owningTeamNodeId || null,
            clientSubscriptionId: base.clientSubscriptionId || null,
            offeringId: base.offeringId || null,
            offeringSubcategoryId: base.offeringSubcategoryId || null,
            kbaseProcessId: base.kbaseProcessId || null,
            estimatedMinutes: parseEstimatedMinutesInput(base.estimatedHours),
            blockedByTicketIds: base.blockedByTicketIds,
            handoffToTicketIds: base.handoffToTicketIds,
            subtasks: base.subtasks,
            participants: base.participants,
        };

        // 2) Overlay drafts only for create mode.
        // Edit mode must always reflect server values so timeline <-> ticket stays in sync.
        if (mode === 'create') {
            try {
                const raw = localStorage.getItem(draftKey);
                if (raw) {
                    const d = JSON.parse(raw);
                    if (d.title !== undefined) setTitle(d.title);
                    if (d.projectId !== undefined) setProjectId(d.projectId);
                    if (d.milestoneId !== undefined) setSelectedMilestoneId(String(d.milestoneId ?? ''));
                    if (d.priority !== undefined) setPriority(d.priority);
                    if (d.startDate !== undefined) setStartDate(d.startDate);
                    if (d.dueDate !== undefined) setDueDate(d.dueDate);
                    if (d.assigned !== undefined) setAssigned(d.assigned);
                    if (d.assignedOrgMember !== undefined) setAssignedOrgMember(d.assignedOrgMember);
                    if (d.owningTeamNodeId !== undefined) setOwningTeamNodeId(d.owningTeamNodeId);
                    if (d.clientSubscriptionId !== undefined) {
                        setClientSubscriptionId(d.clientSubscriptionId);
                    }
                    if (d.offeringId !== undefined) setOfferingId(d.offeringId);
                    if (d.offeringSubcategoryId !== undefined) {
                        setOfferingSubcategoryId(String(d.offeringSubcategoryId ?? ''));
                    }
                    if (d.estimatedHours !== undefined) setEstimatedHours(String(d.estimatedHours ?? ''));
                    if (d.desc !== undefined) setDesc(d.desc);
                    if (d.blockedByTicketIds !== undefined) {
                        setBlockedByTicketIds(normalizeRelationshipIds(d.blockedByTicketIds));
                    }
                    if (d.handoffToTicketIds !== undefined) {
                        setHandoffToTicketIds(normalizeRelationshipIds(d.handoffToTicketIds));
                    }
                    if (d.subtasks !== undefined) setSubtasks(normalizeSubtasks(d.subtasks));
                    if (d.participants !== undefined) {
                        setParticipants(Array.isArray(d.participants) ? d.participants : []);
                    }
                }
            } catch {/* ignore JSON issues */ }
        } else {
            localStorage.removeItem(draftKey);
        }
    }, [draftKey, mode, open, project?.id, ticket]);

    // Keep timeline dates in sync when the same ticket is updated externally (timeline drag/resize).
    React.useEffect(() => {
        if (!open || mode !== 'edit' || !ticket?.id) return;
        suppressNextAutosave();
        const nextStartDate = toDateInputValueInTimezone(ticket.plannedStartAt, ticket.timezone);
        const nextDueDate = toDateInputValueInTimezone(ticket.plannedEndAt, ticket.timezone);
        updateAutosaveBaseline({
            startDate: nextStartDate,
            dueDate: nextDueDate,
        });
        setStartDate(prev => (prev === nextStartDate ? prev : nextStartDate));
        setDueDate(prev => (prev === nextDueDate ? prev : nextDueDate));
    }, [mode, open, suppressNextAutosave, ticket, updateAutosaveBaseline]);


    // autosave to localStorage
    React.useEffect(() => {
        if (!open) return;
        const payload = {
            title,
            projectId,
            milestoneId: selectedMilestoneId,
            priority,
            startDate,
            dueDate,
            assigned,
            assignedOrgMember,
            owningTeamNodeId,
            clientSubscriptionId,
            offeringId,
            offeringSubcategoryId,
            estimatedHours,
            desc,
            blockedByTicketIds,
            handoffToTicketIds,
            subtasks,
            participants,
        };
        localStorage.setItem(draftKey, JSON.stringify(payload));
    }, [
        open,
        draftKey,
        title,
        projectId,
        selectedMilestoneId,
        priority,
        startDate,
        dueDate,
        assigned,
        assignedOrgMember,
        owningTeamNodeId,
        clientSubscriptionId,
        offeringId,
        offeringSubcategoryId,
        estimatedHours,
        desc,
        blockedByTicketIds,
        handoffToTicketIds,
        subtasks,
        participants,
    ]);





    // NEW: field-change autosave (edit mode only; skip first paint after open/seed)
    React.useEffect(() => {
        if (!open || mode !== 'edit' || readOnly || !ticket?.id) return;
        if (ticketHydrating || contractContextLoading) return;

        if (autosaveSuppressionVersion !== lastHandledAutosaveSuppressionRef.current) {
            lastHandledAutosaveSuppressionRef.current = autosaveSuppressionVersion;
            return;
        }

        // Skip the first run after seeding state from props/draft
        if (isFirstLoadRef.current) {
            isFirstLoadRef.current = false;
            return;
        }

        if (subscriptionResolutionError) return;
        if (subscriptionSelectionRequired && !clientSubscriptionId) return;
        if (offeringSelectionRequired && !offeringId) return;

        const baseline = autosaveBaselineRef.current ?? buildAutosaveSnapshot();
        const nextSnapshot = buildAutosaveSnapshot();
        const patch: TicketUpdatePayload = {};

        if (nextSnapshot.title !== baseline.title) patch.title = nextSnapshot.title;
        if (nextSnapshot.estimatedMinutes !== baseline.estimatedMinutes) {
            patch.estimatedMinutes = nextSnapshot.estimatedMinutes;
        }
        if (!areSubtasksEqual(nextSnapshot.subtasks, baseline.subtasks)) {
            patch.subtasks = nextSnapshot.subtasks;
        }

        if (!Object.keys(patch).length) return;

        queueAutosave(patch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        assigned,
        open,
        mode,
        readOnly,
        ticket?.id,
        title,
        desc,
        projectId,
        priority,
        startDate,
        dueDate,
        clientSubscriptionId,
        offeringId,
        offeringSubcategoryId,
        selectedKbaseProcessId,
        estimatedHours,
        assignedOrgMember,
        owningTeamNodeId,
        subtasks,
        participants,
        subscriptionResolutionError,
        subscriptionSelectionRequired,
        offeringSelectionRequired,
        canEditScheduleWindow,
        ticketHydrating,
        contractContextLoading,
        autosaveSuppressionVersion,
        buildAutosaveSnapshot,
    ]);



    // load comments in edit mode
    React.useEffect(() => {
        let cancel = false;
        async function load() {
            if (!open || mode !== 'edit' || !ticket?.id) return;
            try {
                let nextComments = cachedComments;
                const fetchedAt = cachedCommentsMeta?.fetchedAt ?? null;
                const hasFreshComments =
                    Boolean(fetchedAt) &&
                    Date.now() - Number(fetchedAt) < TICKET_COMMENTS_TTL_MS;

                if (!hasFreshComments) {
                    const action = await dispatch(fetchTicketComments({ id: ticket.id }));
                    if (cancel) return;
                    if (fetchTicketComments.fulfilled.match(action)) {
                        nextComments = action.payload.comments || [];
                    }
                }

                if (!cancel) setComments(nextComments || []);
            } catch (e) { /* ignore */ }
        }
        load();
        return () => { cancel = true; };
    }, [cachedComments, cachedCommentsMeta?.fetchedAt, dispatch, mode, open, ticket?.id]);

    // Keep assigned in sync with the latest ticket prop (initial hydration & live updates)
    React.useEffect(() => {
        if (!open) return;
        const next = ticket?.assignedAgentId ?? null;
        setAssigned(prev => (prev === next ? prev : next));
    }, [open, ticket?.assignedAgentId]);

    React.useEffect(() => {
        if (!open) return;
        const next = ticket?.assignedOrgMemberId ?? null;
        suppressNextAutosave();
        updateAutosaveBaseline({ assignedOrgMemberId: next });
        setAssignedOrgMember(prev => (prev === next ? prev : next));
    }, [open, suppressNextAutosave, ticket?.assignedOrgMemberId, updateAutosaveBaseline]);

    React.useEffect(() => {
        if (!open) return;
        const next =
            ticket?.owningTeamNodeId ??
            ticket?.currentTeamNodeId ??
            ticket?.teamId ??
            '';
        suppressNextAutosave();
        updateAutosaveBaseline({ owningTeamNodeId: next || null });
        setOwningTeamNodeId(prev => (prev === next ? prev : next));
    }, [open, suppressNextAutosave, ticket?.owningTeamNodeId, ticket?.currentTeamNodeId, ticket?.teamId, updateAutosaveBaseline]);

    React.useEffect(() => {
        if (open) return;
        setSaveState('idle');
    }, [open]);

    React.useEffect(() => {
        if (!open || mode !== 'edit') return;
        const nextClientSubscriptionId = ticket?.clientSubscriptionId ?? '';
        suppressNextAutosave();
        updateAutosaveBaseline({
            clientSubscriptionId: nextClientSubscriptionId || null,
            offeringId: (ticket?.offeringId ?? '') || null,
            offeringSubcategoryId:
                resolveOfferingSubcategoryId(
                    Array.isArray(ticket?.offeringOptions) ? ticket.offeringOptions : [],
                    ticket?.offeringId ?? '',
                    ticket?.offeringSubcategoryId ?? '',
                ) || null,
            kbaseProcessId: (ticket?.kbaseProcessId ?? '') || null,
            estimatedMinutes: parseEstimatedMinutesInput(toEstimatedHoursInput(ticket?.estimatedMinutes)),
        });
        setClientSubscriptionId(prev => (prev === nextClientSubscriptionId ? prev : nextClientSubscriptionId));
        const nextOfferingId = ticket?.offeringId ?? '';
        setOfferingId(prev => (prev === nextOfferingId ? prev : nextOfferingId));
        const nextOfferingSubcategoryId = resolveOfferingSubcategoryId(
            Array.isArray(ticket?.offeringOptions) ? ticket.offeringOptions : [],
            nextOfferingId,
            ticket?.offeringSubcategoryId ?? '',
        );
        setOfferingSubcategoryId(prev => (prev === nextOfferingSubcategoryId ? prev : nextOfferingSubcategoryId));
        const nextKBaseProcessId = ticket?.kbaseProcessId ?? '';
        setSelectedKbaseProcessId(prev => (prev === nextKBaseProcessId ? prev : nextKBaseProcessId));
        const nextEstimatedHours = toEstimatedHoursInput(ticket?.estimatedMinutes);
        setEstimatedHours(prev => (prev === nextEstimatedHours ? prev : nextEstimatedHours));
        setSubscriptionSelectionRequired(Boolean(ticket?.subscriptionSelectionRequired));
        setSubscriptionResolutionError(cleanText(ticket?.subscriptionResolutionError) || null);
        if (Array.isArray(ticket?.subscriptionOptions)) {
            setSubscriptionOptions(ticket.subscriptionOptions);
        }
        setOfferingSelectionRequired(Boolean(ticket?.offeringSelectionRequired));
        if (Array.isArray(ticket?.offeringOptions)) {
            setOfferingOptions(ticket.offeringOptions);
        }
        if (Array.isArray(ticket?.teamOptions)) {
            setTeamOptions(ticket.teamOptions);
        }
    }, [
        mode,
        open,
        suppressNextAutosave,
        ticket?.clientSubscriptionId,
        ticket?.estimatedMinutes,
        ticket?.kbaseProcessId,
        ticket?.offeringId,
        ticket?.offeringSubcategoryId,
        ticket?.offeringOptions,
        ticket?.offeringSelectionRequired,
        ticket?.subscriptionOptions,
        ticket?.subscriptionResolutionError,
        ticket?.subscriptionSelectionRequired,
        ticket?.teamOptions,
        updateAutosaveBaseline,
    ]);

    React.useEffect(() => {
        if (offeringId && formError && /request category|service lane/i.test(formError)) {
            setFormError(null);
        }
    }, [offeringId, formError]);

    React.useEffect(() => {
        if (clientSubscriptionId && formError && /service( contract| \/ tier| tier)/i.test(formError)) {
            setFormError(null);
        }
    }, [clientSubscriptionId, formError]);


    // Pull user labels for assignees, comment authors, participants
    React.useEffect(() => {
        const ids = new Set<string>();
        if (assigned) ids.add(assigned);
        if (assignedOrgMember) ids.add(assignedOrgMember);
        participants.forEach(p => ids.add(p));
        comments.forEach(c => {
            if (c.authorId) ids.add(c.authorId);
        });
        if (ids.size) fetchUsersById(Array.from(ids));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assigned, assignedOrgMember, participants, comments]);



    // Esc to close
    React.useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    // subtask helpers
    const addSubtask = () =>
        setSubtasks((current) => [
            ...current,
            {
                id: Math.random().toString(36).slice(2, 10),
                text: '',
                done: false,
                dueDate: null,
                assigneeId: null,
            },
        ]);
    const toggleSubtask = (id: string) =>
        setSubtasks((current) =>
            current.map((entry) =>
                entry.id === id ? { ...entry, done: !entry.done } : entry,
            ),
        );
    const editSubtask = (id: string, text: string) =>
        setSubtasks((current) =>
            current.map((entry) => (entry.id === id ? { ...entry, text } : entry)),
        );
    const setSubtaskDueDate = (id: string, nextDate: string) =>
        setSubtasks((current) =>
            current.map((entry) =>
                entry.id === id
                    ? { ...entry, dueDate: normalizeSubtaskDate(nextDate) }
                    : entry,
            ),
        );
    const setSubtaskAssignee = (id: string, nextAssigneeId: string | null) =>
        setSubtasks((current) =>
            current.map((entry) =>
                entry.id === id
                    ? { ...entry, assigneeId: nextAssigneeId }
                    : entry,
            ),
        );
    const removeSubtask = (id: string) => {
        setSubtasks((current) => current.filter((entry) => entry.id !== id));
        if (openSubtaskAssigneeId === id) setOpenSubtaskAssigneeId(null);
        delete subtaskDateInputRefs.current[id];
    };

    function reorderSubtasks(sourceId: string, targetId: string) {
        if (!sourceId || !targetId || sourceId === targetId) return;
        setSubtasks((current) => {
            const sourceIndex = current.findIndex((entry) => entry.id === sourceId);
            const targetIndex = current.findIndex((entry) => entry.id === targetId);
            if (sourceIndex < 0 || targetIndex < 0) return current;

            const next = [...current];
            const [moved] = next.splice(sourceIndex, 1);
            next.splice(targetIndex, 0, moved);
            return next;
        });
    }

    function onSubtaskDragStart(event: React.DragEvent<HTMLElement>, id: string) {
        if (readOnly) return;
        setDraggingSubtaskId(id);
        setSubtaskDropTargetId(id);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', id);
    }

    function onSubtaskDragOver(event: React.DragEvent<HTMLElement>, id: string) {
        if (readOnly || !draggingSubtaskId) return;
        event.preventDefault();
        if (draggingSubtaskId !== id) {
            setSubtaskDropTargetId(id);
        }
        event.dataTransfer.dropEffect = 'move';
    }

    function onSubtaskDrop(event: React.DragEvent<HTMLElement>, id: string) {
        if (readOnly) return;
        event.preventDefault();
        const sourceId = event.dataTransfer.getData('text/plain') || draggingSubtaskId;
        if (sourceId) {
            reorderSubtasks(sourceId, id);
        }
        setDraggingSubtaskId(null);
        setSubtaskDropTargetId(null);
    }

    function onSubtaskDragEnd() {
        setDraggingSubtaskId(null);
        setSubtaskDropTargetId(null);
    }

    function openSubtaskDatePicker(id: string) {
        if (readOnly) return;
        const input = subtaskDateInputRefs.current[id];
        if (!input) return;
        const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
        if (typeof pickerInput.showPicker === 'function') {
            pickerInput.showPicker();
            return;
        }
        input.focus();
        input.click();
    }

    // Debounced autosave to backend (edit mode only). We pass UI values; the page's onUpdate
    // will map priority to API units (P0..P3) and call PATCH.
    const buildTicketCacheChanges = React.useCallback(
        (patch: TicketUpdatePayload): Partial<StoreTicket> => {
            const changes: Partial<StoreTicket> = {};
            if (patch.title !== undefined) changes.title = patch.title;
            if (patch.description !== undefined) changes.description = patch.description;
            if (patch.projectId !== undefined) changes.projectId = patch.projectId;
            if (patch.milestoneId !== undefined) {
                changes.milestoneId = patch.milestoneId;
                const milestoneTitle =
                    patch.milestoneId
                        ? milestoneOptions.find((entry) => entry.id === patch.milestoneId)?.title ?? null
                        : null;
                changes.milestoneTitle = milestoneTitle;
            }
            if (patch.priority !== undefined) changes.priority = mapUiToApiPriority(patch.priority);
            if (patch.assignedAgentId !== undefined) changes.assignedAgentId = patch.assignedAgentId;
            if (patch.assignedOrgMemberId !== undefined) {
                changes.assignedOrgMemberId = patch.assignedOrgMemberId;
            }
            if (patch.owningTeamNodeId !== undefined) {
                changes.owningTeamNodeId = patch.owningTeamNodeId;
                changes.currentTeamNodeId = patch.owningTeamNodeId;
            }
            if (patch.clientSubscriptionId !== undefined) {
                changes.clientSubscriptionId = patch.clientSubscriptionId;
            }
            if (patch.offeringId !== undefined) changes.offeringId = patch.offeringId;
            if (patch.offeringSubcategoryId !== undefined) {
                changes.offeringSubcategoryId = patch.offeringSubcategoryId;
            }
            if (patch.kbaseProcessId !== undefined) {
                changes.kbaseProcessId = patch.kbaseProcessId;
            }
            if (patch.estimatedMinutes !== undefined) {
                changes.estimatedMinutes = patch.estimatedMinutes;
            }
            if (patch.blockedByTicketIds !== undefined) {
                changes.blockedBy = patch.blockedByTicketIds
                    .map((id) => relationshipOptionById.get(id))
                    .filter((entry): entry is TicketRelationshipSummary => Boolean(entry));
            }
            if (patch.handoffToTicketIds !== undefined) {
                changes.handoffTo = patch.handoffToTicketIds
                    .map((id) => relationshipOptionById.get(id))
                    .filter((entry): entry is TicketRelationshipSummary => Boolean(entry));
            }
            if (patch.subtasks !== undefined) {
                changes.subtasks = patch.subtasks.map((entry) => ({
                    id: entry.id,
                    text: entry.text,
                    done: entry.done,
                    dueDate: entry.dueDate ?? null,
                    assigneeId: entry.assigneeId ?? null,
                }));
            }
            if (patch.participants !== undefined) {
                changes.secondaryAgentIds = patch.participants;
                (changes as any).participants = patch.participants;
            }
            if (patch.startDate !== undefined) {
                changes.plannedStartAt = toPlannedBoundaryIsoFromLocalDate(
                    patch.startDate,
                    'start',
                    viewerTimezone,
                );
                changes.timezone = viewerTimezone;
            }
            if (patch.dueDate !== undefined) {
                changes.plannedEndAt = toPlannedBoundaryIsoFromLocalDate(
                    patch.dueDate,
                    'end',
                    viewerTimezone,
                );
                changes.timezone = viewerTimezone;
            }
            return changes;
        },
        [milestoneOptions, relationshipOptionById, viewerTimezone],
    );
    const syncTicketCache = React.useCallback((ticketId: string, patch: TicketUpdatePayload) => {
        dispatch(ticketPatched({ id: ticketId, changes: buildTicketCacheChanges(patch) }));
    }, [buildTicketCacheChanges, dispatch]);
    const commitImmediatePatch = React.useCallback(
        async (patch: TicketUpdatePayload) => {
            if (readOnly || mode !== 'edit' || !ticket?.id) return;
            setSaveState('saving');
            try {
                await onUpdate(ticket.id, patch);
                syncTicketCache(ticket.id, patch);
                updateAutosaveBaseline(patch);
                setSaveState('saved');
                window.setTimeout(() => setSaveState('idle'), 1200);
            } catch {
                setSaveState('error');
            }
        },
        [mode, onUpdate, readOnly, syncTicketCache, ticket?.id, updateAutosaveBaseline],
    );

    function queueAutosave(patch: TicketUpdatePayload) {
        if (readOnly || mode !== 'edit' || !ticket?.id) return; // skip when read-only
        setSaveState('saving');
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                await onUpdate(ticket!.id, patch);
                syncTicketCache(ticket!.id, patch);
                updateAutosaveBaseline(patch);
                setSaveState('saved');
                // Fade back to idle after a moment
                setTimeout(() => setSaveState('idle'), 1200);
            } catch {
                setSaveState('error');
            }
        }, 500);
    }

    // Cleanup any pending timers (unmount/close)
    React.useEffect(() => {
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, []);

    React.useEffect(() => {
        if (open) return;
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
    }, [open]);


    // submit
    const submit = async () => {
        if (saving) return;
        if (!readOnly && subscriptionResolutionError) {
            setFormError(subscriptionResolutionError);
            return;
        }
        if (!readOnly && subscriptionSelectionRequired && !clientSubscriptionId) {
            setFormError('Select the service / tier before saving this ticket.');
            return;
        }
        if (!readOnly && offeringSelectionRequired && !offeringId) {
            setFormError('Select the request category before saving this ticket.');
            return;
        }
        setFormError(null);
        setSaving(true);
        try {
            if (mode === 'create') {
                await onCreate({
                    title: title.trim(),
                    description: desc,
                    projectId: projectId || project?.id || '',
                    milestoneId: selectedMilestoneId || null,
                    priority,
                    assignedAgentId: canEditInternalAssignee ? assigned ?? null : null,
                    assignedOrgMemberId: canEditOrganizationAssignee ? assignedOrgMember ?? null : null,
                    owningTeamNodeId: canEditInternalAssignee ? owningTeamNodeId || null : null,
                    clientSubscriptionId: clientSubscriptionId || null,
                    offeringId: offeringId || null,
                    offeringSubcategoryId: offeringSubcategoryId || null,
                    kbaseProcessId: selectedKbaseProcessId || null,
                    estimatedMinutes: parseEstimatedMinutesInput(estimatedHours),
                    blockedByTicketIds,
                    handoffToTicketIds,
                    subtasks,
                });
                localStorage.removeItem(draftKey);
                onClose();
            } else if (mode === 'edit' && ticket?.id) {
                const patch: {
                    title: string;
                    description: string;
                    projectId: string;
                    milestoneId?: string | null;
                    priority: UiPriority;
                    assignedAgentId?: string | null;
                    assignedOrgMemberId?: string | null;
                    owningTeamNodeId?: string | null;
                    clientSubscriptionId: string | null;
                    offeringId: string | null;
                    offeringSubcategoryId: string | null;
                    kbaseProcessId: string | null;
                    estimatedMinutes: number | null;
                    blockedByTicketIds: string[];
                    handoffToTicketIds: string[];
                    subtasks: Subtask[];
                    startDate?: string;
                    dueDate?: string;
                } = {
                    title,
                    description: desc,
                    projectId,
                    milestoneId: selectedMilestoneId || null,
                    priority,
                    assignedAgentId: canEditInternalAssignee ? assigned : null,
                    assignedOrgMemberId: canEditOrganizationAssignee ? assignedOrgMember : null,
                    clientSubscriptionId: clientSubscriptionId || null,
                    offeringId: offeringId || null,
                    offeringSubcategoryId: offeringSubcategoryId || null,
                    kbaseProcessId: selectedKbaseProcessId || null,
                    estimatedMinutes: parseEstimatedMinutesInput(estimatedHours),
                    blockedByTicketIds,
                    handoffToTicketIds,
                    subtasks,
                };
                if (canEditInternalAssignee) {
                    patch.owningTeamNodeId = owningTeamNodeId || null;
                }
                if (!canEditInternalAssignee) {
                    delete patch.assignedAgentId;
                }
                if (!canEditOrganizationAssignee) {
                    delete patch.assignedOrgMemberId;
                }
                if (canEditScheduleWindow) {
                    patch.startDate = startDate;
                    patch.dueDate = dueDate;
                }
                await onUpdate(ticket.id, patch);
                syncTicketCache(ticket.id, patch);
                localStorage.removeItem(draftKey);
                onClose();
            }
        } finally {
            setSaving(false);
        }
    };

    // comment send
    const sendComment = async () => {
        if (!commentText.trim() || !ticket?.id) return;
        const body = commentText.trim();
        setCommentText('');
        try {
            await api.post(`/tickets/${ticket.id}/comments`, {
                body, attachments: [], internal: false,
            });
            const action = await dispatch(fetchTicketComments({ id: ticket.id, force: true }));
            if (fetchTicketComments.fulfilled.match(action)) {
                setComments(action.payload.comments || []);
            }
        } catch { /* ignore */ }
    };

    const headerTitle = mode === 'create' ? 'Create ticket' : 'Edit ticket';

    const STAFF_WORKFLOW_STATUSES: TicketStatus[] = ['open', 'in_progress', 'blocked', 'resolved'];
    const CLIENT_ALLOWED: TicketStatus[] = ['blocked', 'resolved', 'closed'];
    const canEditPriority = isStaff && !readOnly;
    const statusOptions =
        role === 'agent' || role === 'admin' ? STAFF_WORKFLOW_STATUSES : CLIENT_ALLOWED;
    const statusLabel = React.useCallback((value: TicketStatus) => {
        switch (value) {
            case 'open':
                return 'Planning';
            case 'in_progress':
                return 'In Progress';
            case 'blocked':
                return 'Blockers';
            case 'resolved':
                return 'Complete';
            case 'closed':
                return 'Closed';
            default:
                return value;
        }
    }, []);
    const priorityLabel = React.useCallback((value: UiPriority) => {
        switch (value) {
            case 'low':
                return 'Low';
            case 'medium':
                return 'Medium';
            case 'high':
                return 'High';
            case 'urgent':
                return 'Urgent';
            default:
                return value;
        }
    }, []);

    const renderKBaseProcessListSection = React.useCallback(
        (title: string, items: string[]) => {
            if (!items.length) return null;
            return (
                <KBaseProcessSection>
                    <KBaseProcessSectionTitle>{title}</KBaseProcessSectionTitle>
                    <KBaseProcessList>
                        {items.map((item, index) => (
                            <li key={`${title}-${index}`}>{item}</li>
                        ))}
                    </KBaseProcessList>
                </KBaseProcessSection>
            );
        },
        [],
    );


    async function onStatusChange(next: TicketStatus) {
        setStatus(next);
        if (mode === 'edit' && ticket?.id) {
            try {
                await api.post(`/tickets/${ticket.id}/transition`, { status: next });
                dispatch(ticketPatched({ id: ticket.id, changes: { status: next } }));
            }
            catch { /* ignore; live updates will correct UI */ }
        }
    }

    const handlePriorityChange = React.useCallback((nextPriority: UiPriority) => {
        suppressNextAutosave();
        setPriority(nextPriority);
        void commitImmediatePatch({ priority: nextPriority });
    }, [commitImmediatePatch, suppressNextAutosave]);

    const handleMilestoneChange = React.useCallback((nextMilestoneId: string) => {
        suppressNextAutosave();
        setSelectedMilestoneId(nextMilestoneId);
        void commitImmediatePatch({ milestoneId: nextMilestoneId || null });
    }, [commitImmediatePatch, suppressNextAutosave]);

    const handleProjectChange = React.useCallback((nextProjectId: string) => {
        suppressNextAutosave();
        setProjectId(nextProjectId);
        const nextProjectMilestones =
            (projects || []).find((entry) => entry.id === nextProjectId)?.milestones ?? [];
        const nextMilestoneId = nextProjectMilestones.some((entry) => entry.id === selectedMilestoneId)
            ? selectedMilestoneId
            : '';
        setSelectedMilestoneId(nextMilestoneId);
        void commitImmediatePatch({
            projectId: nextProjectId,
            milestoneId: nextMilestoneId || null,
        });
    }, [commitImmediatePatch, projects, selectedMilestoneId, suppressNextAutosave]);

    const handleOwningTeamChange = React.useCallback((nextTeamNodeId: string) => {
        suppressNextAutosave();
        setOwningTeamNodeId(nextTeamNodeId);
        void commitImmediatePatch({ owningTeamNodeId: nextTeamNodeId || null });
    }, [commitImmediatePatch, suppressNextAutosave]);

    const handleStartDateChange = React.useCallback((nextStartDate: string) => {
        suppressNextAutosave();
        setStartDate(nextStartDate);
        void commitImmediatePatch({ startDate: nextStartDate });
    }, [commitImmediatePatch, suppressNextAutosave]);

    const handleDueDateChange = React.useCallback((nextDueDate: string) => {
        suppressNextAutosave();
        setDueDate(nextDueDate);
        void commitImmediatePatch({ dueDate: nextDueDate });
    }, [commitImmediatePatch, suppressNextAutosave]);

    const handleSubscriptionChange = React.useCallback((nextSubscriptionId: string) => {
        suppressNextAutosave();
        setClientSubscriptionId(nextSubscriptionId);
        setOfferingId('');
        setOfferingSubcategoryId('');
        void commitImmediatePatch({
            clientSubscriptionId: nextSubscriptionId || null,
            offeringId: null,
            offeringSubcategoryId: null,
        });
    }, [commitImmediatePatch, suppressNextAutosave]);

    const handleOfferingChange = React.useCallback((nextOfferingId: string) => {
        suppressNextAutosave();
        setOfferingId(nextOfferingId);
        setOfferingSubcategoryId('');
        void commitImmediatePatch({
            offeringId: nextOfferingId || null,
            offeringSubcategoryId: null,
        });
    }, [commitImmediatePatch, suppressNextAutosave]);

    const handleOfferingSubcategoryChange = React.useCallback((nextSubcategoryId: string) => {
        suppressNextAutosave();
        setOfferingSubcategoryId(nextSubcategoryId);
        void commitImmediatePatch({ offeringSubcategoryId: nextSubcategoryId || null });
    }, [commitImmediatePatch, suppressNextAutosave]);

    const handleDescriptionBlur = React.useCallback(() => {
        if (readOnly || mode !== 'edit' || !ticket?.id) return;
        if (ticketHydrating || contractContextLoading) return;
        if (subscriptionResolutionError) return;
        if (subscriptionSelectionRequired && !clientSubscriptionId) return;
        if (offeringSelectionRequired && !offeringId) return;

        const baseline = autosaveBaselineRef.current ?? buildAutosaveSnapshot();
        if ((baseline.description ?? '') === desc) return;

        void commitImmediatePatch({ description: desc });
    }, [
        buildAutosaveSnapshot,
        clientSubscriptionId,
        commitImmediatePatch,
        contractContextLoading,
        desc,
        mode,
        offeringId,
        offeringSelectionRequired,
        readOnly,
        subscriptionResolutionError,
        subscriptionSelectionRequired,
        ticket?.id,
        ticketHydrating,
    ]);



    const canEditOwningTeam = isStaff && !readOnly;
    const canAssignSelf = readOnly && isStaff && !ticket?.assignedAgentId;
    const assignmentMenuLabel = canEditOrganizationAssignee
        ? 'Assign organization member'
        : 'Assign agent or admin';
    const fallbackSubscriptionOption = React.useMemo<TicketSubscriptionOption | null>(() => {
        const fallbackId = cleanText(clientSubscriptionId) || cleanText(ticket?.clientSubscriptionId);
        const fallbackServiceId = cleanText(ticket?.serviceId);
        const fallbackServiceCode = cleanText(ticket?.serviceCode);
        const fallbackServiceTitle = cleanText(ticket?.serviceTitle);
        if (!fallbackId && !fallbackServiceId && !fallbackServiceCode && !fallbackServiceTitle) return null;
        return {
            id: fallbackId || `ticket-subscription:${cleanText(ticket?.id) || 'current'}`,
            serviceId: fallbackServiceId || fallbackServiceCode || fallbackServiceTitle || fallbackId || 'current-service',
            tierId: cleanText(ticket?.tierId) || null,
            serviceCode: fallbackServiceCode,
            serviceTitle: fallbackServiceTitle || fallbackServiceCode || 'Selected service',
            tierSkuId: cleanText(ticket?.tierSkuId) || null,
            tierName: cleanText(ticket?.tierName) || null,
        };
    }, [
        clientSubscriptionId,
        ticket?.clientSubscriptionId,
        ticket?.id,
        ticket?.serviceCode,
        ticket?.serviceId,
        ticket?.serviceTitle,
        ticket?.tierId,
        ticket?.tierName,
        ticket?.tierSkuId,
    ]);
    const currentSubscriptionSelectionId = cleanText(clientSubscriptionId);
    const persistedSubscriptionSelectionId = cleanText(ticket?.clientSubscriptionId);
    const effectiveSubscriptionOptions = React.useMemo(() => {
        if (!fallbackSubscriptionOption) return subscriptionOptions;
        const fallbackId =
            currentSubscriptionSelectionId
            || (!subscriptionOptions.length ? persistedSubscriptionSelectionId : '')
            || fallbackSubscriptionOption.id;
        if (!fallbackId) return subscriptionOptions;
        if (subscriptionOptions.some((option) => option.id === fallbackId)) {
            return subscriptionOptions;
        }
        if (!subscriptionOptions.length || currentSubscriptionSelectionId) {
            return [fallbackSubscriptionOption, ...subscriptionOptions];
        }
        return subscriptionOptions;
    }, [
        currentSubscriptionSelectionId,
        fallbackSubscriptionOption,
        persistedSubscriptionSelectionId,
        subscriptionOptions,
    ]);
    const selectedSubscriptionOption = React.useMemo(
        () =>
            effectiveSubscriptionOptions.find((option) => option.id === clientSubscriptionId)
            ?? (!clientSubscriptionId ? effectiveSubscriptionOptions[0] ?? null : null),
        [clientSubscriptionId, effectiveSubscriptionOptions],
    );
    const fallbackOfferingSubcategoryOption = React.useMemo(() => {
        const fallbackId = cleanText(offeringSubcategoryId) || cleanText(ticket?.offeringSubcategoryId);
        const fallbackName = cleanText(ticket?.offeringSubcategoryName);
        if (!fallbackId || !fallbackName) return null;
        return {
            id: fallbackId,
            name: fallbackName,
            description: cleanText(ticket?.offeringSubcategoryDescription) || null,
        };
    }, [
        offeringSubcategoryId,
        ticket?.offeringSubcategoryDescription,
        ticket?.offeringSubcategoryId,
        ticket?.offeringSubcategoryName,
    ]);
    const fallbackOfferingOption = React.useMemo<TicketOfferingOption | null>(() => {
        const fallbackId = cleanText(offeringId) || cleanText(ticket?.offeringId);
        const fallbackName = cleanText(ticket?.offeringName);
        if (!fallbackId && !fallbackName) return null;
        return {
            id: fallbackId || `ticket-offering:${cleanText(ticket?.id) || 'current'}`,
            code: cleanText(ticket?.offeringCode),
            name: fallbackName || cleanText(ticket?.offeringCode) || 'Selected request category',
            type: null,
            source: null,
            targetCompletionMinutes: null,
            resolutionTargetMinutes: null,
            subcategories: fallbackOfferingSubcategoryOption ? [fallbackOfferingSubcategoryOption] : [],
        };
    }, [
        fallbackOfferingSubcategoryOption,
        offeringId,
        ticket?.id,
        ticket?.offeringCode,
        ticket?.offeringId,
        ticket?.offeringName,
    ]);
    const currentOfferingSelectionId = cleanText(offeringId);
    const persistedOfferingSelectionId = cleanText(ticket?.offeringId);
    const effectiveOfferingOptions = React.useMemo(() => {
        if (!fallbackOfferingOption) return offeringOptions;
        const fallbackId =
            currentOfferingSelectionId
            || (!offeringOptions.length ? persistedOfferingSelectionId : '')
            || fallbackOfferingOption.id;
        if (!fallbackId) return offeringOptions;
        if (offeringOptions.some((option) => option.id === fallbackId)) {
            return offeringOptions;
        }
        if (!offeringOptions.length || currentOfferingSelectionId) {
            return [fallbackOfferingOption, ...offeringOptions];
        }
        return offeringOptions;
    }, [
        currentOfferingSelectionId,
        fallbackOfferingOption,
        offeringOptions,
        persistedOfferingSelectionId,
    ]);
    const selectedOfferingOption = React.useMemo(
        () =>
            effectiveOfferingOptions.find((option) => option.id === offeringId)
            ?? (!offeringId ? effectiveOfferingOptions[0] ?? null : null),
        [effectiveOfferingOptions, offeringId],
    );
    const selectedOfferingSubcategories = React.useMemo(
        () => selectedOfferingOption?.subcategories ?? [],
        [selectedOfferingOption],
    );
    const selectedOfferingSubcategoryOption = React.useMemo(
        () =>
            selectedOfferingSubcategories.find(
                (subcategory) => subcategory.id === offeringSubcategoryId,
            )
            ?? fallbackOfferingSubcategoryOption
            ?? null,
        [fallbackOfferingSubcategoryOption, offeringSubcategoryId, selectedOfferingSubcategories],
    );
    const subscriptionDisplayLabel = React.useMemo(
        () => formatSubscriptionLabel(selectedSubscriptionOption),
        [selectedSubscriptionOption],
    );
    const requestCategoryDisplayLabel = React.useMemo(
        () => cleanText(selectedOfferingOption?.name) || cleanText(ticket?.offeringName) || '--',
        [selectedOfferingOption?.name, ticket?.offeringName],
    );
    const requestSubcategoryDisplayLabel = React.useMemo(
        () =>
            cleanText(selectedOfferingSubcategoryOption?.name)
            || cleanText(ticket?.offeringSubcategoryName)
            || '--',
        [selectedOfferingSubcategoryOption?.name, ticket?.offeringSubcategoryName],
    );
    const requestSubcategoryDescription = React.useMemo(
        () =>
            cleanText(selectedOfferingSubcategoryOption?.description)
            || cleanText(ticket?.offeringSubcategoryDescription)
            || null,
        [selectedOfferingSubcategoryOption?.description, ticket?.offeringSubcategoryDescription],
    );
    const currentFirebaseUid = cleanText(user?.uid);
    const kbaseTicketScope = React.useMemo<TicketKBaseScope>(() => ({
        level: selectedOfferingSubcategoryOption?.id
            ? 'subcategory'
            : selectedOfferingOption?.code
                ? 'offering'
                : selectedSubscriptionOption?.serviceId || selectedSubscriptionOption?.serviceCode || selectedSubscriptionOption?.serviceTitle
                    ? 'service'
                    : 'none',
        serviceId: cleanText(selectedSubscriptionOption?.serviceId),
        serviceCode: cleanText(selectedSubscriptionOption?.serviceCode),
        serviceLabel: cleanText(selectedSubscriptionOption?.serviceTitle),
        offeringCode: cleanText(selectedOfferingOption?.code),
        offeringName: cleanText(selectedOfferingOption?.name),
        subcategoryId: cleanText(selectedOfferingSubcategoryOption?.id),
        subcategoryName: cleanText(selectedOfferingSubcategoryOption?.name),
    }), [
        selectedOfferingOption?.code,
        selectedOfferingOption?.name,
        selectedOfferingSubcategoryOption?.id,
        selectedOfferingSubcategoryOption?.name,
        selectedSubscriptionOption?.serviceCode,
        selectedSubscriptionOption?.serviceId,
        selectedSubscriptionOption?.serviceTitle,
    ]);
    const exactScopeKBaseProcesses = React.useMemo(
        () => kbaseProcesses
            .filter((process) => processMatchesTicketScope(process, kbaseTicketScope))
            .sort((a, b) =>
                rankTicketKBaseStatus(a.status) - rankTicketKBaseStatus(b.status)
                || a.title.localeCompare(b.title),
            ),
        [kbaseProcesses, kbaseTicketScope],
    );
    const recentKBaseProcesses = React.useMemo(
        () => recentKbaseProcessIds
            .map((processId) => kbaseProcesses.find((process) => process.id === processId))
            .filter(Boolean) as TicketKBaseProcess[],
        [kbaseProcesses, recentKbaseProcessIds],
    );
    const searchedKBaseProcesses = React.useMemo(() => {
        const normalizedQuery = cleanText(kbaseSearchQuery);
        if (!normalizedQuery) return [];
        return kbaseProcesses
            .filter((process) => matchesTicketKBaseQuery(process, normalizedQuery))
            .sort((a, b) =>
                scoreTicketKBaseProcessSearch(b, normalizedQuery, kbaseTicketScope)
                - scoreTicketKBaseProcessSearch(a, normalizedQuery, kbaseTicketScope)
                || a.title.localeCompare(b.title),
            );
    }, [kbaseProcesses, kbaseSearchQuery, kbaseTicketScope]);
    const selectedKBaseProcess = React.useMemo(
        () => kbaseProcesses.find((process) => process.id === selectedKbaseProcessId) ?? null,
        [kbaseProcesses, selectedKbaseProcessId],
    );
    const kbaseSuggestionScopeLabel = React.useMemo(() => {
        if (kbaseTicketScope.level === 'subcategory' && kbaseTicketScope.subcategoryName) {
            return `Suggested for ${kbaseTicketScope.subcategoryName}`;
        }
        if (kbaseTicketScope.level === 'offering' && kbaseTicketScope.offeringName) {
            return `Suggested for ${kbaseTicketScope.offeringName}`;
        }
        if (kbaseTicketScope.level === 'service' && kbaseTicketScope.serviceLabel) {
            return `Suggested for ${kbaseTicketScope.serviceLabel}`;
        }
        return 'Suggested processes';
    }, [kbaseTicketScope]);
    const selectedTeamOption = React.useMemo(
        () => teamOptions.find((option) => option.id === owningTeamNodeId) ?? null,
        [owningTeamNodeId, teamOptions],
    );
    const effectiveTeamLabel = React.useMemo(() => {
        if (selectedTeamOption?.label) return selectedTeamOption.label;
        if ((ticket as any)?.currentTeamLabel) return String((ticket as any).currentTeamLabel);
        if ((ticket as any)?.defaultTeamLabel) return String((ticket as any).defaultTeamLabel);
        return owningTeamNodeId || '--';
    }, [owningTeamNodeId, selectedTeamOption, ticket]);
    const formatRelationshipOptionMeta = React.useCallback((entry: TicketRelationshipSummary) => {
        const teamNodeId = cleanText(entry.owningTeamNodeId) || cleanText(entry.defaultTeamNodeId);
        const teamLabel = teamNodeId ? teamLabelByOptionId.get(teamNodeId) || teamNodeId : null;
        const statusLabel = cleanText(entry.status)?.replace(/_/g, ' ') || null;
        return [teamLabel, statusLabel].filter(Boolean).join(' • ');
    }, [teamLabelByOptionId]);
    const formatRelationshipOptionTitle = React.useCallback((entry: TicketRelationshipSummary) => {
        const ticketNumber = cleanText(entry.ticketNumber);
        return ticketNumber ? `#${ticketNumber} ${entry.title}` : entry.title;
    }, []);
    const activeTimeLabel = React.useMemo(
        () => formatMinutesAsHours(ticket?.actualActiveMinutes ?? null),
        [ticket?.actualActiveMinutes],
    );
    React.useEffect(() => {
        setRecentKbaseProcessIds(readRecentTicketKBaseProcessIds(currentFirebaseUid));
    }, [currentFirebaseUid]);

    React.useEffect(() => {
        if (!open || !isStaff) return;

        let cancelled = false;
        const cached = cachedTicketKBaseProcesses;
        const cacheIsFresh =
            Boolean(cached) && Date.now() - (cached?.fetchedAt ?? 0) < TICKET_KBASE_CACHE_TTL_MS;

        if (cached?.processes?.length) {
            setKbaseProcesses(cached.processes);
        }
        if (cacheIsFresh) {
            setKbaseProcessesError(null);
            setKbaseProcessesLoading(false);
            return;
        }

        if (!cached?.processes?.length) {
            setKbaseProcessesLoading(true);
        }

        fetchCachedTicketKBaseProcesses()
            .then((processes) => {
                if (cancelled) return;
                setKbaseProcesses(processes);
                setKbaseProcessesError(null);
            })
            .catch((error: any) => {
                if (cancelled) return;
                setKbaseProcessesError(
                    cleanText(error?.message) || 'Unable to load KBase processes right now.',
                );
            })
            .finally(() => {
                if (cancelled) return;
                setKbaseProcessesLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, isStaff]);

    React.useEffect(() => {
        kBaseAiLoadVersionRef.current += 1;
        setKbaseMenuOpen(false);
        setKbaseSearchQuery('');
        setIsKBaseProcessPanelOpen(false);
        setIsKBaseAiPanelOpen(false);
        setIsKBaseAiActionsOpen(false);
        setKBaseAiThreadId(null);
        setKBaseAiDraft('');
        setKBaseAiAttachments([]);
        setKBaseAiMessages([]);
        setKBaseAiLoading(false);
        setKBaseAiSending(false);
        setKBaseAiError(null);
    }, [ticket?.id]);

    React.useEffect(() => {
        if (open) return;
        kBaseAiLoadVersionRef.current += 1;
        setIsKBaseProcessPanelOpen(false);
        setIsKBaseAiPanelOpen(false);
        setIsKBaseAiActionsOpen(false);
        setKBaseAiThreadId(null);
        setKBaseAiDraft('');
        setKBaseAiAttachments([]);
        setKBaseAiMessages([]);
        setKBaseAiLoading(false);
        setKBaseAiSending(false);
        setKBaseAiError(null);
    }, [open]);

    React.useEffect(() => {
        if (!kbaseMenuOpen) return;
        const raf = window.requestAnimationFrame(() => {
            kbaseSearchInputRef.current?.focus();
            kbaseSearchInputRef.current?.select();
        });
        return () => window.cancelAnimationFrame(raf);
    }, [kbaseMenuOpen]);

    React.useEffect(() => {
        if (!kbaseMenuOpen) return;
        const handlePointerDown = (event: MouseEvent) => {
            if (!kbaseFinderRef.current?.contains(event.target as Node)) {
                setKbaseMenuOpen(false);
                setKbaseSearchQuery('');
            }
        };
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            setKbaseMenuOpen(false);
            setKbaseSearchQuery('');
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [kbaseMenuOpen]);

    React.useEffect(() => {
        if (!isKBaseAiActionsOpen) return;
        const handlePointerDown = (event: MouseEvent) => {
            if (!kBaseAiActionsRef.current?.contains(event.target as Node)) {
                setIsKBaseAiActionsOpen(false);
            }
        };
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            setIsKBaseAiActionsOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isKBaseAiActionsOpen]);

    const rememberKBaseProcessSelection = React.useCallback((processId: string) => {
        if (!processId) return;
        setRecentKbaseProcessIds((current) => {
            const next = [processId, ...current.filter((value) => value !== processId)].slice(
                0,
                TICKET_KBASE_RECENT_LIMIT,
            );
            writeRecentTicketKBaseProcessIds(currentFirebaseUid, next);
            return next;
        });
    }, [currentFirebaseUid]);

    const clearKBaseAiPendingWork = React.useCallback(() => {
        if (kBaseAiAbortRef.current) {
            kBaseAiAbortRef.current.abort();
            kBaseAiAbortRef.current = null;
        }
    }, []);

    const streamKBaseAiConversation = React.useCallback(async (params: {
        ticketId: string;
        processId: string;
        threadId?: string | null;
        message?: string;
        attachments?: File[];
        bootstrap?: boolean;
        restoreDraftOnError?: string | null;
        restoreAttachmentsOnError?: File[];
    }) => {
        const normalizedTicketId = cleanText(params.ticketId);
        const normalizedProcessId = cleanText(params.processId);
        if (!normalizedTicketId || !normalizedProcessId) return;

        clearKBaseAiPendingWork();

        const previousMessages = kBaseAiMessagesRef.current;
        const requestVersion = kBaseAiLoadVersionRef.current + 1;
        kBaseAiLoadVersionRef.current = requestVersion;
        const pendingAssistantMessageId = `assistant-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const trimmedMessage = cleanText(params.message);
        const selectedAttachments = params.attachments ?? [];
        const optimisticUserBody =
            !params.bootstrap
                ? buildKBaseAiAttachmentUserMessage(trimmedMessage, selectedAttachments)
                : '';
        const optimisticUserMessageId =
            !params.bootstrap && optimisticUserBody
                ? `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
                : null;

        setKBaseAiError(null);
        if (params.bootstrap) {
            setKBaseAiMessages([
                {
                    id: pendingAssistantMessageId,
                    role: 'assistant',
                    body: 'Loading ticket details...',
                    pending: true,
                },
            ]);
            setKBaseAiLoading(true);
        } else {
            setKBaseAiMessages((current) => [
                ...current,
                ...(optimisticUserMessageId
                    ? [{
                        id: optimisticUserMessageId,
                        role: 'user' as const,
                        body: optimisticUserBody,
                    }]
                    : []),
                {
                    id: pendingAssistantMessageId,
                    role: 'assistant',
                    body: 'Loading ticket details...',
                    pending: true,
                },
            ]);
            setKBaseAiSending(true);
        }

        const controller = new AbortController();
        kBaseAiAbortRef.current = controller;
        let finalResponse: SendTicketCopilotMessageResponse | null = null;
        let hasStartedTokenStream = false;

        try {
            await streamTicketCopilotMessage(normalizedTicketId, {
                threadId: cleanText(params.threadId) || undefined,
                agentCode: TICKET_PROCESS_COPILOT_AGENT_CODE,
                processIds: [normalizedProcessId],
                message: !params.bootstrap ? trimmedMessage || undefined : undefined,
                attachments: selectedAttachments,
                bootstrapMode: params.bootstrap ? 'ticket_process_ready' : undefined,
            }, {
                signal: controller.signal,
                onEvent: (event) => {
                    if (kBaseAiLoadVersionRef.current !== requestVersion) return;
                    if (event.type === 'status') {
                        if (hasStartedTokenStream) return;
                        setKBaseAiMessages((current) =>
                            current.map((message) =>
                                message.id === pendingAssistantMessageId
                                    ? {
                                        ...message,
                                        body: cleanText(event.message) || message.body,
                                        pending: true,
                                    }
                                    : message,
                            ),
                        );
                    } else if (event.type === 'token') {
                        const nextToken = event.token || '';
                        if (!nextToken) return;
                        const firstToken = !hasStartedTokenStream;
                        hasStartedTokenStream = true;
                        setKBaseAiMessages((current) =>
                            current.map((message) =>
                                message.id === pendingAssistantMessageId
                                    ? {
                                        ...message,
                                        body: firstToken
                                            ? nextToken
                                            : `${message.body || ''}${nextToken}`,
                                        pending: true,
                                    }
                                    : message,
                            ),
                        );
                    } else if (event.type === 'complete') {
                        finalResponse = event.response;
                    }
                },
            });

            if (kBaseAiLoadVersionRef.current !== requestVersion) return;
            if (!finalResponse) {
                throw new Error('AI response was empty.');
            }
            const resolvedResponse = finalResponse as SendTicketCopilotMessageResponse;

            setKBaseAiThreadId(resolvedResponse.thread.id);

            if (kBaseAiLoadVersionRef.current !== requestVersion) return;
            setKBaseAiMessages(
                mapTicketCopilotThreadMessagesToUi(resolvedResponse.thread.messages),
            );
            setKBaseAiError(null);
        } catch (error: any) {
            if (error?.name === 'AbortError') {
                return;
            }
            if (params.restoreDraftOnError) {
                setKBaseAiDraft(params.restoreDraftOnError);
            }
            if (params.restoreAttachmentsOnError?.length) {
                setKBaseAiAttachments(params.restoreAttachmentsOnError);
            }
            setKBaseAiMessages(previousMessages);
            setKBaseAiError(
                cleanText(error?.message) || 'Unable to reach the AI copilot right now.',
            );
        } finally {
            if (kBaseAiAbortRef.current === controller) {
                kBaseAiAbortRef.current = null;
            }
            if (kBaseAiLoadVersionRef.current === requestVersion) {
                setKBaseAiLoading(false);
                setKBaseAiSending(false);
            }
        }
    }, [clearKBaseAiPendingWork]);

    const clearKBaseAiConversation = React.useCallback(async () => {
        const normalizedThreadId = cleanText(kBaseAiThreadId);
        const normalizedTicketId = cleanText(ticket?.id);
        const normalizedProcessId = cleanText(selectedKBaseProcess?.id);

        if (kBaseAiClearing || !normalizedTicketId || !normalizedProcessId) return;

        setIsKBaseAiActionsOpen(false);
        clearKBaseAiPendingWork();
        setKBaseAiClearing(true);
        setKBaseAiError(null);
        setKBaseAiThreadId(null);
        setKBaseAiMessages([]);
        setKBaseAiDraft('');
        setKBaseAiAttachments([]);

        try {
            if (normalizedThreadId) {
                await deleteTicketCopilotThread(normalizedThreadId);
            }
            await streamKBaseAiConversation({
                ticketId: normalizedTicketId,
                processId: normalizedProcessId,
                bootstrap: true,
            });
        } catch (error: any) {
            setKBaseAiError(
                cleanText(error?.message) || 'Unable to clear the AI chat right now.',
            );
        } finally {
            setKBaseAiClearing(false);
        }
    }, [
        clearKBaseAiPendingWork,
        kBaseAiClearing,
        kBaseAiThreadId,
        selectedKBaseProcess?.id,
        streamKBaseAiConversation,
        ticket?.id,
    ]);

    const openKBaseAiFilePicker = React.useCallback(() => {
        if (kBaseAiSending || kBaseAiLoading || kBaseAiClearing) return;
        kBaseAiFileInputRef.current?.click();
    }, [kBaseAiClearing, kBaseAiLoading, kBaseAiSending]);

    const removeKBaseAiAttachment = React.useCallback((indexToRemove: number) => {
        setKBaseAiAttachments((current) =>
            current.filter((_, index) => index !== indexToRemove),
        );
    }, []);

    const handleKBaseAiFilesSelected = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const incoming = Array.from(event.target.files ?? []);
        event.target.value = '';
        if (!incoming.length) return;

        const validationError = incoming.find((file) =>
            !KBASE_AI_ALLOWED_ATTACHMENT_MIME_TYPES.has(file.type)
            && !/\.(txt|csv|md|json|docx)$/i.test(file.name),
        );
        if (validationError) {
            setKBaseAiError(
                'Supported attachments are PNG, JPG, WEBP, PDF, CSV, TXT, JSON, and DOCX.',
            );
            return;
        }

        const oversizeFile = incoming.find((file) => file.size > MAX_KBASE_AI_ATTACHMENT_BYTES);
        if (oversizeFile) {
            setKBaseAiError(
                `${oversizeFile.name} must be 10MB or smaller.`,
            );
            return;
        }

        setKBaseAiAttachments((current) => {
            const next = [...current, ...incoming].slice(0, MAX_KBASE_AI_ATTACHMENTS);
            if (current.length + incoming.length > MAX_KBASE_AI_ATTACHMENTS) {
                setKBaseAiError(`You can attach up to ${MAX_KBASE_AI_ATTACHMENTS} files per message.`);
            } else {
                setKBaseAiError(null);
            }
            return next;
        });
    }, []);

    const selectKBaseProcess = React.useCallback((processId: string) => {
        suppressNextAutosave();
        setSelectedKbaseProcessId(processId);
        rememberKBaseProcessSelection(processId);
        setKbaseMenuOpen(false);
        setKbaseSearchQuery('');
        void commitImmediatePatch({ kbaseProcessId: processId || null });
    }, [commitImmediatePatch, rememberKBaseProcessSelection, suppressNextAutosave]);

    React.useEffect(() => {
        if (selectedKBaseProcess) return;
        clearKBaseAiPendingWork();
        kBaseAiLoadVersionRef.current += 1;
        setIsKBaseProcessPanelOpen(false);
        setIsKBaseAiPanelOpen(false);
        setKBaseAiThreadId(null);
        setKBaseAiDraft('');
        setKBaseAiAttachments([]);
        setKBaseAiMessages([]);
        setKBaseAiLoading(false);
        setKBaseAiSending(false);
        setKBaseAiError(null);
    }, [clearKBaseAiPendingWork, selectedKBaseProcess]);

    React.useEffect(() => () => {
        clearKBaseAiPendingWork();
    }, [clearKBaseAiPendingWork]);

    React.useEffect(() => {
        if (open && isKBaseAiPanelOpen) return;
        clearKBaseAiPendingWork();
    }, [clearKBaseAiPendingWork, isKBaseAiPanelOpen, open]);

    const hydrateKBaseAiThread = React.useCallback(async (processId: string) => {
        const normalizedTicketId = cleanText(ticket?.id);
        const normalizedProcessId = cleanText(processId);

        if (!normalizedTicketId || !normalizedProcessId) {
            clearKBaseAiPendingWork();
            setKBaseAiThreadId(null);
            setKBaseAiMessages([]);
            setKBaseAiLoading(false);
            return;
        }

        const requestVersion = kBaseAiLoadVersionRef.current + 1;
        kBaseAiLoadVersionRef.current = requestVersion;
        setKBaseAiLoading(true);
        setKBaseAiError(null);

        try {
            const response = await listTicketCopilotThreads(normalizedTicketId, {
                agentCode: TICKET_PROCESS_COPILOT_AGENT_CODE,
            });

            if (kBaseAiLoadVersionRef.current !== requestVersion) return;

            const matchingThread =
                response.threads.find((thread) =>
                    threadMatchesSelectedProcess(thread, normalizedProcessId),
                ) ?? null;

            if (matchingThread) {
                const hydratedMessages = mapTicketCopilotThreadMessagesToUi(matchingThread.messages);
                if (hydratedMessages.length > 0) {
                    setKBaseAiThreadId(matchingThread.id);
                    setKBaseAiMessages(hydratedMessages);
                    setKBaseAiLoading(false);
                    return;
                }
            }
            await streamKBaseAiConversation({
                ticketId: normalizedTicketId,
                processId: normalizedProcessId,
                threadId: matchingThread?.id ?? undefined,
                bootstrap: true,
            });
        } catch (error: any) {
            if (kBaseAiLoadVersionRef.current !== requestVersion) return;
            setKBaseAiThreadId(null);
            setKBaseAiMessages([]);
            setKBaseAiError(
                cleanText(error?.message) || 'Unable to load the AI conversation right now.',
            );
        } finally {
            if (kBaseAiLoadVersionRef.current === requestVersion) {
                setKBaseAiLoading(false);
            }
        }
    }, [clearKBaseAiPendingWork, streamKBaseAiConversation, ticket?.id]);

    React.useEffect(() => {
        if (!isStaff || !isKBaseAiPanelOpen || !selectedKBaseProcess?.id) return;
        setKBaseAiDraft('');
        setKBaseAiThreadId(null);
        setKBaseAiMessages([]);
        setKBaseAiAttachments([]);
        void hydrateKBaseAiThread(selectedKBaseProcess.id);
    }, [hydrateKBaseAiThread, isKBaseAiPanelOpen, isStaff, selectedKBaseProcess?.id]);

    const sendKBaseAiMessage = React.useCallback(async () => {
        const trimmed = kBaseAiDraft.trim();
        const attachmentsForSend = [...kBaseAiAttachments];
        if ((!trimmed && !attachmentsForSend.length) || !ticket?.id || !selectedKBaseProcess?.id || kBaseAiSending) return;
        setKBaseAiDraft('');
        setKBaseAiAttachments([]);
        await streamKBaseAiConversation({
            ticketId: ticket.id,
            processId: selectedKBaseProcess.id,
            threadId: kBaseAiThreadId || undefined,
            message: trimmed || undefined,
            attachments: attachmentsForSend,
            restoreDraftOnError: trimmed,
            restoreAttachmentsOnError: attachmentsForSend,
        });
    }, [
        kBaseAiAttachments,
        kBaseAiDraft,
        kBaseAiSending,
        kBaseAiThreadId,
        selectedKBaseProcess?.id,
        streamKBaseAiConversation,
        ticket?.id,
    ]);

    const resolvedClientIdentity = React.useMemo(() => {
        const lookup = ticket?.clientId ? userMap[ticket.clientId] : undefined;
        return {
            uid: firstNonEmptyText(clientIdentity.uid, ticket?.clientId) || null,
            businessName: firstNonEmptyText(clientIdentity.businessName, lookup?.businessName) || null,
            businessLogoURL: firstNonEmptyText(clientIdentity.businessLogoURL, lookup?.businessLogoURL) || null,
            firstName: firstNonEmptyText(clientIdentity.firstName, lookup?.firstName) || null,
            lastName: firstNonEmptyText(clientIdentity.lastName, lookup?.lastName) || null,
            displayName: firstNonEmptyText(
                clientIdentity.displayName,
                lookup?.displayName,
                lookup?.name,
            ) || null,
            email: firstNonEmptyText(clientIdentity.email, lookup?.email) || null,
            photoURL: firstNonEmptyText(
                clientIdentity.businessLogoURL,
                clientIdentity.photoURL,
                lookup?.businessLogoURL,
                lookup?.photoURL,
            ) || null,
        };
    }, [clientIdentity, ticket?.clientId, userMap]);
    const clientName = React.useMemo(() => resolveClientDisplayName({
        businessName: resolvedClientIdentity.businessName,
        firstName: resolvedClientIdentity.firstName,
        lastName: resolvedClientIdentity.lastName,
        displayName: resolvedClientIdentity.displayName,
        email: resolvedClientIdentity.email,
        uid: ticket?.clientId,
    }, '--'), [resolvedClientIdentity, ticket?.clientId]);
    const clientAvatarUid = firstNonEmptyText(resolvedClientIdentity.uid, ticket?.clientId);
    const clientAvatarEmail = firstNonEmptyText(resolvedClientIdentity.email, (ticket as any)?.clientEmail);
    const clientAvatarName = firstNonEmptyText(resolvedClientIdentity.displayName, clientName);
    const clientAvatarPhotoURL = firstNonEmptyText(resolvedClientIdentity.businessLogoURL, resolvedClientIdentity.photoURL);
    const showHeaderDetailsSkeleton = open && mode === 'edit' && ticketHydrating;
    const showContractFieldsSkeleton =
        open &&
        !readOnly &&
        contractContextLoading &&
        (mode === 'create' || mode === 'edit');
    const showTeamSkeleton =
        showContractFieldsSkeleton &&
        !selectedTeamOption?.label &&
        !firstNonEmptyText((ticket as any)?.currentTeamLabel, (ticket as any)?.defaultTeamLabel);
    const showSubscriptionSkeleton =
        showContractFieldsSkeleton &&
        effectiveSubscriptionOptions.length === 0 &&
        !subscriptionResolutionError;
    const showOfferingSkeleton =
        showContractFieldsSkeleton &&
        effectiveOfferingOptions.length === 0 &&
        requestCategoryDisplayLabel === '--';

    function getCommentAuthor(comment: TicketComment): {
        uid: string | null;
        firstName: string | null;
        lastName: string | null;
        name: string;
        email: string | null;
        photoURL: string | null;
    } {
        const uid = firstNonEmptyText(comment.authorId);
        const lookup = uid ? userMap[uid] : undefined;
        const matchedAgent = uid ? assigneeAgents.find((agent) => agent.id === uid) : undefined;
        const isClientAuthor = Boolean(uid && clientAvatarUid && uid === clientAvatarUid);

        const firstName = firstNonEmptyText(comment.authorFirstName, lookup?.firstName);
        const lastName = firstNonEmptyText(comment.authorLastName, lookup?.lastName);
        const displayName = firstNonEmptyText(
            comment.authorDisplayName,
            lookup?.displayName,
            lookup?.name,
            matchedAgent?.name,
            isClientAuthor ? resolvedClientIdentity.displayName : '',
        );
        const email = firstNonEmptyText(
            comment.authorEmail,
            lookup?.email,
            matchedAgent?.email,
            isClientAuthor ? clientAvatarEmail : '',
        );
        const photoURL = firstNonEmptyText(
            comment.authorPhotoURL,
            lookup?.photoURL,
            matchedAgent?.photoURL,
            isClientAuthor ? resolvedClientIdentity.photoURL : '',
        );

        const name = nameFromIdentity(
            {
                firstName,
                lastName,
                displayName,
                email,
                username: lookup?.username,
            },
            uid || null,
        );

        return {
            uid: uid || null,
            firstName: firstName || null,
            lastName: lastName || null,
            name,
            email: email || null,
            photoURL: photoURL || null,
        };
    }

    // The current user is the agent assigned to this ticket
    const amAssignee = Boolean(assigned && user?.uid && assigned === user.uid);
    // Agents may unassign themselves (admins optional; see note below)
    const canUnassignSelf = role === 'agent' && amAssignee;

    async function unassignSelf() {
        if (!ticket?.id || !canUnassignSelf) return;
        setShowMenu(false);

        // Optimistic UI
        setAssigned(null);

        try {
            // Reuse the page's updater so it hits your /assign endpoint via the thunk
            await onUpdate(ticket.id, { assignedAgentId: null });
            syncTicketCache(ticket.id, { assignedAgentId: null });
            // (backend will also move status -> 'open' unless it's closed)
        } catch {
            // If you want, you could restore the optimistic change on error
        }
    }


    // Accept several shapes/keys so we always render a created date when available
    // Prefer locally-fetched value, then props (accepts multiple shapes)
    const rawCreated =
        createdAtOverride ??
        ticket?.createdAt ??
        (ticket as any)?.created ??
        (ticket as any)?.created_at ??
        (ticket as any)?.created_on ??
        null;


    const createdOn = React.useMemo(() => {
        const d = tsToDate(rawCreated);
        return d ? d.toLocaleString() : '--';
    }, [rawCreated]);
    const ticketNumberLabel = React.useMemo(() => {
        const value = String((ticket as any)?.ticketNumber ?? '').trim();
        return value ? `#${value}` : null;
    }, [ticket]);


    const fileRef = React.useRef<HTMLInputElement>(null);

    async function handleMarkComplete() {
        if (mode !== 'edit' || !ticket?.id) return;
        try {
            await api.post(`/tickets/${ticket.id}/transition`, { status: 'resolved' });
        } catch {/* ignore; live updates will correct */ }
    }

    function handleFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
        const f = e.target.files?.[0];
        if (!f || !ticket?.id) return;
        ticket?.onAttach?.(ticket.id, f); // <-- reference the nested handler
        e.currentTarget.value = '';
    }

    function formatCommentTimestamp(ts?: any): string {
        const d = tsToDate(ts);
        if (!d) return '--';
        return d.toLocaleString();
    }


    const drawer = (
        <AnimatePresence>
            {open && (
                <>
                    <Backdrop onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
                    <Sheet
                        $kbaseOpen={Boolean(isStaff && isKBaseProcessPanelOpen)}
                        $rightOffset={isStaff && isKBaseProcessPanelOpen ? TICKET_KBASE_PANEL_WIDTH : '0px'}
                        role="dialog" aria-modal="true" aria-label={headerTitle}
                        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                        transition={{ type: 'spring', stiffness: 260, damping: 28 }}
                    >
                        <Header>
                            <HeaderGrid>
                                {/* Title row */}


                                {/* Control bar (no status dropdown) */}
                                {mode === 'edit' && (
                                    <ControlsBar>
                                        <LeftControls>
                                            {showHeaderDetailsSkeleton ? (
                                                <ClientIdentityValue aria-label="Client identity loading">
                                                    <DrawerSkeletonCircle />
                                                    <DrawerSkeletonStack>
                                                        <DrawerSkeletonLine $width="156px" />
                                                        <DrawerSkeletonLine $width="88px" $height="12px" />
                                                    </DrawerSkeletonStack>
                                                </ClientIdentityValue>
                                            ) : (
                                                <ClientIdentityValue aria-label="Client identity">
                                                    <PortalAvatar
                                                        uid={clientAvatarUid || null}
                                                        email={clientAvatarEmail || null}
                                                        firstName={resolvedClientIdentity.firstName ?? null}
                                                        lastName={resolvedClientIdentity.lastName ?? null}
                                                        name={clientAvatarName || null}
                                                        photoURL={clientAvatarPhotoURL ?? null}
                                                        size={38}
                                                        disableNavigation
                                                        implicitSelfFallback={Boolean(clientAvatarUid || clientAvatarEmail)}
                                                    />
                                                    <ClientIdentityName>{clientName}</ClientIdentityName>
                                                </ClientIdentityValue>
                                            )}
                                            {!readOnly ? (
                                                <SaveStatus role="status" aria-live="polite">
                                                    {saveState === 'saving' ? 'Saving...'
                                                        : saveState === 'saved' ? 'Saved'
                                                            : saveState === 'error' ? 'Save failed'
                                                                : ''}
                                                </SaveStatus>
                                            ) : null}
                                        </LeftControls>
                                        <RightControls ref={rightRef}>
                                            <HeaderMetricsStack aria-label="Ticket summary metrics">
                                                {showHeaderDetailsSkeleton ? (
                                                    <>
                                                        <DrawerSkeletonLine $width="118px" $height="12px" />
                                                        <DrawerSkeletonLine $width="124px" $height="12px" />
                                                    </>
                                                ) : (
                                                    <>
                                                        <HeaderMetricLine>{`Estimate time: ${estimatedHours || '--'}`}</HeaderMetricLine>
                                                        <HeaderMetricLine>{`Active time: ${activeTimeLabel}`}</HeaderMetricLine>
                                                    </>
                                                )}
                                            </HeaderMetricsStack>
                                            <CreatedHeaderMeta aria-label="Created timestamp">
                                                {ticketNumberLabel ? (
                                                    <TicketNumberBadge>{ticketNumberLabel}</TicketNumberBadge>
                                                ) : null}
                                                <CreatedHeaderValue>
                                                    {showHeaderDetailsSkeleton ? (
                                                        <DrawerSkeletonLine $width="132px" $height="12px" />
                                                    ) : (
                                                        createdOn
                                                    )}
                                                </CreatedHeaderValue>
                                            </CreatedHeaderMeta>
                                            {!readOnly && (
                                                <>
                                            <input
                                                ref={fileRef}
                                                type="file"
                                                style={{ display: 'none' }}
                                                onChange={handleFilePicked}
                                            />
                                            <IconBtn
                                                onClick={() => setShowMenu(s => !s)}
                                                title="More actions"
                                                aria-haspopup="menu"
                                                aria-expanded={showMenu ? 'true' : 'false'}
                                            >
                                                ...
                                            </IconBtn>

                                            {showMenu && (
                                                <MenuPopover ref={menuRef} role="menu" aria-label="Ticket actions">
                                                    <MenuItem onClick={() => { setShowMenu(false); handleMarkComplete(); }}>
                                                        <MenuIcon aria-hidden>v</MenuIcon>
                                                        <div className="text">Mark complete</div>
                                                    </MenuItem>

                                                    {canDelete && <MenuDivider />}

                                                    {canDelete && (
                                                        <MenuItem $danger onClick={() => { setShowMenu(false); setConfirmDelete(true); }}>
                                                            <MenuIcon aria-hidden>x</MenuIcon>
                                                            <div className="text">Delete ticket</div>
                                                        </MenuItem>
                                                    )}

                                                    {canUnassignSelf && (
                                                        <>
                                                            <MenuDivider />
                                                            <MenuItem onClick={unassignSelf}>
                                                                <MenuIcon aria-hidden>{'<'}</MenuIcon>
                                                                <div className="text">Return to global queue</div>
                                                            </MenuItem>
                                                        </>
                                                    )}

                                                </MenuPopover>
                                            )}

                                            {confirmDelete && (
                                                <ConfirmPopover ref={confirmRef} role="dialog" aria-label="Confirm delete">
                                                    <div style={{ fontWeight: 900, color: '#E8EAED' }}>Delete this ticket?</div>
                                                    <div style={{ color: '#9AA4B2', fontSize: 12, fontWeight: 800 }}>
                                                        This action can't be undone.
                                                    </div>
                                                    <ConfirmActions>
                                                        <SecondaryBtn onClick={() => setConfirmDelete(false)}>Cancel</SecondaryBtn>
                                                        <DangerBtn onClick={performDelete}>Delete</DangerBtn>
                                                    </ConfirmActions>
                                                </ConfirmPopover>
                                            )}
                                                </>
                                            )}
                                        </RightControls>

                                    </ControlsBar>
                                )}
                                {canAssignSelf && (
                                    <ControlsBar>
                                        <LeftControls>
                                            <GhostBtn onClick={() => ticket?.id && onAssignSelf?.(ticket.id)}>
                                                Assign to me
                                            </GhostBtn>
                                        </LeftControls>
                                    </ControlsBar>
                                )}


                                <HeaderBar>
                                    <TitleInput
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                        placeholder="Give your ticket a clear name"
                                        aria-label="Ticket title"
                                        disabled={readOnly}
                                    />
                                </HeaderBar>



                                <MetaGrid>
                                    <AssignCell ref={assignCellRef}>
                                        <label>Assign organization member</label>
                                        {readOnly || !canEditOrganizationAssignee ? (
                                            assignedOrgMemberIdentity ? (
                                                <AssignmentIdentityValue>
                                                    <PortalAvatar
                                                        uid={assignedOrgMemberIdentity.id}
                                                        email={assignedOrgMemberIdentity.email}
                                                        name={assignedOrgMemberIdentity.name}
                                                        photoURL={assignedOrgMemberIdentity.photoURL}
                                                        size={38}
                                                        disableNavigation
                                                    />
                                                    <AssignmentIdentityName>{assignedOrgMemberIdentity.name}</AssignmentIdentityName>
                                                </AssignmentIdentityValue>
                                            ) : (
                                                <EmptyAssigneeAvatar>+</EmptyAssigneeAvatar>
                                            )
                                        ) : (
                                            <>
                                                <AgentAssignTrigger
                                                    type="button"
                                                    aria-label="Assign organization member"
                                                    title={assignedOrgMemberIdentity?.name || 'Assign organization member'}
                                                    onClick={() => setShowOrgAssigneeMenu((prev) => !prev)}
                                                >
                                                    {assignedOrgMemberIdentity ? (
                                                        <AssignmentIdentityValue>
                                                            <PortalAvatar
                                                                uid={assignedOrgMemberIdentity.id}
                                                                email={assignedOrgMemberIdentity.email}
                                                                name={assignedOrgMemberIdentity.name}
                                                                photoURL={assignedOrgMemberIdentity.photoURL}
                                                                size={38}
                                                                disableNavigation
                                                            />
                                                            <AssignmentIdentityName>{assignedOrgMemberIdentity.name}</AssignmentIdentityName>
                                                        </AssignmentIdentityValue>
                                                    ) : (
                                                        <EmptyAssigneeAvatar>+</EmptyAssigneeAvatar>
                                                    )}
                                                </AgentAssignTrigger>
                                                {showOrgAssigneeMenu ? (
                                                    <AgentAssignMenu role="listbox" aria-label="Assign organization member">
                                                        <AgentAssignList>
                                                            <AgentAssignItem
                                                                type="button"
                                                                $active={!assignedOrgMember}
                                                                onClick={() => setAssignedOrganizationMember(null)}
                                                            >
                                                                <EmptyAssigneeAvatar>+</EmptyAssigneeAvatar>
                                                                <AgentAssignName>Unassigned</AgentAssignName>
                                                            </AgentAssignItem>
                                                            {filteredAgents.map((agent) => (
                                                                <AgentAssignItem
                                                                    key={agent.id}
                                                                    type="button"
                                                                    $active={assignedOrgMember === agent.id}
                                                                    onClick={() => setAssignedOrganizationMember(agent.id)}
                                                                >
                                                                    <PortalAvatar
                                                                        uid={agent.id}
                                                                        email={agent.email ?? null}
                                                                        name={agent.name}
                                                                        photoURL={agent.photoURL ?? null}
                                                                        size={38}
                                                                        disableNavigation
                                                                    />
                                                                    <AgentAssignName>{agent.name}</AgentAssignName>
                                                                </AgentAssignItem>
                                                            ))}
                                                        </AgentAssignList>
                                                    </AgentAssignMenu>
                                                ) : null}
                                            </>
                                        )}
                                    </AssignCell>

                                    <AgentAssignCell ref={agentAssignCellRef}>
                                        <label>Assign agent/admin</label>
                                        {readOnly || !canEditInternalAssignee ? (
                                            assignedAgentIdentity ? (
                                                <AssignmentIdentityValue>
                                                    <PortalAvatar
                                                        uid={assignedAgentIdentity.id}
                                                        email={assignedAgentIdentity.email}
                                                        name={assignedAgentIdentity.name}
                                                        photoURL={assignedAgentIdentity.photoURL}
                                                        size={38}
                                                        disableNavigation
                                                    />
                                                    <AssignmentIdentityName>{assignedAgentIdentity.name}</AssignmentIdentityName>
                                                </AssignmentIdentityValue>
                                            ) : (
                                                <StaticValue>--</StaticValue>
                                            )
                                        ) : (
                                            <>
                                                <AgentAssignTrigger
                                                    type="button"
                                                    aria-label={assignmentMenuLabel}
                                                    title={assignedAgentIdentity?.name || assignmentMenuLabel}
                                                    onClick={() => setShowAssignedAgentMenu((prev) => !prev)}
                                                >
                                                    {assignedAgentIdentity ? (
                                                        <AssignmentIdentityValue>
                                                            <PortalAvatar
                                                                uid={assignedAgentIdentity.id}
                                                                email={assignedAgentIdentity.email}
                                                                name={assignedAgentIdentity.name}
                                                                photoURL={assignedAgentIdentity.photoURL}
                                                                size={38}
                                                                disableNavigation
                                                            />
                                                            <AssignmentIdentityName>{assignedAgentIdentity.name}</AssignmentIdentityName>
                                                        </AssignmentIdentityValue>
                                                    ) : (
                                                        <EmptyAssigneeAvatar>+</EmptyAssigneeAvatar>
                                                    )}
                                                </AgentAssignTrigger>
                                                {showAssignedAgentMenu ? (
                                                    <AgentAssignMenu role="listbox" aria-label={assignmentMenuLabel}>
                                                        <AgentAssignList>
                                                            <AgentAssignItem
                                                                type="button"
                                                                $active={!assigned}
                                                                onClick={() => setAssignedAgent(null)}
                                                            >
                                                                <EmptyAssigneeAvatar>+</EmptyAssigneeAvatar>
                                                                <AgentAssignName>Unassigned</AgentAssignName>
                                                            </AgentAssignItem>
                                                            {filteredInternalTeamAssignees.map((agent) => (
                                                                <AgentAssignItem
                                                                    key={agent.id}
                                                                    type="button"
                                                                    $active={assigned === agent.id}
                                                                    onClick={() => setAssignedAgent(agent.id)}
                                                                >
                                                                    <PortalAvatar
                                                                        uid={agent.id}
                                                                        email={agent.email ?? null}
                                                                        name={agent.name}
                                                                        photoURL={agent.photoURL ?? null}
                                                                        size={38}
                                                                        disableNavigation
                                                                    />
                                                                    <AgentAssignName>{agent.name}</AgentAssignName>
                                                                </AgentAssignItem>
                                                            ))}
                                                        </AgentAssignList>
                                                    </AgentAssignMenu>
                                                ) : null}
                                            </>
                                        )}
                                    </AgentAssignCell>


                                    <MetaCell>
                                        <label>Ticket status</label>
                                        {managedWorkflowFieldsReadOnly ? (
                                            <StaticValue>{statusLabel(status)}</StaticValue>
                                        ) : (
                                            <Select
                                                value={status}
                                                onChange={(event) => void onStatusChange(event.target.value as TicketStatus)}
                                                aria-label="Ticket status"
                                            >
                                                {statusOptions.map((option) => (
                                                    <option key={option} value={option}>
                                                        {statusLabel(option)}
                                                    </option>
                                                ))}
                                            </Select>
                                        )}
                                    </MetaCell>


                                    {/* Priority (interactive when editable) */}
                                    <MetaCell>
                                        <label>Priority</label>
                                        {managedWorkflowFieldsReadOnly || !canEditPriority ? (
                                            <StaticValue>{priorityLabel(priority)}</StaticValue>
                                        ) : (
                                            <Select
                                                value={priority}
                                                onChange={e => handlePriorityChange(e.target.value as UiPriority)}
                                                aria-label="Priority"
                                            >
                                                <option value="low">Low</option>
                                                <option value="medium">Medium</option>
                                                <option value="high">High</option>
                                                <option value="urgent">Urgent</option>
                                            </Select>
                                        )}
                                    </MetaCell>

                                    <MetaCell>
                                        <label>Milestone</label>
                                        {managedWorkflowFieldsReadOnly ? (
                                            <StaticValue>{milestoneLabel}</StaticValue>
                                        ) : (
                                            <Select
                                                value={selectedMilestoneId}
                                                onChange={(event) => handleMilestoneChange(event.target.value)}
                                                aria-label="Milestone"
                                            >
                                                <option value="">Unassigned</option>
                                                {milestoneOptions.map((option) => (
                                                    <option key={option.id} value={option.id}>
                                                        {option.title}
                                                    </option>
                                                ))}
                                            </Select>
                                        )}
                                    </MetaCell>


                                    {/* Project */}
                                    <MetaCell>
                                        <label>Project</label>
                                        {projects?.length && !managedWorkflowFieldsReadOnly ? (
                                            <Select value={projectId} onChange={e => handleProjectChange(e.target.value)}>
                                                {projects.map(p => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </Select>
                                        ) : (
                                            <StaticValue>{projectLabel}</StaticValue>
                                        )}
                                    </MetaCell>

                                    <MetaCell>
                                        <label>Team</label>
                                        {showTeamSkeleton ? (
                                            <StaticValue>
                                                <DrawerSkeletonLine $width="72%" />
                                            </StaticValue>
                                        ) : canEditOwningTeam && teamOptions.length > 1 ? (
                                            <Select
                                                value={owningTeamNodeId}
                                                onChange={(event) => handleOwningTeamChange(event.target.value)}
                                                aria-label="Owning team"
                                            >
                                                <option value="">Select team</option>
                                                {teamOptions.map((option) => (
                                                    <option key={option.id} value={option.id}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        ) : (
                                            <StaticValue>{effectiveTeamLabel}</StaticValue>
                                        )}
                                    </MetaCell>

                                    <MetaCell>
                                        <label>Start date</label>
                                        {readOnly || !canEditScheduleWindow ? (
                                            <StaticValue>{startDate || '--'}</StaticValue>
                                        ) : (
                                            <DateInput
                                                type="date"
                                                value={startDate}
                                                onChange={(e) => handleStartDateChange(e.target.value)}
                                                aria-label="Start date"
                                            />
                                        )}
                                    </MetaCell>

                                    <MetaCell>
                                        <label>Due date</label>
                                        {readOnly || !canEditScheduleWindow ? (
                                            <StaticValue>{dueDate || '--'}</StaticValue>
                                        ) : (
                                            <DateInput
                                                type="date"
                                                value={dueDate}
                                                min={startDate || undefined}
                                                onChange={(e) => handleDueDateChange(e.target.value)}
                                                aria-label="Due date"
                                            />
                                        )}
                                    </MetaCell>
                                </MetaGrid>

                                <RoutingFieldsGrid>
                                    <MetaCell>
                                        <label>Service / tier</label>
                                        {showSubscriptionSkeleton ? (
                                            <StaticValue>
                                                <DrawerSkeletonLine $width="88%" />
                                            </StaticValue>
                                        ) : readOnly ? (
                                            <StaticValue>{subscriptionDisplayLabel}</StaticValue>
                                        ) : effectiveSubscriptionOptions.length > 1 ? (
                                            <Select
                                                value={clientSubscriptionId}
                                                onChange={(event) => handleSubscriptionChange(event.target.value)}
                                                aria-label="Service or tier"
                                            >
                                                <option value="">Select service / tier</option>
                                                {effectiveSubscriptionOptions.map((option) => (
                                                    <option key={option.id} value={option.id}>
                                                        {formatSubscriptionLabel(option)}
                                                    </option>
                                                ))}
                                            </Select>
                                        ) : effectiveSubscriptionOptions.length === 1 ? (
                                            <StaticValue>{formatSubscriptionLabel(effectiveSubscriptionOptions[0])}</StaticValue>
                                        ) : (
                                            <StaticValue>--</StaticValue>
                                        )}
                                        {subscriptionResolutionError ? (
                                            <FieldHint $error>{subscriptionResolutionError}</FieldHint>
                                        ) : subscriptionSelectionRequired ? (
                                            <FieldHint $error={!clientSubscriptionId}>
                                                {!clientSubscriptionId
                                                    ? 'Required. Select the active service / tier for this ticket.'
                                                    : 'Active service / tier selected.'}
                                            </FieldHint>
                                        ) : null}
                                    </MetaCell>

                                    <MetaCell>
                                        <label>Request category</label>
                                        {showOfferingSkeleton ? (
                                            <StaticValue>
                                                <DrawerSkeletonLine $width="76%" />
                                            </StaticValue>
                                        ) : managedWorkflowFieldsReadOnly ? (
                                            <StaticValue>{requestCategoryDisplayLabel}</StaticValue>
                                        ) : effectiveOfferingOptions.length > 1 ? (
                                            <Select
                                                value={offeringId}
                                                onChange={(event) => handleOfferingChange(event.target.value)}
                                                aria-label="Request category"
                                            >
                                                <option value="">Select request category</option>
                                                {effectiveOfferingOptions.map((option) => (
                                                    <option key={option.id} value={option.id}>
                                                        {option.name}
                                                    </option>
                                                ))}
                                            </Select>
                                        ) : effectiveOfferingOptions.length === 1 ? (
                                            <StaticValue>{effectiveOfferingOptions[0].name}</StaticValue>
                                        ) : (
                                            <StaticValue>--</StaticValue>
                                        )}
                                        {offeringSelectionRequired ? (
                                            <FieldHint $error={!offeringId}>
                                                {!offeringId
                                                    ? 'Required. Select the contracted request category for this ticket.'
                                                    : ''}
                                            </FieldHint>
                                        ) : null}
                                    </MetaCell>

                                    <MetaCell>
                                        <label>Request subcategory</label>
                                        {showOfferingSkeleton ? (
                                            <StaticValue>
                                                <DrawerSkeletonLine $width="68%" />
                                            </StaticValue>
                                        ) : managedWorkflowFieldsReadOnly ? (
                                            <StaticValue>{requestSubcategoryDisplayLabel}</StaticValue>
                                        ) : (selectedOfferingSubcategories.length > 0 || offeringSubcategoryId) ? (
                                            <Select
                                                value={offeringSubcategoryId}
                                                onChange={(event) => handleOfferingSubcategoryChange(event.target.value)}
                                                aria-label="Offering subcategory"
                                            >
                                                <option value="">Select subcategory</option>
                                                {selectedOfferingSubcategories.map((subcategory) => (
                                                    <option key={subcategory.id} value={subcategory.id}>
                                                        {subcategory.name}
                                                    </option>
                                                ))}
                                            </Select>
                                        ) : (
                                            <StaticValue>--</StaticValue>
                                        )}
                                        {requestSubcategoryDescription ? (
                                            <FieldHint>
                                                {requestSubcategoryDescription}
                                            </FieldHint>
                                        ) : null}
                                    </MetaCell>
                                </RoutingFieldsGrid>

                                {relationshipOptions.length > 0 || (ticket?.blocks?.length ?? 0) > 0 || (ticket?.handoffFrom?.length ?? 0) > 0 ? (
                                    <RelationshipFieldsGrid>
                                        {/* Temporarily hidden to reduce crowding in the ticket drawer.
                                        <MetaCell>
                                            <label>Blocked by</label>
                                            {readOnly ? (
                                                selectedBlockedByRelationships.length ? (
                                                    <RelationshipPillList>
                                                        {selectedBlockedByRelationships.map((entry) => (
                                                            <RelationshipPill key={`blocked-by-${entry.id}`}>
                                                                {formatRelationshipOptionTitle(entry)}
                                                            </RelationshipPill>
                                                        ))}
                                                    </RelationshipPillList>
                                                ) : (
                                                    <StaticValue>None</StaticValue>
                                                )
                                            ) : relationshipOptions.length ? (
                                                <RelationshipChooser>
                                                    <RelationshipList>
                                                        {relationshipOptions.map((entry) => (
                                                            <RelationshipOption
                                                                key={`blocked-by-option-${entry.id}`}
                                                                type="button"
                                                                $active={blockedByTicketIds.includes(entry.id)}
                                                                onClick={() => toggleBlockedByTicketId(entry.id)}
                                                            >
                                                                <RelationshipTitle>{formatRelationshipOptionTitle(entry)}</RelationshipTitle>
                                                                <RelationshipMeta>{formatRelationshipOptionMeta(entry) || 'Same project ticket'}</RelationshipMeta>
                                                            </RelationshipOption>
                                                        ))}
                                                    </RelationshipList>
                                                    <FieldHint>
                                                        Select tickets that must finish before this work can start.
                                                    </FieldHint>
                                                </RelationshipChooser>
                                            ) : (
                                                <StaticValue>No other tickets in this project yet.</StaticValue>
                                            )}
                                        </MetaCell>

                                        <MetaCell>
                                            <label>Handoff to</label>
                                            {readOnly ? (
                                                selectedHandoffToRelationships.length ? (
                                                    <RelationshipPillList>
                                                        {selectedHandoffToRelationships.map((entry) => (
                                                            <RelationshipPill key={`handoff-to-${entry.id}`}>
                                                                {formatRelationshipOptionTitle(entry)}
                                                            </RelationshipPill>
                                                        ))}
                                                    </RelationshipPillList>
                                                ) : (
                                                    <StaticValue>None</StaticValue>
                                                )
                                            ) : relationshipOptions.length ? (
                                                <RelationshipChooser>
                                                    <RelationshipList>
                                                        {relationshipOptions.map((entry) => (
                                                            <RelationshipOption
                                                                key={`handoff-to-option-${entry.id}`}
                                                                type="button"
                                                                $active={handoffToTicketIds.includes(entry.id)}
                                                                onClick={() => toggleHandoffToTicketId(entry.id)}
                                                            >
                                                                <RelationshipTitle>{formatRelationshipOptionTitle(entry)}</RelationshipTitle>
                                                                <RelationshipMeta>{formatRelationshipOptionMeta(entry) || 'Same project ticket'}</RelationshipMeta>
                                                            </RelationshipOption>
                                                        ))}
                                                    </RelationshipList>
                                                    <FieldHint>
                                                        Select downstream tickets that should pick up once this work lands.
                                                    </FieldHint>
                                                </RelationshipChooser>
                                            ) : (
                                                <StaticValue>No other tickets in this project yet.</StaticValue>
                                            )}
                                        </MetaCell>
                                        */}

                                        {mode === 'edit' && (ticket?.blocks?.length ?? 0) > 0 ? (
                                            <MetaCell>
                                                <label>Blocking</label>
                                                <RelationshipPillList>
                                                    {(ticket?.blocks ?? []).map((entry) => (
                                                        <RelationshipPill
                                                            key={`blocks-${entry.id}`}
                                                            title={formatRelationshipOptionMeta(entry) || undefined}
                                                        >
                                                            {formatRelationshipOptionTitle(entry)}
                                                        </RelationshipPill>
                                                    ))}
                                                </RelationshipPillList>
                                            </MetaCell>
                                        ) : null}

                                        {mode === 'edit' && (ticket?.handoffFrom?.length ?? 0) > 0 ? (
                                            <MetaCell>
                                                <label>Receiving handoff from</label>
                                                <RelationshipPillList>
                                                    {(ticket?.handoffFrom ?? []).map((entry) => (
                                                        <RelationshipPill
                                                            key={`handoff-from-${entry.id}`}
                                                            title={formatRelationshipOptionMeta(entry) || undefined}
                                                        >
                                                            {formatRelationshipOptionTitle(entry)}
                                                        </RelationshipPill>
                                                    ))}
                                                </RelationshipPillList>
                                            </MetaCell>
                                        ) : null}
                                    </RelationshipFieldsGrid>
                                ) : null}

                                    {isStaff ? (
                                    <KBaseProcessRow>
                                        <MetaCell>
                                            <label>KBase process</label>
                                            <KBaseFinder ref={kbaseFinderRef}>
                                                <KBaseTrigger
                                                    type="button"
                                                    $open={kbaseMenuOpen}
                                                    onClick={() => setKbaseMenuOpen((current) => !current)}
                                                    aria-haspopup="listbox"
                                                    aria-expanded={kbaseMenuOpen}
                                                    aria-label="Search KBase processes"
                                                >
                                                    <KBaseTriggerValue $placeholder={!selectedKBaseProcess}>
                                                        {selectedKBaseProcess?.title || 'Search KBase processes'}
                                                    </KBaseTriggerValue>
                                                </KBaseTrigger>
                                                {kbaseMenuOpen ? (
                                                    <KBasePopover role="dialog" aria-label="KBase process search">
                                                        <KBaseSearchInput
                                                            ref={kbaseSearchInputRef}
                                                            value={kbaseSearchQuery}
                                                            onChange={(event) => setKbaseSearchQuery(event.target.value)}
                                                            placeholder="Search KBase processes"
                                                            aria-label="Search KBase processes"
                                                        />
                                                        <KBaseResults>
                                                            {!cleanText(kbaseSearchQuery) ? (
                                                                <>
                                                                    {exactScopeKBaseProcesses.length > 0 ? (
                                                                        <KBaseResultSection>
                                                                            <KBaseResultHeading>{kbaseSuggestionScopeLabel}</KBaseResultHeading>
                                                                            {exactScopeKBaseProcesses.map((process) => (
                                                                                <KBaseResultButton
                                                                                    key={`suggested-${process.id}`}
                                                                                    type="button"
                                                                                    $active={selectedKbaseProcessId === process.id}
                                                                                    onClick={() => selectKBaseProcess(process.id)}
                                                                                >
                                                                                    <KBaseResultTop>
                                                                                        <KBaseResultTitle>{process.title}</KBaseResultTitle>
                                                                                        <KBaseResultBadge>{process.status}</KBaseResultBadge>
                                                                                    </KBaseResultTop>
                                                                                    <KBaseResultMeta>
                                                                                        {process.summary || process.category}
                                                                                    </KBaseResultMeta>
                                                                                </KBaseResultButton>
                                                                            ))}
                                                                        </KBaseResultSection>
                                                                    ) : null}
                                                                    {recentKBaseProcesses.length > 0 ? (
                                                                        <KBaseResultSection>
                                                                            <KBaseResultHeading>Recent selections</KBaseResultHeading>
                                                                            {recentKBaseProcesses.map((process) => (
                                                                                <KBaseResultButton
                                                                                    key={`recent-${process.id}`}
                                                                                    type="button"
                                                                                    $active={selectedKbaseProcessId === process.id}
                                                                                    onClick={() => selectKBaseProcess(process.id)}
                                                                                >
                                                                                    <KBaseResultTop>
                                                                                        <KBaseResultTitle>{process.title}</KBaseResultTitle>
                                                                                        <KBaseResultBadge>{process.status}</KBaseResultBadge>
                                                                                    </KBaseResultTop>
                                                                                    <KBaseResultMeta>
                                                                                        {process.processType === 'service'
                                                                                            ? `${process.serviceLabel || 'Service'} / ${process.category}`
                                                                                            : process.tags.length
                                                                                                ? `Internal / ${process.tags.slice(0, 3).join(', ')}`
                                                                                                : 'Internal process'}
                                                                                    </KBaseResultMeta>
                                                                                </KBaseResultButton>
                                                                            ))}
                                                                        </KBaseResultSection>
                                                                    ) : null}
                                                                    {exactScopeKBaseProcesses.length === 0 && recentKBaseProcesses.length === 0 ? (
                                                                        <KBaseEmptyState>
                                                                            No suggested or recent processes yet. Start typing to search all processes.
                                                                        </KBaseEmptyState>
                                                                    ) : null}
                                                                </>
                                                            ) : searchedKBaseProcesses.length > 0 ? (
                                                                <KBaseResultSection>
                                                                    <KBaseResultHeading>Search results</KBaseResultHeading>
                                                                    {searchedKBaseProcesses.map((process) => (
                                                                        <KBaseResultButton
                                                                            key={`search-${process.id}`}
                                                                            type="button"
                                                                            $active={selectedKbaseProcessId === process.id}
                                                                            onClick={() => selectKBaseProcess(process.id)}
                                                                        >
                                                                            <KBaseResultTop>
                                                                                <KBaseResultTitle>{process.title}</KBaseResultTitle>
                                                                                <KBaseResultBadge>{process.status}</KBaseResultBadge>
                                                                            </KBaseResultTop>
                                                                            <KBaseResultMeta>
                                                                                {process.processType === 'service'
                                                                                    ? `${process.serviceLabel || 'Service'} / ${process.category}`
                                                                                    : process.tags.length
                                                                                        ? `Internal / ${process.tags.slice(0, 3).join(', ')}`
                                                                                        : 'Internal process'}
                                                                            </KBaseResultMeta>
                                                                        </KBaseResultButton>
                                                                    ))}
                                                                </KBaseResultSection>
                                                            ) : (
                                                                <KBaseEmptyState>
                                                                    No processes match that search yet.
                                                                </KBaseEmptyState>
                                                            )}
                                                        </KBaseResults>
                                                    </KBasePopover>
                                                ) : null}
                                            </KBaseFinder>
                                            {kbaseProcessesError ? (
                                                <FieldHint $error>{kbaseProcessesError}</FieldHint>
                                            ) : kbaseProcessesLoading && kbaseProcesses.length === 0 ? (
                                                <FieldHint>Loading suggested KBase processes...</FieldHint>
                                            ) : null}
                                        </MetaCell>

                                        <AssistantActionsRow>
                                            <OpenProcessActionButton
                                                type="button"
                                                aria-label="Open KBase process"
                                                disabled={!selectedKBaseProcess}
                                                title={
                                                    selectedKBaseProcess
                                                        ? 'Open selected KBase process'
                                                        : 'Select a KBase process first'
                                                }
                                                onClick={() => {
                                                    if (!selectedKBaseProcess) return;
                                                    setIsKBaseProcessPanelOpen(true);
                                                }}
                                            >
                                                <AssistantButtonIcon aria-hidden="true">
                                                    <svg
                                                        width="18"
                                                        height="18"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M6 4.75C6 4.33579 6.33579 4 6.75 4H18.25C18.6642 4 19 4.33579 19 4.75V17.25C19 17.6642 18.6642 18 18.25 18H6.75C6.33579 18 6 17.6642 6 17.25V4.75Z"
                                                            stroke="currentColor"
                                                            strokeWidth="1.7"
                                                        />
                                                        <path
                                                            d="M9 8H16"
                                                            stroke="currentColor"
                                                            strokeWidth="1.7"
                                                            strokeLinecap="round"
                                                        />
                                                        <path
                                                            d="M9 11.5H16"
                                                            stroke="currentColor"
                                                            strokeWidth="1.7"
                                                            strokeLinecap="round"
                                                        />
                                                        <path
                                                            d="M9 15H13"
                                                            stroke="currentColor"
                                                            strokeWidth="1.7"
                                                            strokeLinecap="round"
                                                        />
                                                    </svg>
                                                </AssistantButtonIcon>
                                                <span>Open Process</span>
                                            </OpenProcessActionButton>
                                        </AssistantActionsRow>
                                    </KBaseProcessRow>
                                    ) : null}



                            </HeaderGrid>
                        </Header>



                        <Body $withComments={mode === 'edit' && !readOnly}>
                            <DescriptionSubtasksSection $withComments={mode === 'edit' && !readOnly}>
                                <Field>
                                    <label>Description</label>
                                    <textarea
                                        value={desc}
                                        onChange={e => setDesc(e.target.value)}
                                        onBlur={handleDescriptionBlur}
                                        placeholder="Describe the goal, acceptance criteria, links, constraints..."
                                        disabled={readOnly}
                                    />
                                </Field>

                                <Field>
                                    <Subtasks>
                                        {subtasks.map((st) => {
                                            const subtaskAssignee = st.assigneeId
                                                ? (assigneeById.get(st.assigneeId) ?? null)
                                                : null;
                                            const isAssigneeMenuOpen = openSubtaskAssigneeId === st.id;
                                            return (
                                                <SubtaskItem
                                                    key={st.id}
                                                    onDragOver={(event) => onSubtaskDragOver(event, st.id)}
                                                    onDrop={(event) => onSubtaskDrop(event, st.id)}
                                                    $dragging={draggingSubtaskId === st.id}
                                                    $dropTarget={subtaskDropTargetId === st.id}
                                                    $done={Boolean(st.done)}
                                                >
                                                    <SubtaskDragHandle
                                                        type="button"
                                                        aria-label="Reorder subtask"
                                                        title="Drag to reorder"
                                                        draggable={!readOnly}
                                                        onDragStart={(event) => onSubtaskDragStart(event, st.id)}
                                                        onDragEnd={onSubtaskDragEnd}
                                                        disabled={readOnly}
                                                    >
                                                        ::
                                                    </SubtaskDragHandle>
                                                    <SubtaskToggle
                                                        type="button"
                                                        $done={Boolean(st.done)}
                                                        onClick={() => toggleSubtask(st.id)}
                                                        aria-label="Toggle subtask completion"
                                                        title={st.done ? 'Mark incomplete' : 'Mark complete'}
                                                        disabled={readOnly}
                                                    >
                                                        {st.done ? 'v' : ''}
                                                    </SubtaskToggle>
                                                    <SubtaskTextInput
                                                        type="text"
                                                        value={st.text}
                                                        placeholder="Describe the subtask"
                                                        onChange={(e) => editSubtask(st.id, e.target.value)}
                                                        disabled={readOnly}
                                                    />
                                                    <SubtaskDateWrap>
                                                        <SubtaskDateTrigger
                                                            type="button"
                                                            $selected={Boolean(st.dueDate)}
                                                            onClick={() => openSubtaskDatePicker(st.id)}
                                                            aria-label={st.dueDate ? `Subtask due ${formatSubtaskDueDateLabel(st.dueDate)}` : 'Subtask due date'}
                                                            title={st.dueDate ? formatSubtaskDueDateLabel(st.dueDate) : 'Set due date'}
                                                            disabled={readOnly}
                                                        >
                                                            {st.dueDate ? (
                                                                <SubtaskDateText>
                                                                    {formatSubtaskDueDateLabel(st.dueDate)}
                                                                </SubtaskDateText>
                                                            ) : (
                                                                <SubtaskDateIconCircle>
                                                                    <svg viewBox="0 0 24 24" aria-hidden="true">
                                                                        <path d="M7 3v3" />
                                                                        <path d="M17 3v3" />
                                                                        <path d="M4 9h16" />
                                                                        <rect x="4" y="5" width="16" height="16" rx="2" ry="2" />
                                                                    </svg>
                                                                </SubtaskDateIconCircle>
                                                            )}
                                                        </SubtaskDateTrigger>
                                                        <HiddenSubtaskDateInput
                                                            ref={(node) => {
                                                                subtaskDateInputRefs.current[st.id] = node;
                                                            }}
                                                            type="date"
                                                            value={st.dueDate ?? ''}
                                                            min={startDate || undefined}
                                                            onChange={(e) => setSubtaskDueDate(st.id, e.target.value)}
                                                            aria-label="Subtask due date picker"
                                                            disabled={readOnly}
                                                        />
                                                    </SubtaskDateWrap>
                                                    <SubtaskAssigneeWrap data-subtask-assignee-root>
                                                        <SubtaskAssigneeTrigger
                                                            type="button"
                                                            aria-label="Assign subtask"
                                                            title={subtaskAssignee?.name || 'Assign team member'}
                                                            onClick={() =>
                                                                setOpenSubtaskAssigneeId((prev) =>
                                                                    prev === st.id ? null : st.id,
                                                                )
                                                            }
                                                            disabled={readOnly}
                                                        >
                                                            {subtaskAssignee ? (
                                                                <PortalAvatar
                                                                    uid={subtaskAssignee.id}
                                                                    email={subtaskAssignee.email ?? null}
                                                                    name={subtaskAssignee.name}
                                                                    photoURL={subtaskAssignee.photoURL ?? null}
                                                                    size={30}
                                                                    disableNavigation
                                                                />
                                                            ) : (
                                                                '+'
                                                            )}
                                                        </SubtaskAssigneeTrigger>
                                                        {!readOnly && isAssigneeMenuOpen ? (
                                                            <SubtaskAssigneeMenu role="listbox" aria-label="Assign subtask member">
                                                                <SubtaskAssigneeItem
                                                                    type="button"
                                                                    $active={!st.assigneeId}
                                                                    onClick={() => {
                                                                        setSubtaskAssignee(st.id, null);
                                                                        setOpenSubtaskAssigneeId(null);
                                                                    }}
                                                                >
                                                                    <EmptyAssigneeAvatar>+</EmptyAssigneeAvatar>
                                                                    <SubtaskAssigneeName>Unassigned</SubtaskAssigneeName>
                                                                </SubtaskAssigneeItem>
                                                                {assigneeAgents.map((agent) => (
                                                                    <SubtaskAssigneeItem
                                                                        key={agent.id}
                                                                        type="button"
                                                                        $active={st.assigneeId === agent.id}
                                                                        onClick={() => {
                                                                            setSubtaskAssignee(st.id, agent.id);
                                                                            setOpenSubtaskAssigneeId(null);
                                                                        }}
                                                                    >
                                                                        <PortalAvatar
                                                                            uid={agent.id}
                                                                            email={agent.email ?? null}
                                                                            name={agent.name}
                                                                            photoURL={agent.photoURL ?? null}
                                                                            size={30}
                                                                            disableNavigation
                                                                        />
                                                                        <SubtaskAssigneeName>{agent.name}</SubtaskAssigneeName>
                                                                    </SubtaskAssigneeItem>
                                                                ))}
                                                            </SubtaskAssigneeMenu>
                                                        ) : null}
                                                    </SubtaskAssigneeWrap>
                                                    {!readOnly ? (
                                                        <SubtaskActionBtn
                                                            type="button"
                                                            $danger
                                                            onClick={() => removeSubtask(st.id)}
                                                            aria-label="Delete subtask"
                                                            title="Delete subtask"
                                                        >
                                                            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                                                <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                                <path d="M8 6V4h8v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                <path d="M8 6l1 14h6l1-14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                                                <path d="M10 10v6M14 10v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                            </svg>
                                                        </SubtaskActionBtn>
                                                    ) : (
                                                        <span />
                                                    )}
                                                </SubtaskItem>
                                            );
                                        })}
                                        {!readOnly && (
                                            <AddSubtaskBtn onClick={addSubtask}>
                                                + Add subtask
                                            </AddSubtaskBtn>
                                        )}
                                    </Subtasks>
                                </Field>
                            </DescriptionSubtasksSection>

                            {mode === 'edit' && !readOnly && (
                                <CommentsField>
                                    <label>Comments</label>
                                    <CommentsArea>
                                        {comments.length === 0 && (
                                            <div className="empty">No comments yet.</div>
                                        )}
                                        {comments.map(c => (
                                            <div key={c.id} className="item">
                                                {(() => {
                                                    const author = getCommentAuthor(c);
                                                    return (
                                                        <>
                                                            <PortalAvatar
                                                                uid={author.uid}
                                                                email={author.email}
                                                                firstName={author.firstName}
                                                                lastName={author.lastName}
                                                                name={author.name}
                                                                photoURL={author.photoURL}
                                                                size={30}
                                                                disableNavigation
                                                            />
                                                            <div className="itemContent">
                                                                <div className="meta">
                                                                    {author.name} - {formatCommentTimestamp(c.createdAt)}
                                                                </div>
                                                                <div className="body">{c.body}</div>
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        ))}
                                    </CommentsArea>
                                </CommentsField>
                            )}
                        </Body>

                        <Footer>
                            {/* Create CTA only for new tickets */}
                            {mode === 'create' && (
                                <PrimaryCta onClick={submit} aria-busy={saving}>
                                    Create ticket
                                </PrimaryCta>
                            )}

                            {/* Edit mode: show save status instead of a button */}
                            {/* Comments composer stays in edit mode */}
                            {mode === 'edit' && (
                                <CommentComposer>
                                    <textarea
                                        placeholder="Write a comment..."
                                        value={commentText}
                                        onChange={e => setCommentText(e.target.value)}
                                    />
                                    <button onClick={sendComment}>Send</button>
                                </CommentComposer>
                            )}

                        </Footer>

                    </Sheet>
                    <AnimatePresence>
                        {isStaff && isKBaseProcessPanelOpen && selectedKBaseProcess ? (
                            <KBaseProcessSheet
                                role="dialog"
                                aria-label="KBase process details"
                                initial={{ x: '100%' }}
                                animate={{ x: 0 }}
                                exit={{ x: '100%' }}
                                transition={{ type: 'spring', stiffness: 260, damping: 28 }}
                            >
                                <KBaseProcessMainPane $aiOpen={isKBaseAiPanelOpen}>
                                <KBaseProcessHeader>
                                    <KBaseProcessHeaderTop>
                                        <div>
                                            <KBaseProcessKicker>KBase process</KBaseProcessKicker>
                                            <KBaseProcessTitle>{selectedKBaseProcess.title}</KBaseProcessTitle>
                                        </div>
                                        <KBaseProcessCloseButton
                                            type="button"
                                            onClick={() => {
                                                setIsKBaseProcessPanelOpen(false);
                                                setIsKBaseAiPanelOpen(false);
                                            }}
                                            aria-label="Close KBase process panel"
                                        >
                                            x
                                        </KBaseProcessCloseButton>
                                    </KBaseProcessHeaderTop>
                                    <KBaseProcessMetaText>
                                        {selectedKBaseProcess.processType === 'service'
                                            ? [
                                                selectedKBaseProcess.serviceLabel || 'Service',
                                                selectedKBaseProcess.category,
                                                selectedKBaseProcess.subcategories[0] || '',
                                            ]
                                                .filter(Boolean)
                                                .join(' / ')
                                            : selectedKBaseProcess.tags.length
                                                ? `Internal process / ${selectedKBaseProcess.tags.map((tag) => `#${tag}`).join(', ')}`
                                                : 'Internal process'}
                                    </KBaseProcessMetaText>
                                </KBaseProcessHeader>
                                <KBaseProcessBody>
                                    {selectedKBaseProcess.summary ? (
                                        <KBaseProcessSection>
                                            <KBaseProcessSectionTitle>Summary</KBaseProcessSectionTitle>
                                            <KBaseProcessParagraph>{selectedKBaseProcess.summary}</KBaseProcessParagraph>
                                        </KBaseProcessSection>
                                    ) : null}
                                    {selectedKBaseProcess.objective ? (
                                        <KBaseProcessSection>
                                            <KBaseProcessSectionTitle>Objective</KBaseProcessSectionTitle>
                                            <KBaseProcessParagraph>{selectedKBaseProcess.objective}</KBaseProcessParagraph>
                                        </KBaseProcessSection>
                                    ) : null}
                                    {selectedKBaseProcess.scope ? (
                                        <KBaseProcessSection>
                                            <KBaseProcessSectionTitle>Scope</KBaseProcessSectionTitle>
                                            <KBaseProcessParagraph>{selectedKBaseProcess.scope}</KBaseProcessParagraph>
                                        </KBaseProcessSection>
                                    ) : null}
                                    {renderKBaseProcessListSection('Prerequisites', selectedKBaseProcess.prerequisites)}
                                    {renderKBaseProcessListSection('Required inputs', selectedKBaseProcess.requiredInputs)}
                                    {renderKBaseProcessListSection('Expected outputs', selectedKBaseProcess.expectedOutputs)}
                                    {renderKBaseProcessListSection('Tools and platforms', selectedKBaseProcess.toolsAndPlatforms)}
                                    {renderKBaseProcessListSection('Ordered steps', selectedKBaseProcess.orderedSteps)}
                                    {renderKBaseProcessListSection('QA checklist', selectedKBaseProcess.qaChecklist)}
                                    {renderKBaseProcessListSection('Definition of done', selectedKBaseProcess.definitionOfDone)}
                                    {renderKBaseProcessListSection('Hard rules', selectedKBaseProcess.hardRules)}
                                    {renderKBaseProcessListSection('Do not do', selectedKBaseProcess.doNotDo)}
                                    {renderKBaseProcessListSection('Approval requirements', selectedKBaseProcess.approvalRequirements)}
                                    {renderKBaseProcessListSection('Escalation triggers', selectedKBaseProcess.escalationTriggers)}
                                    {renderKBaseProcessListSection('Handoff rules', selectedKBaseProcess.handoffRules)}
                                    {renderKBaseProcessListSection('Risk notes', selectedKBaseProcess.riskNotes)}
                                    {selectedKBaseProcess.bodyHtml ? (
                                        <KBaseProcessSection>
                                            <KBaseProcessSectionTitle>Process body</KBaseProcessSectionTitle>
                                            <KBaseProcessRichText
                                                dangerouslySetInnerHTML={{ __html: selectedKBaseProcess.bodyHtml }}
                                            />
                                        </KBaseProcessSection>
                                    ) : null}
                                    {selectedKBaseProcess.revisions.length ? (
                                        <KBaseProcessSection>
                                            <KBaseProcessSectionTitle>Recent revisions</KBaseProcessSectionTitle>
                                            <KBaseProcessList as="ul">
                                                {selectedKBaseProcess.revisions.slice(0, 5).map((revision) => (
                                                    <li key={revision.id}>
                                                        {[revision.author || 'Unknown', revision.changedAt || '', revision.summary || 'Updated process']
                                                            .filter(Boolean)
                                                            .join(' • ')}
                                                    </li>
                                                ))}
                                            </KBaseProcessList>
                                        </KBaseProcessSection>
                                    ) : null}
                                </KBaseProcessBody>
                                    {!isKBaseAiPanelOpen ? (
                                        <KBaseProcessFooter>
                                            <KBaseFooterActionButton
                                                type="button"
                                                onClick={() => setIsKBaseAiPanelOpen(true)}
                                                aria-label="Use AI Assistant"
                                                title="Open AI assistant"
                                            >
                                                <AssistantButtonIcon aria-hidden="true">
                                                    <svg
                                                        width="18"
                                                        height="18"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M12 3l1.6 4.8L18 9.4l-4.4 1.6L12 16l-1.6-5-4.4-1.6 4.4-1.6L12 3z"
                                                            stroke="currentColor"
                                                            strokeWidth="1.8"
                                                            strokeLinejoin="round"
                                                            fill="currentColor"
                                                            fillOpacity="0.16"
                                                        />
                                                        <path d="M19 4l.5 1.5L21 6l-1.5.5L19 8l-.5-1.5L17 6l1.5-.5L19 4z" fill="currentColor" />
                                                        <path d="M5 15l.5 1.5L7 17l-1.5.5L5 19l-.5-1.5L3 17l1.5-.5L5 15z" fill="currentColor" />
                                                    </svg>
                                                </AssistantButtonIcon>
                                                <span>Use AI Assistant</span>
                                            </KBaseFooterActionButton>
                                        </KBaseProcessFooter>
                                    ) : null}
                                </KBaseProcessMainPane>
                                <KBaseAiPane $open={isKBaseAiPanelOpen}>
                                    <KBaseAiHeader>
                                        <KBaseAiTitleGroup>
                                            <KBaseAiBotIcon aria-hidden="true">
                                                <svg
                                                    width="18"
                                                    height="18"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <rect x="7" y="8" width="10" height="9" rx="3" stroke="currentColor" strokeWidth="1.8" />
                                                    <path d="M12 4v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                                    <path d="M9 18v1M15 18v1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                                    <circle cx="10.5" cy="12.5" r="1" fill="currentColor" />
                                                    <circle cx="13.5" cy="12.5" r="1" fill="currentColor" />
                                                    <path d="M10 15h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                                                </svg>
                                            </KBaseAiBotIcon>
                                            <KBaseAiTitle>AI Assistant</KBaseAiTitle>
                                        </KBaseAiTitleGroup>
                                        <KBaseAiHeaderActions ref={kBaseAiActionsRef}>
                                            <KBaseAiActionsButton
                                                type="button"
                                                onClick={() => setIsKBaseAiActionsOpen((current) => !current)}
                                                aria-label="AI assistant actions"
                                                title="AI assistant actions"
                                            >
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    aria-hidden="true"
                                                >
                                                    <circle cx="5" cy="12" r="1.8" fill="currentColor" />
                                                    <circle cx="12" cy="12" r="1.8" fill="currentColor" />
                                                    <circle cx="19" cy="12" r="1.8" fill="currentColor" />
                                                </svg>
                                            </KBaseAiActionsButton>
                                            {isKBaseAiActionsOpen ? (
                                                <KBaseAiActionsMenu>
                                                    <KBaseAiActionItem
                                                        type="button"
                                                        onClick={clearKBaseAiConversation}
                                                        disabled={kBaseAiSending || kBaseAiLoading || kBaseAiClearing}
                                                        $danger
                                                    >
                                                        <span>{kBaseAiClearing ? 'Clearing chat...' : 'Clear chat'}</span>
                                                        <svg
                                                            width="14"
                                                            height="14"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            aria-hidden="true"
                                                        >
                                                            <path
                                                                d="M6 7h12M9 7V5h6v2m-7 3v7m4-7v7m4-7v7M8 7l.5 12a2 2 0 0 0 2 1.9h3a2 2 0 0 0 2-1.9L16 7"
                                                                stroke="currentColor"
                                                                strokeWidth="1.8"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </KBaseAiActionItem>
                                                </KBaseAiActionsMenu>
                                            ) : null}
                                            <KBaseAiCollapseButton
                                                type="button"
                                                onClick={() => {
                                                    setIsKBaseAiActionsOpen(false);
                                                    setIsKBaseAiPanelOpen(false);
                                                }}
                                                aria-label="Collapse AI assistant"
                                                title="Collapse AI assistant"
                                            >
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        d="M15 6l-6 6 6 6"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </KBaseAiCollapseButton>
                                        </KBaseAiHeaderActions>
                                    </KBaseAiHeader>
                                    <KBaseAiBody>
                                        {kBaseAiLoading && kBaseAiMessages.length === 0 ? (
                                            <KBaseAiEmptyState>Loading conversation...</KBaseAiEmptyState>
                                        ) : null}
                                        {!kBaseAiLoading && kBaseAiMessages.length === 0 ? (
                                            <KBaseAiEmptyState>
                                                Open the AI assistant to load a process-scoped ticket summary.
                                            </KBaseAiEmptyState>
                                        ) : null}
                                        {kBaseAiMessages.map((message) => (
                                            <KBaseAiMessage
                                                key={message.id}
                                                $assistant={message.role === 'assistant'}
                                                $pending={message.pending}
                                            >
                                                {message.body}
                                            </KBaseAiMessage>
                                        ))}
                                    </KBaseAiBody>
                                    <KBaseAiComposer>
                                        <input
                                            ref={kBaseAiFileInputRef}
                                            type="file"
                                            multiple
                                            hidden
                                            accept=".png,.jpg,.jpeg,.webp,.pdf,.txt,.csv,.md,.json,.docx,image/png,image/jpeg,image/webp,application/pdf,text/plain,text/csv,application/json,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                            onChange={handleKBaseAiFilesSelected}
                                        />
                                        {kBaseAiAttachments.length ? (
                                            <KBaseAiAttachmentList>
                                                {kBaseAiAttachments.map((attachment, index) => (
                                                    <KBaseAiAttachmentChip key={`${attachment.name}-${attachment.size}-${index}`}>
                                                        <span className="name" title={attachment.name}>
                                                            {attachment.name}
                                                        </span>
                                                        <span>{formatKBaseAiAttachmentSize(attachment.size)}</span>
                                                        <KBaseAiAttachmentRemove
                                                            type="button"
                                                            onClick={() => removeKBaseAiAttachment(index)}
                                                            aria-label={`Remove ${attachment.name}`}
                                                            title={`Remove ${attachment.name}`}
                                                            disabled={kBaseAiSending || kBaseAiLoading || kBaseAiClearing}
                                                        >
                                                            <svg
                                                                width="12"
                                                                height="12"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                aria-hidden="true"
                                                            >
                                                                <path
                                                                    d="M6 6l12 12M18 6L6 18"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                />
                                                            </svg>
                                                        </KBaseAiAttachmentRemove>
                                                    </KBaseAiAttachmentChip>
                                                ))}
                                            </KBaseAiAttachmentList>
                                        ) : null}
                                        <KBaseAiAttachButton
                                            type="button"
                                            onClick={openKBaseAiFilePicker}
                                            disabled={kBaseAiSending || kBaseAiLoading || kBaseAiClearing}
                                            title="Attach files"
                                            aria-label="Attach files"
                                        >
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    d="M21.44 11.05l-8.49 8.49a6 6 0 1 1-8.49-8.49l9.19-9.19a4 4 0 1 1 5.66 5.66l-9.2 9.19a2 2 0 1 1-2.82-2.83l8.48-8.48"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </KBaseAiAttachButton>
                                        <KBaseAiInput
                                            placeholder="Ask the AI assistant to apply this process to the current ticket, or attach files for context..."
                                            value={kBaseAiDraft}
                                            onChange={(event) => setKBaseAiDraft(event.target.value)}
                                            disabled={kBaseAiSending || kBaseAiClearing}
                                        />
                                        <KBaseAiSendButton
                                            type="button"
                                            onClick={sendKBaseAiMessage}
                                            disabled={(!kBaseAiDraft.trim() && !kBaseAiAttachments.length) || kBaseAiSending || kBaseAiClearing}
                                            title={kBaseAiSending ? 'Sending message' : 'Send message'}
                                        >
                                            {kBaseAiSending ? 'Sending...' : 'Send'}
                                        </KBaseAiSendButton>
                                        {kBaseAiError ? (
                                            <KBaseAiErrorText>{kBaseAiError}</KBaseAiErrorText>
                                        ) : null}
                                    </KBaseAiComposer>
                                </KBaseAiPane>
                            </KBaseProcessSheet>
                        ) : null}
                    </AnimatePresence>
                </>
            )}
        </AnimatePresence>
    );

    if (typeof document === 'undefined') return drawer;
    return createPortal(drawer, document.body);
}



